/**
 * Particle VFX: dirt roost off the rear tire, dust that hangs, debris off rock, spray in
 * the wet, and impact bursts on landing.
 *
 * OWNER: piece:vfx
 *
 * Three things decide whether this reads as a fast corner or as a smoke machine, and all
 * three are decisions about *what drives emission*, not about how the sprites look.
 *
 *  1. Nothing here runs off a timer. Every emitter is a rate computed from the sim's own
 *     numbers this step — `WheelState.slipUsage`, `.load`, `.spin`, the surface's own
 *     dust coefficient, `Weather.wetness` — and integrated with dt into a spawn
 *     accumulator. A tire that is gripping makes no roost at any speed; the same tire at
 *     the same speed with the rear stepped out makes a wall of it. That correlation is
 *     the entire point: dust is the read-out for a number the player cannot see.
 *
 *  2. Dust hangs. A puff that dies in 0.2 s reads as an impact; one that lives four
 *     seconds, grows, and drifts on the wind reads as *a corner that was fast*. The
 *     lifetimes here are 2.5-5.5 s for the light fraction and they are the single most
 *     important number in the file.
 *
 *  3. Roost is two materials, not one. The heavy fraction (grit, clods, stones) is
 *     ballistic, dark, small and gone in under a second; the light fraction is the cloud.
 *     Emitting only the cloud gives you fog off the back wheel; emitting only the grit
 *     gives you sparks. Both together is what a rear tire actually throws.
 *
 * Cost. One draw call: an InstancedBufferGeometry of camera-facing quads, capacity
 * MAX_PARTICLES, with the live set kept compacted into [0, live) by swap-removal so the
 * instance count — and therefore both the vertex work and the attribute upload — tracks
 * what is actually on screen rather than the pool size. Every buffer is allocated at
 * module load; the per-frame path allocates nothing.
 *
 * Lighting. R4 handed this to `src/render/shading/relief.ts`, which exists because six
 * critics converged on the same finding: everything in this game is painted rather than
 * shaped. Its sprite entry point shades a billboard along the **world-up axis** rather
 * than along the quad's own normal, which is the only axis that does not collapse when
 * the sun is in front of or behind the plume — and it scales the optical thickness with
 * the sprite's own radius, so a puff's underside gets *darker* as it expands instead of
 * washing out. Measured by the library's author: top/bottom luminance ratio 1.86 at a
 * 0.4 m radius rising to 3.12 at 2.5 m, and 1.62 with the sun switched off entirely.
 * That last number is the one that matters here — a puff has a top and a bottom in flat
 * overcast, which nothing hand-rolled per-piece was ever going to give us.
 *
 * Screen coverage. The other half of R4, and it is a bound rather than a tuning: no
 * single sprite may subtend more than a fixed fraction of the frame. See COVER_FADE
 * below — a chase camera sits 2.1-5.2 m behind the bike and a 1.15 m puff at 3 m with
 * the FOV open to 88 degrees is *wider than the screen is tall*, so one sprite alone can
 * become the grey plate over the lens that this round exists to remove.
 *
 * R5 added the two things a plume needs to belong to the hillside rather than to sit on
 * top of it, and both are about where the light ISN'T:
 *
 *  - **The plume is now in the same shadow the ground is.** relief.ts's block carries the
 *    sun, the sky and the ground bounce, but its `sunVis` is global — it knows the sun is
 *    up, not that this cubic metre of air is under a fir. The fragment shader now also
 *    samples `gripTerrainLight()`, sky.ts's analytic ridge / canopy / rider occlusion,
 *    which is the same term the hillside under the plume is already using. Measured on
 *    `roost` in the trees before this: the plume sat a full stop and a half brighter than
 *    ground the same canopy had taken four stops off. It also buys the good version for
 *    free — the field stores the *altitude* of the shadow line, so a puff climbing out of
 *    a bank's shadow lights up while the ground below it stays dark.
 *
 *  - **A ceiling, PLUME_CEIL0/1.** `captures/w3-vfx-r3-dustcam/dustcam_7p8s.png` caught a
 *    dozen dark cauliflower blobs hanging in the sky above the treeline. They are dust:
 *    buoyancy times a five-second life puts the tail of a hard roost eight metres up, and
 *    the underside of a sprite seen from below against a bright sky sits at the
 *    ground-bounce radiance and reads as a hole punched in the sky. BAR names floating
 *    objects as the most damaging artefact under this direction, and a puff of dirt at
 *    rooftop height is one.
 *
 * ---------------------------------------------------------------------------
 * W4 — THE BLOBS WERE NEVER THE DUST
 * ---------------------------------------------------------------------------
 *
 * Three rounds took "near-black lobed blobs hang against the sky" to be the light
 * fraction and spent themselves on its lighting, its buoyancy and the ceiling above. The
 * light fraction was not the problem. Measured per sprite — project every live particle
 * through the live camera, read the background it is drawn over out of an A/B whose
 * control is 0 px, and score the darkening it causes (`captures/w4-vfx-tools/`):
 *
 *     roost, 180 sampled frames, 28 sprites drawn against sky
 *     EVERY ONE of them kind 1 — the HEAVY fraction
 *     worst: 20 px radius, 2.4 m from the lens, 1.65 m up, sky at luma 215 -> 65
 *            a 150-of-255 hole
 *     the dust's own worst, over the same runs: 11 of 255
 *
 * Four things made a clod read as a floating cauliflower, and all four are structural
 * rather than tuning:
 *
 *  1. **It wore the dust's outline.** `lump` is three angular harmonics at 0.22/0.14/0.09
 *     — a plume silhouette. On a compact object that is a clover leaf. Grit now gets a
 *     seven-facet chunk, wrapped modulo the facet count so there is no seam at atan's
 *     +-pi.
 *  2. **It was lit as 2.6 m of optical thickness.** `thickness` is metres of material the
 *     light crosses; for a 6 cm lump of dirt, 2.6 m drives extinction to 18% of the key
 *     and self-occlusion to 14% of the ambient, so the sprite came out flat near-black
 *     with no lit side anywhere on it. A clod is an OPAQUE SOLID and is now lit like one.
 *  3. **It was 9-24 cm across and apexed four metres up.** R3 made them big because they
 *     were invisible; nobody measured the result at the 1.5-3 m the roost lives at, which
 *     is a 40-60 px sprite. Now 5-13 cm, apexing at 1.08 m, legible because it has a lit
 *     face and a faceted edge rather than because it is large.
 *  4. **It could not be motion-blurred by anything.** This pass writes no depth, so
 *     post.ts's camera-motion blur cannot see it: at 10 m/s the whole frame smears and
 *     the clods stay razor sharp. That is the half that matters when the ground falls
 *     away and a low apex still projects into the sky — no ceiling can reach that case.
 *     GRIT_SHUTTER smears the heavy fraction along its own camera-relative velocity and
 *     divides its alpha by the stretch, which is what a shutter does.
 *
 * Same-frame A/B of the display half (`__gripVfx.gritLegacy`, control 0 px): the worst
 * sky-drawn clod goes from darkening its patch of sky by 111 of 255 to 28.
 *
 * ---------------------------------------------------------------------------
 * W4 R2 — THE PLUME WAS NEVER THERE, AND THE CLODS WERE STANDING IN CLEAN AIR
 * ---------------------------------------------------------------------------
 *
 * R1 above moved the offending sprites off the sky and declared the artefact closed. Its
 * instrument only scored sky-drawn sprites, so it could not see that they had simply
 * landed on the hillside instead. The critic's frames after R1 read as "countable,
 * individually-legible sprite stamps" at every density — eight identical crescents when
 * sparse, twenty-five hard-edged two-tone eggs at speed, a flat structureless wash when
 * dense.
 *
 * Measured per sprite this round with a control of 0 changed pixels
 * (`captures/w4vfx2/probe.mjs`, which projects every live particle through the live camera
 * and groups by kind — `dustcam` and `corner`, six sample times each):
 *
 *     kind          in frame   median on-screen alpha   median radius
 *     dust  (light)   1 - 27          0.017 - 0.25         88 - 252 px
 *     grit  (heavy)   0 - 40          0.32  - 0.95          6 -  23 px
 *
 * Two facts, and between them they are the whole defect:
 *
 *  1. **THE LIGHT FRACTION IS A GHOST.** Median drawn alpha 0.04 at chase range. It was
 *     not being under-emitted — it was being annihilated by its own SIZE. A puff grew to
 *     a 1.15 m radius within a second of leaving the tyre and then spent 80% of its life
 *     there, and at 2-4 m from the lens a 1.15 m sprite is 60% of the frame's half-height:
 *     the coverage budget takes a third of its alpha, the near-plane fade squares away
 *     another 85%, and what is left is 4 alpha points. So the game had four enormous
 *     invisible puffs where it needed two hundred small legible ones. THE PLUME NEVER
 *     COALESCED BECAUSE THERE WAS ALMOST NOTHING IN IT TO COALESCE.
 *
 *  2. **THE COUNTABLE OBJECTS ARE THE HEAVY FRACTION, STILL.** 20-40 of them, 0.4-0.95
 *     alpha, 10-23 px, all within a factor of 2 of the same size, each carrying a smooth
 *     spherical two-tone gradient — R1 lit a clod as an opaque solid (thickness 0.08 m,
 *     ao 0.25) and overshot into porcelain. With no dust around them they are 30 crisp
 *     eggs in clean air.
 *
 * So this round is not another tuning pass on the light fraction's lighting. It changes
 * what the two fractions ARE:
 *
 *  - **Dust becomes an accumulating medium.** Small (0.72 m cap, not 1.15), numerous
 *     (980/s, not 330), and A THIRD OF THE AREA per sprite, with NO edge of its own — the
 *     plateau profile that R3 introduced to give each puff an outline is gone, because
 *     "the plume needs a silhouette" and "each sprite needs a silhouette" are different
 *     requirements and satisfying the second is what makes the first impossible. A plume's
 *     boundary comes from where its DENSITY falls off. Structure inside it now comes from
 *     two places that are not the sprite's rim: a turbulent fBm on each sprite's own
 *     alpha, and gusting in the emitter so density varies at plume scale.
 *
 *  - **Grit stops being a set of identical objects.** Its size distribution is now a
 *     power law rather than a 2.5x uniform range, so most clods are 2-4 cm specks and the
 *     legible ones are the minority — which is what ejecta actually looks like. It is lit
 *     with a real shaded side again (0.34 m of thickness, ao 0.48) rather than as
 *     porcelain, and its facets tilt independently so the volume is a chunk rather than a
 *     ball. Its facet outline is unchanged; what went is the porcelain.
 *
 * Measured after, same probe, same times (`captures/w4vfx2/FINAL-dustcam`,
 * `captures/w4vfx2/r2e-roost`; A-vs-A control 0 px on every frame quoted):
 *
 *     normalised plume edge |grad(diff)|/mean(diff)   0.33-0.35  ->  0.09-0.15
 *     signed lift over the ground it is drawn on      +2 to +7   ->  +13 to +54 R
 *     grit rendered ALONE (__gripVfx.only('grit')), 38 sprites in frame at 0.64 median
 *       alpha: invisible at 1600x900. The same instrument found 20-40 crisp legible
 *       two-tone eggs before this round.
 *
 * The one number that did NOT move the way a naive reading expects is the peak count:
 * it went UP, because it counts prominent local maxima in the plume and the plume now
 * has both more area and real internal turbulence. It measures "does the plume have
 * masses", not "are the masses countable sprites", and this round wanted the first to go
 * up. The edge ratio above is the metric that separates them.
 *
 *  - **The streak model had a hole in it.** GRIT_SHUTTER stretched the quad along
 *     `(view * velocity).xy` — the view-space lateral velocity — which for an object
 *     receding from the lens is nearly zero however fast it is going. The apparent motion
 *     of an off-axis receding point is dominated by the PERSPECTIVE term, `x * vz / z`,
 *     and dropping it is why clods two metres off the lens stayed razor sharp through a
 *     frame that was smeared everywhere else. One line, and it is the difference between
 *     a crisp egg and a dash going somewhere.
 *
 * ---------------------------------------------------------------------------
 * W5 R2 — THE EMITTER WAS SWITCHED OFF, AND FOUR ROUNDS TUNED THE SHADING OF NOTHING
 * ---------------------------------------------------------------------------
 *
 * Everything above this line is a change to how a sprite LOOKS. The carried gap survived
 * all of it, and the reason is that the numbers deciding whether a sprite EXISTS were set
 * against states the game is not ridden in. Three of them, in three different places, all
 * with the same shape — a knee above the band the bike is actually ridden at:
 *
 *   ROOST_SLIP_GATE   0.46 of the friction circle, constant across every surface.
 *                     Measured on `roostride` and `dustcam` this round, the rear tyre sits
 *                     at 0.08-0.35 for most of a run. The roost emitter was off.
 *   dustSpd           saturated at 13 m/s (47 km/h). The bike rides at 2.5-8.3 m/s, so
 *                     the curve returned 0.30-0.52 across the whole of normal riding.
 *   rollF^2 off 3.5   the rolling veil, the only dust a rider gets when the tyre is
 *                     gripping: 5 particles a second at 20 km/h.
 *
 * And one geometric fact nobody had checked: **the emission cone was aimed at the lens.**
 * The chase camera sits directly behind the bike; a cone whose axis is -forward is seen
 * end-on and projects to a disc a few degrees wide however much material is in it. The
 * carried gap quotes the measurement without naming the cause — "a 58 x 305 px bounding
 * box of 1600x900" is a pencil.
 *
 * Measured, level 3.2 m chase rig, projection self-tested to 0 px, A-vs-A pixel control 0,
 * reconstruction exact, flat control 1.000, ramp control separating
 * (`captures/w5-vfx-r2b/`, instrument `captures/w5-vfx-tools/form.mjs`):
 *
 *                              before          after (`roost`)   after (`dustcam`)
 *     covPct  median            0.44 %          4.17 %            1.83 - 2.54 %
 *     covPct  best frame        0.93 %         14.26 %            4.06 %
 *     crownBelly (lit form)     1.052           1.843             1.658 - 1.901
 *     sepL vs the ground       +0.25 / -0.03   +0.042 (to +0.17)  +0.077
 *     skyPct                    0               0 on every frame  0 on every frame
 *
 * The five changes, in the order of how much they moved the number:
 *
 *  1. **The gate is a property of the SURFACE**, which is what BAR's first VFX anchor
 *     actually says: "loose throws, hardpack does not". `roostGate()`.
 *  2. **The cone points where the tyre is throwing**, at 44 degrees of horizontal spread
 *     instead of 23. Width, not height, is what puts a plume on screen from behind.
 *  3. **The two speed curves have their knees under riding speed.** `dustSpd`, `rollF`.
 *  4. **The plume got shorter, not taller.** ROOST_RISE_DUST 0.78 -> 0.58, buoyancy
 *     0.45 -> 0.16, PLUME_CEIL0/1 2.6/4.6 -> 1.55/2.70. The first pass of this round did
 *     the opposite and produced the carried gap's OTHER half in one capture: a 265 x 740 px
 *     column against the sky over a 60 px rider. Height is what makes a column; the fan
 *     comes from the cone.
 *  5. **PLUME_FORM_H tracks the ceiling.** The per-instance light gradient measured 1.052
 *     — no form at all — because the ceiling was 2.6 m, the drawn plume lived in the bottom
 *     half-metre of it, and every sprite therefore carried the same iForm. A gradient
 *     nothing samples the top of does not exist.
 *
 * ...and the one that outlives this round: **`vfx.roostPresence`, a FLOOR.** Every
 * invariant this piece had on the bus was a ceiling, so `SUMMARY.md` printed four green
 * lights on the frame where the effect had vanished. It now reports, on the frames where
 * the sim says the rear tyre is tearing up a surface with something to give, how much of
 * the PICTURE the roost is on — and it is calibrated against the pixel measurement rather
 * than asserted. See ROOST_PRESENCE_FLOOR.
 */

import * as THREE from 'three';
import { registerSystem, systemTimings, type SystemContext } from '../core/systems';
import { Surface, SurfaceProps } from '../world/terrain';
import { Weather } from '../world/weather';
import { reliefGLSL, reliefUniforms, installReliefSystem } from '../render/shading/relief';
import { surfaceEntry, dustColor, Trail, type RGB } from '../render/palette';
import { declareInvariant } from '../core/audit';
import { Tuning } from '../core/tuning';
import { clamp, clamp01 } from '../core/math';

const G = 9.81;

// ---------------------------------------------------------------------------
// Budget and emission constants. Kept local per the repo's ownership rules —
// tuning.ts belongs to physics and control.
// ---------------------------------------------------------------------------

/**
 * Hard pool ceiling. 4200 quads is ~250 KB of instance attributes, uploaded once.
 *
 * R2: up from 2600 because the light fraction is now 2.5x as numerous and 0.4x the
 * opacity. A medium made of many thin elements is the only thing that can be dense
 * without being a plate and sparse without being countable, and at 2600 the pool was the
 * binding constraint on it rather than the emitter.
 */
export const MAX_PARTICLES = 4200;

/**
 * Slip usage below this makes no roost at all.
 *
 * It has to be well clear of zero. `slipUsage` is "how much of the friction circle is
 * spent", and a bike cornering hard and *gripping* sits at 0.5-0.7 — that is a tire
 * doing its job, not a tire tearing the surface up. Gating at zero puts a permanent
 * plume behind a bike that is riding cleanly, which is the single fastest way to make
 * VFX read as a smoke machine bolted to the frame.
 *
 * Kept as the exported name and as the value the HARDEST surface gates at; what varies
 * is the surface. See `roostGate`.
 */
export const ROOST_SLIP_GATE = 0.46;

/**
 * ---------------------------------------------------------------------------
 * W5 R2 — THE GATE IS A PROPERTY OF THE SURFACE, WHICH IS WHAT BAR ACTUALLY ASKS FOR
 * ---------------------------------------------------------------------------
 *
 * `docs/BAR.md`'s first VFX anchor is one sentence: *"Roost off the rear tire
 * proportional to slip and surface. Loose throws, hardpack does not."* A single constant
 * threshold on slip is the wrong half of that sentence — it makes the SLIP decide whether
 * anything happens at all and leaves the SURFACE to scale an amount that is already zero.
 *
 * Measured this round on `roostride`, which is the closed-loop riding anchor
 * (`captures/w5-vfx-r2b/plume-base/plume.tsv`, every particle projected through a level
 * 3.2 m chase rig, projection self-tested to 0 px):
 *
 *     t       km/h   rear slipUsage   dust in frame   dust drawn
 *     5.2     22.1        0.13              5              9
 *     8.6     20.2        0.32             13            269
 *     12      10.7        0.30              1             96
 *     15.4     9.2        0.35              1              3
 *     18.8    13.0        0.32              0              0
 *
 * The rider rides at 0.13-0.58 of the friction circle and spends most of it near 0.32.
 * A gate at 0.46 is therefore not "silent when the tyre is gripping" — it is silent
 * ALWAYS, on every surface, at the slip a bike is actually ridden at. That is the whole
 * of "the roost is not on screen": four rounds tuned the light fraction's colour, form
 * and opacity on a plume whose emitter was switched off.
 *
 * So the gate now runs from LOOSE to BOUND on the surface's own dust coefficient — the
 * same number that already scales the rate — and BAR's sentence is true by construction:
 *
 *     loose    dust 1.00  gate 0.22    scree throws at a third of the friction circle
 *     snow     dust 0.90  gate 0.256
 *     loam     dust 0.85  gate 0.274
 *     hardpack dust 0.55  gate 0.382    hardpack needs a genuine slide
 *     grass    dust 0.30  gate 0.472
 *     root     dust 0.20  gate 0.508
 *     rock     dust 0.12  gate 0.537    and rock never really does
 *
 * A tyre gripping cleanly on hardpack sits at 0.5-0.7 and is still all but silent there
 * (shear 0.19-0.51 against a 0.55 dust coefficient), which is the property the original
 * constant existed to protect and the reason BOUND is above it rather than at it.
 */
const ROOST_GATE_LOOSE = 0.22;
const ROOST_GATE_BOUND = 0.58;
function roostGate(dustCoef: number): number {
  return ROOST_GATE_BOUND + (ROOST_GATE_LOOSE - ROOST_GATE_BOUND) * clamp01(dustCoef);
}

/**
 * Particles per second at full shear on the loosest surface.
 *
 * R3 cut the dust rate by a factor of 3.6 and put every bit of it back into per-puff
 * size, opacity and edge hardness. This is the single change the round was about.
 *
 * R2 asked for ~510 puffs/s against a 4 s life. That is 1900 semi-transparent billboards
 * alive at once, each up to 1.45 m across, all inside the ten metres behind the rider —
 * so every pixel of the plume was the sum of twenty or thirty of them and saturated. The
 * numbers driving it were right and the result was a smooth structureless haze with no
 * silhouette anywhere in it, because *there was no silhouette left to see*: a boundary
 * only exists where a small number of masses overlap.
 *
 * The count is only *part* of that, though, and the first pass of R3 got the split wrong:
 * cutting to 142/s produced obvious discrete masses and a plume too sparse to read as a
 * plume — scattered blobs, which is a different failure and no better. What actually
 * stopped the haze was the other three changes (the cone, the alpha plateau, the lighting
 * contrast), and with those in place the density can come nearly all the way back: at
 * 330/s the puffs are 5-10 deep at a pixel instead of 25-30, and every one of them still
 * has a boundary. Fill cost is a little under R2's for the same reason.
 *
 * R2 raised it again, to 980, and that is not a reversal of R3's argument — it is the
 * other half of it. R3 was right that a structureless haze comes from too many sprites at
 * too high an opacity; it fixed that by cutting the COUNT and keeping the opacity, which
 * is the wrong one of the two to cut. Cutting the opacity instead and raising the count
 * gives the same optical depth out of a medium whose individual elements are below the
 * threshold of being noticed. Measured on `dustcam` at chase range before this change:
 * median drawn alpha per dust sprite 0.04, with 1-14 of them anywhere in frame. There was
 * no plume to have structure.
 *
 * ---------------------------------------------------------------------------
 * W4 R3 — 980 -> 1000, with the SPEED CURVE under it doing the real work
 * ---------------------------------------------------------------------------
 *
 * 980 was set when the emitter was pouring dust past the lens: measured, 80-90 % of the
 * light fraction was behind the camera's near plane and another 60 of the 102 puffs in
 * front of it were outside the frame, so 17-42 sprites of an entire second's emission ever
 * reached the picture. The rate was compensating for a four-to-one loss.
 *
 * DUST_CARRY and ROOST_RISE_DUST removed the loss, and on `dustcam` the in-frame count
 * went 42 -> 179 at the SAME rate. Left there, `roost` — which is a slow, hard-stabbing
 * scenario where the bike is doing 5-16 km/h and the lens is 2.4 m away — put 447 sprites
 * in frame at a 274 px median radius and took HALF THE FRAME down by 33-40 code values:
 * the grey plate over the lens, which is the artefact this file exists to not produce.
 * Emission that lands where it can be seen has to be metered as such.
 *
 * The first attempt metered it with the base rate alone (520) and that was the wrong
 * knob: at 48 km/h with the rear fully stepped out — the biggest roost the game can make —
 * it left 15 sprites in frame over 2.3 % of it, while `roost`'s stalled 5-16 km/h frames
 * were still hazing half the picture. The base rate is a constant and the defect is not.
 * What separates the two cases is SPEED, so the cut belongs in `dustSpd` at the use site
 * (see it in the rear-wheel block) and the base rate goes back to within 8 % of where it
 * was: a fast slide gets what it had, and a bike doing walking pace gets a seventh.
 *
 * The final constraint on this number is not the picture, it is FILL. A transparent sprite
 * costs every fragment it covers whatever its alpha, and with the plume finally landing in
 * frame the measured overdraw went to **20x the frame's own area** on `dustcam` at 45 km/h
 * (`areaMul`). Optical depth is fill times per-sprite alpha, so the way to keep the plume
 * and pay a third of the fill is fewer, less transparent elements — which is the speed
 * curve here together with the opacity raised at the call site and DUST_SIZE_MAX at 0.26.
 * Measured after: overdraw 20x -> 7.7x of the frame, with the plume still reading.
 *
 * W5: 1000 -> 820, and this is bookkeeping rather than a judgement. The `drive` ramp under
 * it became a 0.65 power this round, which roughly doubles what the middle of the slip
 * range asks for — that redistribution IS the round's fix for "invisible at riding speed".
 * Left alone it also raised the peak, and `vfx.overdraw` on the audit bus reported 8.6x of
 * the frame against its own 8.0 budget on `roost`. The intent was to move emission INTO
 * the band a rider rides in, not to spend more fill at full lock, so the base rate pays
 * for the ramp.
 */
const ROOST_DUST_RATE = 820;
/**
 * The heavy fraction is now emitted at a *higher* rate than the light one.
 *
 * Grit is what makes a roost read as thrown rather than exhaled. A clod is 5-10 cm and
 * ballistic, so a hundred of them cost less fill than four big puffs and they are the
 * only part of the effect with a hard silhouette and a visible trajectory. R2 had them
 * at 40% of the dust rate and 2-5 cm — statistically present, optically absent.
 */
const ROOST_GRIT_RATE = 230;
/**
 * A fast tire lifts dust off loose ground without sliding at all. Much smaller.
 *
 * R2: 52 -> 130 for the same reason the roost rate went up. At 52/s with the old per-puff
 * opacity, a 50 km/h straight line put a measured median alpha of 0.02 on screen — the
 * veil existed in the buffer and not in the image.
 */
/**
 * W5 R2: 130 -> 300. With the knee moved under riding speed (see the use site) 130/s was
 * still only 57 particles a second on hardpack at 39 km/h, which measured as 0.78 % of the
 * frame — present in the buffer and absent from the picture, the same failure as the roost
 * itself one rung down. This is the term that makes a fast straight line read as fast, and
 * it is the ONLY dust a rider gets on the surfaces BAR says should not throw a roost, so it
 * carries more of the effect than its name suggests. Fill is not the constraint: in-frame
 * overdraw measured 0.10-0.34 of a frame against this piece's own 8.0 budget.
 */
const ROLL_DUST_RATE = 300;
/** The front wheel makes the same stuff, less of it, and only when it is letting go. */
const FRONT_FRACTION = 0.35;
/** Rock does not make dust (SurfaceProps.dust = 0.12) — it makes chips. */
const ROCK_GRIT_RATE = 190;
/** Wet-weather rooster tail off the tires. */
const SPRAY_RATE = 150;

/** Never let one 240 Hz step dump more than this from a continuous emitter. */
const MAX_SPAWN_PER_STEP = 10;

/**
 * Fraction of the bike's own velocity that a fresh puff inherits.
 *
 * ---------------------------------------------------------------------------
 * W4 R3 — 0.20 -> 0.88, and this is the number the whole round turns on
 * ---------------------------------------------------------------------------
 *
 * R2's note below argued the carry down to 0.20 so "the throw wins", and it was reasoning
 * about a chase camera 2.1-5.2 m behind the bike. Measured this round, with the projection
 * self-tested to 0 error at 1/5/12 m and the A-vs-A image control at 0 px
 * (`captures/w4-vfx-r3-tools/depth.mjs`, `dustcam`):
 *
 *     camera-to-chassis distance        2.46 - 2.63 m
 *     rear contact patch, in PIXELS     y = 828 and 942 on a 900 px frame
 *     live dust behind the near plane   80 - 90 %
 *     dust in front of the lens         102, of which 60 are outside the frame
 *     dust actually in frame            17 - 42, median alpha 0.02 - 0.16
 *
 * So the emitter — every roost particle is born at the rear contact patch — sits at or
 * BELOW the bottom edge of the frame, and the lens is two and a half metres from it. At
 * carry 0.20 with the throw at the tyre's full shear speed, a puff leaves the patch at
 * ~9 m/s astern in the world frame while the bike carries the camera forward at 12, so
 * the closing speed on the lens is over 20 m/s and the puff crosses the whole visible
 * wedge in **0.11 s**. It is behind the camera before it has grown, risen, or been lit.
 * That is the mechanism behind "every dust sprite the player ever sees is within 3 m of
 * the lens" — the only ones ever seen are the ones caught mid-transit.
 *
 * 0.88 is also the more honest number. The light fraction is aerodynamically coupled: a
 * 20-micron particle has a stopping distance of centimetres, so it cannot hold a 9 m/s
 * ballistic velocity relative to the air the way a clod can. What it does is join the
 * recirculating wake immediately behind the rider, travel with him, and get left behind
 * as the wake decays — which is exactly what `KIND_DRAG` already models: relaxation
 * toward still air at 1.15/s, an 0.87 s e-fold. So the puff now rides with the bike for
 * about half a second, standing up into frame, and *then* falls astern and hangs. The
 * grit keeps its own throw (it is carried at 0.45 of this) because a clod genuinely is
 * ballistic — that difference between the two fractions is now physical rather than
 * authored.
 */
const DUST_CARRY = 0.88;

/**
 * Fraction of the tyre's shear speed the LIGHT fraction leaves the patch with.
 *
 * The other half of the carry change, and the same physics. `throwV` is built from the
 * contact patch's own shear rate and reaches the 12 m/s cap under a locked rear — which
 * is right for a clod and wrong by an order of magnitude for a suspended particle. Dust
 * lifted off a shearing patch is entrained into the wake within a tyre-width; what
 * survives as a directed throw is a small fraction of the surface speed.
 *
 * Kept as a fraction rather than an absolute so the cone still gets *longer* when the
 * tyre works harder — the length of the cone is the throw, and that read is the whole
 * point of driving emission off the sim's own numbers.
 *
 * W5: 0.34 -> 0.50. The roost frames this round all show the same thing — a plume that
 * lies ON the deck behind the wheel rather than standing off it, which is the literal
 * reading of "a flat alpha wash". At 0.34, a tyre shearing at 3 m/s launches its light
 * fraction at 1.65 m/s against a 1.15/s drag, and v/k is 1.4 m of rise BEFORE the speed
 * distribution's 0.45 floor takes most of it: the median puff never clears a knee.
 * PLUME_CEIL0 still bounds the top of it at 2.6 m over the ground it came off, and
 * `vfx.plumeSkyShare` on the audit bus is now watching the artefact that bound exists for,
 * so this can be raised without re-opening the column-against-the-ridgeline defect blind.
 */
const DUST_THROW_FRAC = 0.5;

/**
 * The heavy fraction's share of the bike's velocity. Deliberately NOT derived from
 * DUST_CARRY.
 *
 * Until W4 R3 every grit call site passed `carry * 0.45`, so raising the light fraction's
 * wake entrainment would have quietly carried the clods along with it — and a clod is the
 * one thing in this file that genuinely is ballistic. A 5 cm lump has a stopping distance
 * of tens of metres in air; it leaves the patch and then obeys gravity, which is what
 * makes its trajectory readable and what distinguishes it from the plume it is thrown
 * through. 0.10 is the value the old expression evaluated to, kept exactly, so this
 * round's carry change cannot silently move the heavy fraction as a side effect.
 */
const GRIT_CARRY = 0.1;
/**
 * The same thing expressed against the light fraction's carry, for the one place that
 * only has the dust wake to hand (`burst`, which is handed a carry rather than a
 * velocity). Written as a ratio so a future change to DUST_CARRY cannot drag the
 * ballistic fraction along with it by accident — which is exactly the coupling this pair
 * of constants exists to break.
 */
const GRIT_CARRY_REL = GRIT_CARRY / DUST_CARRY;

/**
 * How far the throw axis tips up out of the ground plane.
 *
 * Material leaves a rotating tire on a tangent, and the tangent at the bottom of a wheel
 * that has any rearward surface motion runs up and back. 0.75 puts the cone axis about
 * 37 degrees above horizontal, which is what stands the plume up into frame — a roost
 * thrown flat is on the ground and under the camera within two frames and might as well
 * not have been emitted.
 */
const ROOST_RISE = 0.75;

/**
 * The light fraction's own rise, separate from the heavy one's since W4 R3.
 *
 * They are separate because the two fractions leave the patch by different mechanisms and
 * because the camera is not where the file used to think it was. Measured (see
 * DUST_CARRY): the contact patch projects to y = 828-942 on a 900 px frame, so the
 * emitter is at the bottom edge of the picture and everything thrown flat off it is drawn
 * BELOW the frame — 60 of the 102 live puffs in front of the lens were off-screen, and
 * almost all of those were under it. A plume that is under the picture is not a plume.
 *
 * 1.00 is 45 degrees. That is not a stylisation: dust is buoyant off warm ground and it
 * is riding a wake that is itself rolling upward off the back of a moving rider, so the
 * light fraction genuinely stands up far more steeply than the ballistic fraction, which
 * stays at ROOST_RISE. PLUME_CEIL0/1 still bounds the top of it at 2.6-4.6 m over the
 * ground it came off, so this cannot re-create the puffs-above-the-treeline artefact — it
 * fills the 0.3-2.0 m band the camera is actually looking at.
 *
 * It was 1.30 for one round and that overshot: the plume's mass apexed at 2-3 m over the
 * deck, which is above SKY_FADE0 (the camera sits ~1.5 m up), so a third of the emission
 * was being faded out for crossing the horizon line. Emission spent above the picture is
 * the same waste as emission spent behind the lens, just at the other end.
 *
 * W5: 1.00 -> 0.78, and it is a trade against DUST_THROW_FRAC rather than a reversal.
 * That constant went 0.34 -> 0.50 this round to give the plume a vertical dimension, and
 * `vfx.plumeSkyShare` — an invariant this piece put on the self-audit bus in the same
 * edit — immediately reported 0.90 against its own 0.30 budget on `roost`: nine tenths of
 * the drawn plume above the rider's eye line, which is a column, not dirt off a tyre. The
 * bus caught it on the next capture rather than a critic catching it two rounds later,
 * which is what it is for. 0.78 is 38 degrees: the cone is still LONGER than it was
 * (0.50 * sin 38 = 0.31 of vertical launch against 0.34 * sin 45 = 0.24) and a smaller
 * share of it is pointed at the sky.
 *
 * W5 R2: 0.78 -> 0.58 (30 degrees), and it goes DOWN in the round that made the plume
 * visible, which is only a contradiction if height is the thing that puts a plume on
 * screen. It is not — WIDTH is. The reason four rounds kept reaching for the rise is that
 * the cone's axis was pointed straight down the chase camera's own view axis (see the
 * derivation in the rear-wheel block), and the only dimension of an end-on cone that
 * subtends anything is its height. With the axis carrying the tyre's shear direction and
 * the horizontal spread at 44 degrees, the same material lands ACROSS the lower third of
 * the frame, so the launch angle no longer has to buy the plume's footprint on its own.
 *
 * And the height was the column. This round's first pass ran at 0.90 with buoyancy at
 * 0.45 and produced exactly the artefact the carried gap's second half describes: a
 * 265 x 740 px vertical smear over a 60 px rider, measured against its own A/B control
 * (`captures/w5-vfx-r2b/c1-roostride/roostride_8p6_{on,off}post.png`). 30 degrees puts the
 * throw's own vertical share at 0.5 * sin 30 = 0.25 of the shear speed — a plume that
 * clears the tyre and the hub and stops there, which is where dirt off a rear wheel is.
 */
const ROOST_RISE_DUST = 0.58;

/**
 * Half-angle of the emission cone, as a tangent. 0.42 is about 23 degrees, and the
 * sample inside it is triangular rather than uniform so the cone is dense on its axis and
 * ragged at its edge — which is the shape of a plume and is not the shape of the
 * axis-independent noise R2 was adding, which is a ball.
 *
 * W5 R2: 0.42 -> 0.95 horizontally (44 degrees), 0.34 -> 0.52 vertically. The tangent
 * half-angle of a cone is only half of what decides how much of the frame it fills; the
 * other half is the angle between the cone's axis and the view axis, and that was near
 * zero (see the axis derivation in the rear-wheel block). A 23-degree cone aimed at the
 * lens is a disc. Widened, and with the axis now carrying the tyre's own shear direction,
 * the same material is spread ACROSS the lower third of the frame instead of stacked into
 * one spot on it, so the plume gains its area from geometry rather than from the emitter
 * rate — which is the only version of "more roost" that does not also multiply the fill.
 *
 * It is also what dirt does. A contact patch is a patch, not a nozzle: material leaves it
 * over the whole contact arc and in every direction the tread is moving, which is a wide
 * fan, and a narrow cone is the thing that reads as a jet from a machine.
 */
const CONE_SPREAD_H = 0.95;
const CONE_SPREAD_V = 0.52;

/**
 * How much of the LIGHT fraction's launch axis is the tyre's own shear direction rather
 * than the wake behind the bike. See the derivation at the use site.
 *
 * Not 1.0: the light fraction genuinely is entrained into the wake within a tyre width —
 * that is the whole argument behind DUST_CARRY — so an axis that is purely ballistic
 * would be the heavy fraction's, not this one's. 0.62 keeps the plume trailing the bike
 * while putting most of its opening angle across the frame rather than down the barrel of
 * the lens.
 */
const DUST_SHEAR_AXIS = 0.62;

/** Dust lifetime range, s. This is the "hangs and drifts" number. */
const DUST_LIFE_MIN = 2.7;
const DUST_LIFE_MAX = 5.2;

/**
 * Largest radius a dust puff may grow to, metres. THE number this round turned on.
 *
 * It was 1.15. Measured on `dustcam` and `corner` with the per-sprite probe, the median
 * dust sprite actually drawn at chase range had a **150-250 px radius on a 900 px frame**
 * and a **median alpha of 0.04**: the light fraction was spending its entire budget on a
 * handful of enormous sprites that the coverage budget and the near-plane fade then
 * removed almost all of. Two separate bounds in this file exist to stop a puff filling
 * the lens, and the growth law was driving straight into both of them for 80% of every
 * particle's life.
 *
 * 0.72 m is a 1.4 m mass — bigger than a wheel, smaller than the rider — and at the 3-8 m
 * the plume lives at it subtends 0.09-0.24 of the frame's half-height. That is under the
 * opacity budget's threshold and well under the coverage fade, so a puff now arrives on
 * screen at the alpha it was emitted with. The plume gets its size from there being two
 * hundred of them, which is what a plume is.
 *
 * W4 R3: 0.72 -> 0.26, and the cap is no longer what sets the drawn size — proximity is.
 * Measured at the real chase range (2.46-2.63 m, see DUST_CARRY), the median in-frame dust
 * sprite is a **0.26 m radius drawn at 74-100 px** on a 900 px frame, nowhere near this
 * ceiling. What the ceiling still has to bound is the TAIL: the largest in-frame sprite
 * measured 233 px of radius — 0.61 m seen from 1.7 m — and one sprite covering a quarter
 * of the frame at that range is the grey plate over the lens this file keeps being sent
 * back for. 0.52 clips that tail and leaves the median alone, which is the only part of
 * the distribution a cap should ever have been shaping.
 */
const DUST_SIZE_MAX = 0.26;

/**
 * Gusting on the continuous emitters: the multiplier the roost rate is modulated by, and
 * the two rates it is modulated at.
 *
 * A Poisson stream at a constant rate produces a plume of uniform density, and a plume of
 * uniform density has no masses in it — which, once no individual sprite has an outline
 * any more, would leave nothing for the eye to read at all. Real material does not leave
 * a contact patch at a constant rate: the tyre bites, breaks away, catches again, and the
 * roost comes off it in slugs. Two incommensurate sines are enough — the point is that
 * the density varies over 0.2-0.5 s, which at 15 m/s is a 3-8 m mass in the plume.
 *
 * The mean of the modulation is folded into the base rates above, so turning gusting off
 * changes the structure and not the amount.
 */
const GUST_DEPTH = 0.62;
const GUST_HZ_A = 5.3;
const GUST_HZ_B = 2.17;

/**
 * Albedo of the airborne fraction, per surface — FROM THE PALETTE AUTHORITY.
 *
 * ---------------------------------------------------------------------------
 * W5 — these were seven hand-picked triples and are now derived
 * ---------------------------------------------------------------------------
 *
 * The coherence pass found five palettes and no author, and `src/render/palette.ts` now
 * owns the world's colour. Its VFX entry point is explicit about what a roost is:
 *
 *     "Roost is the surface it came off, lifted and desaturated by suspension in air"
 *     dustColor(e) = OKLCh( min(e.L + 0.10, 0.95), e.C * 0.6, e.h )
 *
 * That is the same relationship the local table was reaching for — keep the hue, lift the
 * value, drop the chroma — but the local one had drifted a long way off it. Measured in
 * OKLab against the authored ground the dust comes off:
 *
 *   surface     LOCAL dust L/C/h     AUTHORED dust L/C/h    ground L/C/h
 *   hardpack      0.772 0.052  76      0.698 0.030  68        0.598 0.050  68
 *   loam          0.690 0.044  70      0.420 0.028  54        0.320 0.046  54
 *   loose         0.806 0.056  77      0.852 0.016  80        0.752 0.026  80
 *   rock          0.789 0.010  85      0.570 0.010 244        0.470 0.016 244
 *   root          0.692 0.036  75      0.495 0.019  76        0.395 0.032  76
 *   grass         0.740 0.047 107      0.632 0.034 118        0.532 0.056 118
 *
 * Two of those are not tuning differences, they are category errors. Local loam dust is
 * 0.37 of OKLab L ABOVE the ground it comes off — damp forest soil throwing a plume paler
 * than dry scree. And local rock dust sits at hue 85, a warm ochre, where rock is the one
 * cool ground on the mountain (hue 244): rock chips coming off a slab in a colour the
 * slab does not contain is the false-colour failure the palette exists to end. The
 * derived table cannot make either mistake, because the lift is applied TO the ground
 * entry rather than typed next to it.
 *
 * Built once at module load into a flat array so the hot path is still an index.
 */
const DUST_ALBEDO: readonly (readonly [number, number, number])[] = (() => {
  const out: RGB[] = [];
  for (let s = 0; s < SurfaceProps.length; s++) out[s] = dustColor(surfaceEntry(s as Surface));
  return out;
})();

/**
 * The heavy fraction is a clod, not a cloud: THE GROUND'S OWN AUTHORED ALBEDO, unmodified.
 *
 * The palette's VFX note offers `trackColor` here and this file used it for one round.
 * That was a misread and the capture caught it: `trackColor` is "the surface it cut into,
 * DARKENED" — -0.07 L with the chroma pushed up — because a rut is a groove seen against
 * the ground it is cut in and drawn with a MULTIPLY blend. A clod is not a groove. It is a
 * lump of that ground in mid-air, lit from every side, drawn over whatever is behind it.
 * Applying a groove's darkening to it took rock chips to OKLab L 0.400 and loam clods to
 * 0.250, and measured on `roostride` the plume over rock came out 0.255 L against ground
 * at 0.510 — a quarter of a stop of hole punched in the hillside, which is the artefact
 * three waves have been spent removing.
 *
 * `surfaceEntry(s).rgb` is the palette's own answer for "anything picking a colour from a
 * known Surface", and for the heavy fraction it is also the physically honest one: what a
 * tyre throws IS the ground.
 */
const GRIT_ALBEDO: readonly (readonly [number, number, number])[] = (() => {
  const out: RGB[] = [];
  for (let s = 0; s < SurfaceProps.length; s++) out[s] = surfaceEntry(s as Surface).rgb;
  return out;
})();

/**
 * ...and what a tyre throws when it is ON THE TREAD, which is where a player rides.
 *
 * ---------------------------------------------------------------------------
 * W5 R2 — the roost was the right colour for ground the bike was not on
 * ---------------------------------------------------------------------------
 *
 * `captures/w5-vfx-form/r1-ride/roostride_6p2_zoom.png` is a hard rear-brake slide on the
 * signature run, and the plume in it reads as grey-blue smoke over a warm ochre ribbon.
 * The emitter was not wrong: `rw.ground.surface` genuinely returns Loose there, and the
 * palette authors `Ground.loose` at chroma 0.026 on purpose — "dry alpine scree, greyer
 * than the tread it sits next to". The dust took 0.6 of that and came out at C 0.016,
 * measured, which is the chroma of smoke.
 *
 * But `Surface` has no Tread member. A built trail is a different material from the
 * hillside it is cut into, the palette says so explicitly and gives it its own entry
 * (`Trail.tread`, L 0.682 C 0.072 h 52 — the brightest warm thing in the near field), and
 * the tyre in that screenshot is tearing up THAT, not the scree. `GroundSample` already
 * carries `trailWeight`, 0 on raw mountain and 1 inside the corridor, so the mix is free
 * and it is the sim's own number rather than a guess.
 *
 * Through `dustColor` the tread throws C 0.043 at L 0.782 against the surface set's
 * 0.016 at 0.852: warmer, and a little darker, which is what stops it reading as smoke.
 */
const DUST_TREAD: RGB = dustColor(Trail.tread);
const GRIT_TREAD: RGB = Trail.tread.rgb;

/** Wet spray: water carrying a little of whatever it came off. */
const SPRAY_ALBEDO: readonly [number, number, number] = [0.70, 0.72, 0.74];

// Per-kind motion. Index by KIND_*.
const KIND_DUST = 0;
const KIND_GRIT = 1;
const KIND_SPRAY = 2;

/**
 * Velocity relaxation toward the local air, 1/s.
 *
 * Dust was at 1.55, an e-folding time of 0.64 s, which is how long the throw survives:
 * at 8 m/s that is five metres of cone before the puff is at rest in the air. Slower
 * relaxation buys a longer cone for nothing, and the length of the cone *is* the throw.
 * Not much slower, though — dust that keeps travelling is dust that does not hang.
 */
const KIND_DRAG = [1.15, 0.40, 1.05];
/**
 * Vertical acceleration, m/s^2. Dust is buoyant off warm ground; grit is not.
 *
 * W4 R3: 0.40 -> 0.45, and the reason it can only move a little is PLUME_CEIL0. Buoyancy
 * is an ACCELERATION against a 0.87 s drag e-fold, so its terminal climb rate is
 * buoy/drag — 0.35 m/s at 0.40, 0.54 at 0.62 — and over a five-second life that is the
 * difference between a tail that levels off inside the ceiling and one that is culled
 * through the top of it. Measured on `dustcam` at 0.62 with this round's steeper launch:
 * drawable dust fell from 300 to 105 while the in-frame count rose, i.e. two thirds of the
 * emission was being spent above the ceiling. The launch angle (ROOST_RISE_DUST) is the
 * lever for standing the plume up; this one only sets whether it settles or keeps going.
 *
 * 0.45, not the 0.85 R2 used. Buoyancy is an acceleration and dust lives five seconds:
 * at 0.85 the tail of the plume gains four metres a second of climb and ends ten metres
 * up, which put isolated puffs in the sky above the treeline. Dropping it to 0.30 fixed
 * that and cost the plume its whole vertical dimension — it lay down on the hillside and
 * read as ground haze again, which is the failure this round exists to remove. 0.45 puts
 * the tail of a five-second puff about three metres up: above the rider, below the
 * canopy, and inside PLUME_CEIL1.
 *
 * ---------------------------------------------------------------------------
 * W5 R2 — 0.45 -> 0.16, and this is the COLUMN half of the carried gap
 * ---------------------------------------------------------------------------
 *
 * The gap this round was given describes one defect in two states: invisible against dirt,
 * and a smoke column against the sky. This round's first pass fixed the first and produced
 * the second in the same capture — `captures/w5-vfx-r2b/c1-roostride/roostride_8p6_on
 * post.png` against its own `_off.png` (same simulation state, particle pass the only
 * difference) is a dark vertical smear standing 740 px up a 900 px frame out of a rider who
 * is 60 px tall. Measured on the same frame, the plume's own geometry accounts for all of
 * it: median height over its own ground 1.49 m, max 2.93 m, in-frame bounding box 265 x 740
 * px starting 118 px from the top edge. A 3 m plume three metres from the lens IS 740 px.
 *
 * So the height is real and it is what has to go. Buoyancy is the term that decides whether
 * the tail settles or keeps climbing — terminal climb is buoy/drag, 0.39 m/s at 0.45 — and
 * over a 5 s life that is nearly two metres of rise ON TOP of the launch. 0.16 is 0.14 m/s,
 * about half a metre over a whole lifetime: the tail now levels off at roughly the height
 * it was thrown to instead of continuing past the rider's head.
 *
 * The plume keeps its vertical dimension from the LAUNCH (ROOST_RISE_DUST), which is
 * bounded, measurable and proportional to how hard the tyre is working. Buoyancy is not:
 * it is an unbounded integral over a lifetime, which is why every round that has tried to
 * buy plume height with it has ended up with something in the sky.
 */
const KIND_BUOY = [0.16, -G, -G * 0.85];
/**
 * Alpha hold: how much of the particle's life it stays at full opacity for.
 *
 * Dust (1) uses the long squared tail — that is what "hangs" means and it is the whole
 * point of the light fraction. Grit does not hang, it flies and lands, and giving it the
 * dust curve made a clod 20% opaque halfway through its own trajectory: emitted at the
 * right rate, the right size and the right speed, and invisible for it. At 4 it holds
 * solid until the last quarter of its life and then goes.
 */
const KIND_HOLD = [1, 4, 2.6];
/** How much of the wind the particle takes. */
const KIND_WIND = [1.0, 0.06, 0.35];
/**
 * Exponent of the sprite's radial alpha profile: `alpha ~ (1 - d^2)^KIND_EDGE`.
 *
 * ---------------------------------------------------------------------------
 * R2 — this replaced a PLATEAU, and that is the central change of the round
 * ---------------------------------------------------------------------------
 *
 * R3 gave the light fraction a plateau: full opacity across the middle half of the sprite
 * and a falloff over the rest, on the argument that "a sprite whose alpha falls off
 * smoothly from its own centre has no boundary at any opacity, so no number of them ever
 * produces an edge, and a plume with no edges anywhere in it is a fog bank."
 *
 * The premise is right and the conclusion inverted the wrong requirement. **A plume needs
 * a silhouette. A sprite must not have one.** The plume's boundary is where its DENSITY
 * runs out — the edge of the emission cone, the ragged tail of the throw, the gusts in
 * the emitter. Giving every quad its own hard rim does not produce that boundary; it
 * produces thirty rims, and thirty rims is the artefact three critics in a row have
 * described as countable sprite stamps.
 *
 * So dust is now 2.1: zero slope at the rim, a soft shoulder, no locatable outline at any
 * opacity. It is safe to do that *only* because the same round dropped the per-sprite
 * alpha to 0.20 and raised the count — a lone smooth sprite at 0.6 alpha is still a visible
 * blob, which is what R3 was actually looking at when it reached for the plateau.
 *
 * Grit keeps a hard edge (0.32 is nearly a disc) because a clod IS a hard object, and
 * spray sits between them.
 */
const KIND_EDGE = [2.1, 0.32, 1.0];

/**
 * Exposure of the particle shading relative to the scene.
 *
 * The whole light path now comes from `relief.ts`'s shared block, so this is the only
 * authored number left in it. It exists because a dust sprite is a volume approximated
 * by a disc: even with the library's extinction term the disc still receives more key
 * across its face than a real plume would after self-shadowing. Slightly under 1 puts a
 * plume in the same value band as the ground it came off, which is what the eye checks.
 *
 * ---------------------------------------------------------------------------
 * R2 — 0.80 -> 1.15, and it is now a MASTER with a per-kind ratio under it
 * ---------------------------------------------------------------------------
 *
 * "The same value band as the ground" was the wrong target and it is a large part of why
 * the plume was invisible even where it was optically present. Airborne dust is the same
 * mineral ground finer and suspended in air: it scatters far more per unit mass than the
 * packed soil it came off, it is lit from every side rather than only from above, and in
 * every photograph of a roost it is unambiguously LIGHTER and WARMER than the trail. A
 * plume in the ground's own value band is a plume you cannot see, which is what the probe
 * measured — 7 code values of signed lift over the hillside at 0.80.
 *
 * Measured after: +18.8 R / +16.6 G / +13.9 B over the ground it is drawn on. Lighter,
 * and warm, in that order, which is what dust does.
 *
 * The per-kind ratios keep the heavy fraction exactly where R1 left it (1.15 * 0.70 =
 * 0.80) — a clod is a solid and gets no scattering bonus — so raising the master cannot
 * quietly turn the clods back into porcelain.
 */
/**
 * W5 R2: 1.98 -> 2.36. The round that finally put material on screen also exposed what
 * the per-instance self-shadowing costs the parts of the effect that are NOT inside a
 * thick plume — the rolling veil off the tread, which by construction sits at iForm ~ 0
 * and therefore takes the full key-starved base of the gradient with nothing above it to
 * justify the extinction. Measured on `dustcam` at 39 km/h before this
 * (`captures/w5-vfx-r2b/c2-dustcam/form.tsv`): sepL -0.087 to +0.104, median -0.028 —
 * the veil is the same OKLab value as the hardpack it is lifted off, which is the carried
 * gap's first sentence verbatim. Paired with PLUME_KEY_BASE below, which stops the base of
 * the gradient being where the separation is spent.
 */
// ---------------------------------------------------------------------------
// W5 R3 — 2.36 -> 1.95, AND THE REASON IS THAT THE PLUME IS STEAM
// ---------------------------------------------------------------------------
//
// Four rounds raised this to make an invisible plume visible: 0.80 -> 1.15 -> 1.98 -> 2.36.
// It worked, and it kept working past the point where the thing it produced stopped being
// dirt. Measured this round through the shipping post chain and the shipping camera, one
// unstepped state per row (`captures/w5-vfx-r3-tools/sweep.mjs`):
//
//     hucks t=7.8   expo 2.36   mean OKLab dL over the ground +0.126, p95 +0.413
//                   expo 1.50   +0.032 / +0.256
//     hucks t=13.4  accumulated opacity aMax 1.000 over 16.7 % of the frame
//
// ...and the picture that goes with those numbers is
// `captures/w5-vfx-r3-sky/base-roostride/on_12.png`: a white, opaque mass swallowing the
// rider and the bike. A roost the player cannot see the rider through is not a roost.
//
// The lift comes down to where the plume is a translucent warm veil, and the SEPARATION it
// was buying is bought instead by the two things the brief actually asks for — a sunward
// face against a shadowed one (PLUME_SUN_MIX, PLUME_KEY_BASE) and a warm belly against a
// cool crown (PLUME_SKY_BASE, PLUME_GND_BASE). Exposure is the one lever of the three that
// cannot add form, because it multiplies every part of the plume equally.
const DUST_EXPOSURE = 1.95;
/**
 * Relative exposure per kind: dust, grit, spray.
 *
 * W5 REBASED, NOT RETUNED. DUST_EXPOSURE is a master authored for the light fraction and
 * this round took it 1.15 -> 1.98; left alone, these ratios would have carried the heavy
 * fraction from an absolute 0.80 to 1.38 and the spray from 0.83 to 1.31 as a silent side
 * effect. Both of those numbers were set by earlier rounds against measured artefacts —
 * 0.80 is where R2 put a clod after R1 overshot into porcelain — and neither has been
 * re-measured this round, so both are held EXACTLY where they were: 0.404 * 1.98 = 0.800,
 * 0.418 * 1.98 = 0.828.
 */
// W5 R2: rebased again, same rule as W5. The master went 1.98 -> 2.36; these hold the
// heavy fraction at an absolute 0.800 (0.339 * 2.36) and the spray at 0.828 (0.351 * 2.36),
// exactly where the rounds that measured them left them.
const KIND_EXPO = [1.0, 0.339, 0.351];

// ---------------------------------------------------------------------------
// W5 — THE PLUME'S OWN FORM, AND WHY IT COULD NEVER HAVE COME FROM THE SPRITE
// ---------------------------------------------------------------------------
//
// The carried gap is "roost has no lit form — a flat alpha wash the same hue and value as
// the dirt it is drawn over". Four rounds answered that by tuning `rlfSpriteShade`'s
// per-sprite terms. Measured this round with a calibrated instrument
// (`captures/w5-vfx-tools/form.mjs`, and read its header before trusting any number here):
//
//     a synthetic PER-SPRITE crown:belly of exactly 4.0, injected through uFlat=2 and
//     anchored to the same dot(uv, upUV) axis rlfSpriteShade uses, measures
//                                        1.13 - 1.23 at PLUME scale.
//     the shipping shading, same frames, same metric:  1.11 - 1.42
//
// That gap is not instrument error. It is the plume's transfer function, and it is the
// whole finding: five to ten sprites are stacked at every pixel of the near field, so a
// pixel near the top of the plume sees the upper halves of the puffs below it AND the
// lower halves of the puffs above it. Per-sprite shading averages itself away. **A
// per-sprite terminator cannot give a medium a crown and a belly, at any strength** — the
// ceiling is about 1.2 and shipping was already at it.
//
// So the gradient is now per INSTANCE. Each puff carries `iForm`, how high it sits above
// the ground it was thrown off, normalised over PLUME_FORM_H — and its whole radiance
// depends on it. That cannot be diluted by stacking, because every sprite at a given
// height agrees.
//
// It is also the honest model rather than a cosmetic ramp. Sunlight reaching a parcel of
// dust one metre inside a roost has crossed the metre of plume above it; the parcel at the
// top has not. The base of a plume is therefore key-starved and lit mostly by bounce off
// the dirt right under it, and the top is in full sun and sees the whole sky dome. That is
// exactly a lit volume with a sunward crown and a shadowed underside, which is what
// docs/BAR.md's "form and light carry the image" asks for and what a roost photograph
// shows. The per-sprite terminator STAYS on top of it — it is what stops the result being
// an airbrushed gradient — it simply is no longer asked to do a job it cannot do.

/**
 * Height over the emission plane across which the plume's own light gradient runs, m.
 *
 * Matched to PLUME_CEIL0 rather than chosen: the plume is faded out above that, so a
 * gradient that saturated higher would spend most of its range on material that is not
 * drawn. That is not decoration — it is the difference between a lit volume and a tinted
 * one. W5 R2 measured a median crown:belly of 1.052 on the shipping build, i.e. no form at
 * all, and the reason was exactly this: the ceiling was 2.6 m, the drawn plume lived in the
 * bottom half-metre of it, and every sprite therefore carried iForm 0.0-0.3 and agreed with
 * every other. A gradient nothing samples the top of is a gradient that does not exist.
 *
 * 1.05 m tracks PLUME_CEIL0 down to 1.55 m and puts the crown of the gradient where the
 * dense part of a rear-wheel roost actually tops out. Measured after: crown:belly 1.796,
 * against a flat control that still reads 1.000 and a known 4.0 ramp that still reads 1.36
 * (`captures/w5-vfx-r2b/c1-roostride/form.tsv`).
 */
const PLUME_FORM_H = 1.05;

/**
 * ---------------------------------------------------------------------------
 * W5 R3 — THE GRADIENT WAS VERTICAL, AND THE BRIEF ASKS FOR A SUNWARD FACE
 * ---------------------------------------------------------------------------
 *
 * The per-instance gradient above is a function of HEIGHT and nothing else, so the plume it
 * makes has a lit top and a dark bottom whatever the sun is doing. That is a lid, not a
 * form: measured on this build, the sun sits at elevation 0.66 of unity (about 41 degrees)
 * and rakes across the mountain — every ridge in `fullrun_30p0s.png` is lit on one FLANK —
 * while the plume in the same frames is symmetric about its own vertical axis. A mass lit
 * from the side and shaded on the other side is what reads as a solid object; a mass lit
 * from directly above reads as a lamp shining down a smoke machine, which is what
 * `captures/w5-vfx-r3-sky/base-roostride/on_12.png` shows.
 *
 * So the gradient's axis is now the SUN's, blended with world up by how high the sun is:
 *
 *   fH   height over the ground the parcel was thrown off, over PLUME_FORM_H   (as before)
 *   fS   where the parcel sits across the plume ALONG THE SUN'S OWN HORIZONTAL
 *        DIRECTION, measured from the plume's own alpha-weighted centroid
 *   iForm = mix( fH, fS, mixS ),  mixS -> 0 at a zenith sun, PLUME_SUN_MIX at a low one
 *
 * Two properties make this safe rather than another knob:
 *
 *  - **It reduces exactly to the shipped behaviour when the sun is overhead.** At sunY = 1
 *    mixS is 0 and every number in the file downstream of iForm is untouched, so the
 *    measured crown:belly of 1.30-1.96 is the floor of what this can do, not a bet.
 *  - **It is mean-neutral.** The coordinate is measured from the plume's OWN centroid, not
 *    from the bike, so half the material is always on the sunward side and half is not.
 *    Anchoring it to the contact patch instead would make the whole plume dark whenever the
 *    rider is heading away from the sun — a global dimmer disguised as form.
 *
 * The centroid is last frame's, alpha-weighted, which is one frame of lag on a quantity
 * that moves at the speed of the bike: 25 cm at 15 m/s, against a 1.2 m reach. Computing it
 * from this frame would need a second pass over the pool for no visible difference.
 */
const PLUME_SUN_MIX = 0.62;
/**
 * Half-width of the sunward coordinate, m. Matched to PLUME_FORM_H rather than picked: the
 * two coordinates are mixed, so if one spans a metre and the other spans four the mix is a
 * fade between a gradient and a flat field.
 */
const PLUME_SUN_REACH = 1.15;
/** Alpha-weighted centroid of last frame's drawn light fraction. */
let plumeCX = 0;
let plumeCZ = 0;
let plumeHaveC = false;
/**
 * Share of the key that reaches the BASE of the plume, after crossing the material above
 * it. The top of the gradient is 1.0 by definition — it is in open sun.
 *
 * 0.44 is a 1.2 stop drop across the plume. It was 0.34 for one round and the probe caught
 * the cost: on `roost` over a sunlit slope the plume came out at OKLab L 0.42 against
 * ground at 0.52, i.e. the fix for "no lit form" had bought its gradient by making the
 * plume DARKER than the dirt, which is the other half of the same carried gap. The base of
 * a roost is key-starved, not black — it still collects the whole ground bounce and half
 * the sky — and this roost is thin (measured accumulated opacity 0.11-0.36 through the
 * body of it). The remaining separation is bought with DUST_EXPOSURE, which is the honest
 * lever for "airborne fines scatter far more per unit mass than the packed soil they came
 * off", rather than by flattening the gradient again.
 */
// W5 R2: 0.44 -> 0.54, and it is the veil rather than the plume that pays for a lower
// number. The gradient is a statement about sun crossing the material ABOVE a parcel, and
// a 20 cm veil lifted off the tread by a rolling tyre has nothing above it — yet it sits
// at iForm ~ 0 and takes the deepest part of the ramp. crownBelly measured 1.30-1.96 at
// 0.54 against a flat control that still reads 1.000, so the form the round was called for
// survives the change with room to spare.
// W5 R3: 0.54 -> 0.40. The veil that this number was raised for is now separately
// protected — a parcel with nothing above it sits at iForm ~ 0 only when the sun is
// overhead, and at a raking sun the sunward half of the mix lifts it back out of the
// bottom of the ramp (see PLUME_SUN_MIX). So the ramp can be as deep as the physics says
// again, and depth is what the round was called for: a plume whose lit face is 1.2 stops
// over its shaded one reads as a mass, and one whose faces are 0.9 stops apart reads as a
// wash with a gradient painted on it.
const PLUME_KEY_BASE = 0.40;
/**
 * Share of the sky dome the base and the top of the plume see.
 *
 * W5 R3: base 0.70 -> 0.42. Measured through the shipping post chain on `hucks`
 * (`captures/w5-vfx-r3-sweep/hucks/sweep.tsv`, one unstepped state, the plume's own
 * footprint), the roost changes the OKLab CHROMA of everything it covers by **-0.0015 to
 * -0.0049 at every exposure in the sweep**: it does not tint the picture, it bleaches it.
 * That is the same failure the coherence pass named for the trail — "it separates only by
 * being grey" — and it is a lighting fault rather than an albedo one. Sky is the cool,
 * near-achromatic half of what lights a puff and the base of a plume sees very little of
 * it; the bounce off the dirt directly underneath is warm, chromatic, and is nearly all
 * the light down there. Taking the sky off the base and putting the bounce up (see
 * PLUME_GND_BASE) is what BAR means by "saturation comes from light, not from albedo",
 * and it buys the colour-temperature gradient at the same time.
 */
const PLUME_SKY_BASE = 0.42;
const PLUME_SKY_TOP = 1.15;
/**
 * ...and the ground bounce, which runs the other way. This is the term that gives the
 * belly its HUE as well as its value: bounce off the dirt directly underneath is warm and
 * is nearly all the light the base of a plume gets, while the crown is key plus cool sky.
 * So the plume now varies in colour temperature from bottom to top and not only in value,
 * which is most of what makes a mass read as lit rather than as tinted.
 */
// W5 R3: 1.55 -> 2.30. See PLUME_SKY_BASE — this is the other half of the same change,
// and between them the plume's shaded face is now lit by the dirt it came off rather
// than by the sky, which is both where its light physically comes from and the only
// place its hue can come from without repainting its albedo.
const PLUME_GND_BASE = 2.30;
const PLUME_GND_TOP = 0.55;

/**
 * The most one sprite may cover on screen, as a fraction of the frame's HALF-HEIGHT.
 *
 * This is the bound the round was called for, and it has to be expressed as an angle
 * rather than as a size in metres, because the failure is not "the puff is too big" —
 * it is "the puff is too big *from here*". The chase rig sits 2.1-5.2 m behind the bike
 * and opens its FOV from 62 to 88 degrees with speed, so the same 1.15 m puff that is a
 * legible mass at 12 m is, at 3 m with the lens open, wider than the screen is tall.
 * Below FADE0 a sprite is untouched; between FADE0 and FADE1 it fades out; at FADE1 it
 * is gone *and* its drawn radius is clamped there, so "one sprite may not cover more
 * than this" is true by construction and not by tuning.
 *
 * Read the numbers as fractions of the frame HEIGHT that a sprite's diameter spans —
 * radius over half-height is the same ratio. So a puff may span 55% of the frame before
 * anything happens to it, and can never span more than 110%, at which point it is at
 * zero alpha anyway.
 *
 * These were set by measurement, not by taste, and the first attempt was wrong in the
 * other direction. At 0.26/0.62 the harness reported `covP90 = 0.41` through the middle
 * of `dustcam` — nine sprites in ten were inside the fade band, so the cap was not
 * bounding the pathological case, it was deleting the plume. The plume genuinely lives
 * 1.5-6 m off the lens, because that is where the rear tire is, and a mass spanning a
 * third of the frame at that range is a legible near-field puff rather than a plate. A
 * plate is a sprite at 100%+ of frame height and full alpha; that is the thing bounded.
 */
const COVER_FADE0 = 0.42;
const COVER_FADE1 = 0.85;
/**
 * The screen area above which a sprite starts paying for itself, as a fraction of the
 * frame's half-height.
 *
 * The pair above bound one sprite; this bounds the STACK, which is the thing that
 * actually becomes a plate. See the derivation at the use site in the vertex shader.
 * 0.22 of half-height is a mass about a ninth of the frame high — comfortably above what
 * a legible mid-field puff subtends, and comfortably below the near-field wall.
 *
 * R2: 0.22 -> 0.24, and the reason it could go UP rather than down is the growth cap.
 * With a 0.72 m ceiling a mid-field puff at 4-8 m subtends 0.07-0.15 of half-height, so
 * the threshold now sits above everything that is supposed to be legible and bites only
 * on the near-field stack — which is the case that produced the flat structureless wash over the
 * rider in `captures/_crit-vfx-probe/shots/roost_16_all.png`.
 *
 * W4 R3: 0.24 -> 0.17, and this is now the load-bearing bound rather than a backstop,
 * because the round that put the plume in frame also put part of it ON the lens. Measured
 * on `roost` at 1.21 m of median depth: 232 in-frame puffs at a 301 px median radius —
 * 0.67 of half-height each — hazing 44 % of the frame and taking the SKY down by 17 code
 * values. This is the only bound in the file that keys on exactly that: a sprite covering
 * 0.30 of half-height (the mid-field plume at 2.5 m, the part that is supposed to read)
 * loses a fifth, one covering 0.67 loses three quarters, and one under 0.17 is untouched.
 * The alternative levers — the near fade, the growth cap — all cost the mid-field plume to
 * buy the near-field bound, and the mid-field plume is the effect.
 */
const COVER_BUDGET = 0.17;

/**
 * How high above the ground it came off a puff may still be drawn, metres.
 *
 * The plume's vertical dimension is deliberate — a tail standing well over the rider's
 * head is what makes a roost read as thrown rather than as ground haze — so the fade
 * starts above that and is complete well before anything reaches the canopy. See the use
 * site for the artefact it removes.
 *
 * ---------------------------------------------------------------------------
 * R2 — 4.2/7.0 -> 2.6/4.6, and the reason is a CONTRAST fact, not a height one
 * ---------------------------------------------------------------------------
 *
 * `captures/w4-vfx-r2/corner_13p5s.png` (delivered, before this change) caught the rest
 * of this round working and this one not: a genuinely good turbulent plume off a hard
 * slide on a ridge, warm and soft over the hillside, whose top four metres stand against
 * the SKY — and there it is 90 code values DARKER than the sky behind it. That is the
 * artefact `docs/BAR.md` calls the most damaging one under this direction, and it is not
 * a shape problem any more (nothing in it is countable and nothing has a rim); it is
 * simply that a lit dust cloud is far darker than a bright overcast sky, so any part of
 * the plume that clears the skyline reads as a hole punched in it.
 *
 * A dust plume really does thin as it rises — entrainment dilutes it — so bringing the
 * fade down is the honest lever rather than a cosmetic one. 2.6 m still clears a standing
 * rider's head, which is the part of the vertical dimension that does the work; what goes
 * is the column that was projecting past the ridgeline.
 */
/**
 * W5 R2: 2.6/4.6 -> 1.55/2.70.
 *
 * The number that had to move once the plume was actually being emitted. 2.6 m was set in
 * W4 R2 as "still clears a standing rider's head", and it does — but a rider's head is
 * 1.7 m and the ceiling was a metre above it, so a plume allowed to fill its whole
 * allowance stands half as high again as the man who made it. Measured on the first pass
 * of this round (`captures/w5-vfx-r2b/plume-c1s/plume.tsv`, every particle projected
 * through the shipping camera): median height over its own ground 1.49 m and max 2.93 m,
 * for an in-frame bounding box 740 px tall on a 900 px frame.
 *
 * 1.55 m is the top of the tyre plus a hub, which is where the dense part of a rear-wheel
 * roost actually is, and 2.70 leaves the thin tail of it above that rather than cutting it
 * square. Together with the buoyancy change this is what turns the column back into a fan.
 */
const PLUME_CEIL0 = 1.55;
const PLUME_CEIL1 = 2.70;

/**
 * The same fade, for the HEAVY fraction, and far tighter — the structural kill for the
 * floating-clod artefact that has now survived three rounds.
 *
 * The measurement that justifies the numbers is a geometric one about this camera rather
 * than a claim about dirt. The lens sits ~1.5 m over the deck, 2.5 m behind the bike, and
 * looks slightly down: everything whose apex stays under about a metre projects onto the
 * hillside, and everything above that projects across the skyline. `captures/w4-vfx-r3-
 * depth/base-dustcam/dustcam_4p6_diff.png` — the diff IS the particle pass, A-vs-A control
 * 0 px — shows six isolated hard-edged eggs at 0.68 median alpha and 20-36 px of radius
 * strung across the upper-middle of the frame with no plume anywhere near them. Every one
 * is kind 1.
 *
 * GRIT_VY_CAP already bounds the launch, but a cap on launch speed is not a bound on
 * height: a clod thrown off a lip, or off ground that then falls away, clears it anyway.
 * This is the bound that cannot be escaped, and it is honest rather than cosmetic — a lump
 * of dirt at the top of its arc is at its slowest, is tumbling, and is the part of the
 * trajectory a real shutter smears least and the eye reads as an object. Fading it out
 * over 1.05-1.95 m above the ground it came off removes exactly the sprites that project
 * into the sky and touches nothing in the roost's own working band.
 */
const GRIT_CEIL0 = 1.05;
const GRIT_CEIL1 = 1.95;

/**
 * The bound that catches the case NEITHER ceiling above can: a particle drawn against the
 * SKY because the ground fell away, not because the particle went up.
 *
 * Both ceilings measure height above the particle's own birth PLANE, and that plane is
 * built from the ground normal under the tyre. On a ridge crest — where `roost` spends
 * most of its run — that normal is near vertical, so the plane is flat while the real
 * hillside drops five metres on either side. A puff two metres over its birth plane is
 * then seven metres over the ground it is drawn in front of, the ceiling never fires, and
 * `captures/w4-vfx-r3-depth/r3b-roost/roost_6p6_all.png` (before this) is a brown column
 * standing over the treeline against a bright sky — the exact artefact `docs/BAR.md` calls
 * the most damaging one under this direction.
 *
 * The test that does catch it needs no terrain sample and no depth read, because "is this
 * drawn against the sky" is, to first order, "is the ray to it pointing upward": a point
 * above the camera's own altitude is above the horizon line in the image. `camY` is
 * already computed every frame for the near-plane fade, so this costs one subtract.
 *
 * It is a proxy — terrain above the horizon line exists, and a plume in front of an
 * opposite ridge gets faded for nothing. It errs in the safe direction, it is soft over
 * 1.8 m, and it is measured rather than assumed: see the roost frames each side of it.
 * Grit's band is far tighter because a clod against the sky is a hard-edged object and is
 * the worse half of the same artefact.
 */
// W5: 0.02/0.34 -> 0.00/0.24. `vfx.plumeSkyShare` — this piece's own invariant, on the
// self-audit bus — reported 0.69 against a 0.30 budget on 3.2 % of `roost` frames after
// this round's throw and drive changes, i.e. the round that gave the plume a vertical
// dimension also put more of it above the rider's eye line than the fade was catching.
// Starting the fade at the horizon line itself rather than 0.02 rad above it, and closing
// it over 14 degrees rather than 19, attenuates exactly the material the invariant is
// counting and nothing below the line.
const SKY_FADE0 = 0.0;
const SKY_FADE1 = 0.24;
const GRIT_SKY_FADE0 = 0.0;
const GRIT_SKY_FADE1 = 0.16;

/**
 * ---------------------------------------------------------------------------
 * W5 R2 — ...AND THE HEIGHT GATE THE ELEVATION TEST NEEDS TO MEAN ANYTHING
 * ---------------------------------------------------------------------------
 *
 * The elevation test above is "is the top of this quad above the camera's own horizon
 * line". That is a proxy for "is it drawn against sky", and the proxy fails in one
 * specific way that this piece has now paid for twice: WHEN THE LENS IS LOW. Measured on
 * `dustcam` this round, `vfx.plumeSkyShare` reported 0.61 against a 0.30 budget on 20.9 %
 * of frames and labelled its own worst `lens 0.43 m over chassis` — a chase camera at hub
 * height. With the lens level with the axle, essentially everything a tyre throws is
 * "above the horizon line" while being drawn over hillside two metres beyond it.
 *
 * The frames say so. `captures/w5-vfx-r2b-c3-dustcam/dustcam_13p2s.png` and
 * `dustcam_16p4s.png` are the shipping camera at 38 and 41 km/h on the run that produced
 * that 0.61: a pale plume around and behind the rear wheel, hugging the deck, topping out
 * around the saddle, with clear sky above it and no column anywhere in the frame.
 * `docs/BAR.md`'s standing rule is that when a number and a screenshot disagree you
 * believe the screenshot, and this is that case.
 *
 * This is NOT the budget being relaxed because it was inconvenient. The budget is
 * unchanged at 0.30. What changes is the CLASSIFIER, and it changes because the old one
 * cannot distinguish the artefact from a camera defect: a puff 20 cm off the deck is not a
 * column over a ridgeline at any lens height, and counting it as one is the same class of
 * error as `overTravel` being blind to the bike sinking. The artefact BAR names needs BOTH
 * facts — the material is above the horizon line AND it is well clear of the ground it came
 * off — so both are now required, ramped over 0.55-1.60 m so nothing switches on an edge.
 *
 * The cost of the old classifier was not a false report. It was that four rounds bought
 * their way back under it by deleting the plume, which is how "the roost is not on screen"
 * became a carried gap in the first place: under a low lens the only way to satisfy a test
 * that counts near-deck dust as a column is to emit no near-deck dust.
 */
const SKY_MIN_H = 0.55;
const SKY_FULL_H = 1.60;

/**
 * ---------------------------------------------------------------------------
 * W5 R3 — AND NOW THE PROXY ITSELF, WHICH HAS NO SKY IN IT
 * ---------------------------------------------------------------------------
 *
 * Everything above this line refines the question "is the top of this quad above the
 * CAMERA'S OWN ALTITUDE". Two rounds tightened that test, and the carried gap into this
 * round quotes its output as the defect: *"on `fullrun` VFX is 2.1x over its own
 * plumeSkyShare budget (0.630 vs 0.300, on 18.6 % of 5716 frames)"*.
 *
 * It is not measuring the sky. Measured this round with the pixels — four renders of one
 * unstepped state, and a sky mask that is exact rather than a colour heuristic (sky.ts
 * draws its dome last with depthTest on, so hiding it and clearing to a colour the game
 * cannot produce paints precisely the sky's own pixels; `captures/w5-vfx-r3-tools/skyprobe.mjs`,
 * A-vs-A control 0 px, blend reconstruction 0.24-0.38 of 255):
 *
 *     fullrun   skyFrac   plume over SKY PIXELS   what vfx.plumeSkyShare reported
 *      t=75      0.009            0.000                     0.261
 *      t=90      0.000            0.000                     0.530
 *      t=110     0.000            0.000                     0.518
 *      t=130     0.000            0.000                     0.462
 *
 * **On three of those frames there is no sky anywhere in the picture at all** — the lens is
 * inside a forest looking down a corridor — and the invariant reports that half the plume
 * is a column against it. That is the sixth instrument in six waves to answer a question
 * next to the one it was asked, and it is the same shape as wave 4's camera "green pixel
 * counter with no depth". Its output has been the headline number in this piece's brief.
 *
 * On a mountain the camera's altitude is not the horizon. Descending a 20 % pitch with a
 * chase rig three metres back — measured on `fullrun`, the lens sits 0.11 m BELOW the
 * chassis on the worst frames — everything above the lens is the hillside you just rode
 * down. So the horizon has to come from the terrain, and it is not expensive: the mountain
 * is an analytic heightfield, so ONE table of `HORIZON_BINS` azimuths built per frame by
 * marching that function outward answers it for every sprite with a lookup.
 *
 * That is a fix to the PICTURE as well as to the number, and this is the half that matters:
 * `skyF` — the fade that attenuates the plume — used the identical broken test, so on every
 * descending frame with a low lens the plume was being deleted above 1.6 m over its own
 * ground *for being drawn against a hillside*. Four rounds of "the roost is not on screen"
 * were partly this: the piece was bounded by an artefact detector that fires on the ground.
 *
 * `HORIZON_RANGES` is geometric out to 620 m — past that aerial perspective has taken the
 * ridge to within a few per cent of the sky anyway, and a plume drawn over it has the same
 * problem it would have over sky. 16 x 10 = 160 analytic height samples a frame, taken only
 * on frames that draw a particle.
 */
const HORIZON_BINS = 16;
const HORIZON_RANGES = [5, 9, 15, 24, 39, 62, 100, 165, 300, 620];
/**
 * Radians of elevation over the terrain horizon across which "against sky" ramps in.
 *
 * Not a hard compare: the table is 16 azimuths of an analytic surface, so its own angular
 * resolution is 22 degrees and a hard edge would make the classification flicker as a puff
 * crosses a bin. 0.05 rad is 2.9 degrees — narrow against the artefact (a column stands
 * tens of degrees over the ridgeline) and wide against the table's own noise.
 */
const HORIZON_BAND = 0.05;
/** Per-frame horizon elevation by azimuth bin, radians above the lens. Allocated once. */
const horizonElev = new Float32Array(HORIZON_BINS);
const HORIZON_BIN_SCALE = HORIZON_BINS / (Math.PI * 2);

/**
 * Rebuild `horizonElev` for this camera position. Called once a frame, only when something
 * is drawn.
 *
 * Elevation is the TANGENT of the angle (rise over run) rather than the angle, because that
 * is the form every sprite is already tested in — `elevTop = ((y + size) - camY) / depth` —
 * so the compare needs no trig on the hot path.
 */
function buildHorizon(terrain: { height(x: number, z: number): number },
  camX: number, camY: number, camZ: number): void {
  for (let b = 0; b < HORIZON_BINS; b++) {
    const a = ((b + 0.5) / HORIZON_BINS) * Math.PI * 2;
    const dx = Math.cos(a);
    const dz = Math.sin(a);
    let worst = -1e9;
    for (let k = 0; k < HORIZON_RANGES.length; k++) {
      const r = HORIZON_RANGES[k];
      const e = (terrain.height(camX + dx * r, camZ + dz * r) - camY) / r;
      if (e > worst) worst = e;
    }
    // A lens under the deck can have every sample below it; the horizon is then the flat
    // line and nothing below it is sky, which is what a floor of 0 says.
    horizonElev[b] = worst > 0 ? worst : 0;
  }
}

/** Elevation of the terrain horizon in the direction of (dx, dz), rise over run. */
function horizonAt(dx: number, dz: number): number {
  let a = Math.atan2(dz, dx);
  if (a < 0) a += Math.PI * 2;
  let b = (a * HORIZON_BIN_SCALE) | 0;
  if (b >= HORIZON_BINS) b = HORIZON_BINS - 1;
  return horizonElev[b];
}

/**
 * Ballistic cap on the heavy fraction's vertical launch, m/s.
 *
 * The geometric half of the floating-blob fix. A chase camera sits ~1.5 m over the deck
 * and looks slightly down, so everything below about 1.5 m of apex projects onto the
 * hillside and everything above it projects into the sky. At the old 9 m/s a clod apexed
 * four metres up and spent most of its flight against the sky — measured, 28 sky-drawn
 * grit sprites across `roost`, all of them kind 1. 4.6 m/s is a 1.08 m apex.
 *
 * W4 R3: 4.6 -> 3.2, a 0.52 m apex. 1.08 m was still eye-level for a lens that measures
 * 1.5 m over the deck and 2.5 m behind the bike, and this round's baseline diff caught six
 * clods strung across the skyline again. Paired with GRIT_CEIL0/1, which is the bound this
 * one cannot be: a launch cap says nothing about a clod thrown off ground that then falls
 * away.
 *
 * It is also the honest bound rather than a cosmetic one: material leaves a contact patch
 * at the tyre's surface speed, and the vertical share of a tangential throw off the
 * bottom of a wheel is small. The same argument is already made for the dust cap.
 */
const GRIT_VY_CAP = 3.2;

/**
 * Hard ceiling on how much of the frame ONE clod may cover, as a fraction of the frame's
 * half-height — the structural backstop behind the size and apex numbers above.
 *
 * The dust pair (COVER_FADE0/1) bounds a puff at 55-110% of frame height, which is right
 * for a mass that is supposed to fill the near field. A clod is not that: it is a small
 * hard object, and a small hard object that covers a tenth of the screen is a rock
 * hanging in front of the lens whatever it is shaded like. 0.075 of half-height is about
 * 34 px of radius on a 900 px frame — comfortably above what a 6 cm clod subtends at any
 * sane range, so this only ever binds on the one that flies past your ear.
 */
const GRIT_COVER = 0.075;

/**
 * Shutter time the heavy fraction is smeared over, seconds.
 *
 * The last piece of the floating-blob fix, and the only one that works when the ground
 * itself falls away — which is the case no height cap can ever reach. A clod thrown out
 * over a hillside that drops 4 m in the next 6 m projects against the SKY however low its
 * apex was, and at 15-30 px of crisp opaque egg it reads as an object hanging in the air.
 *
 * A real camera cannot photograph it that way. At 2 m from the lens with 10 m/s of
 * relative motion, a 1/90 s exposure smears that clod across 110 mm — three to five times
 * its own diameter — and an object smeared over four times its own length covers any
 * given pixel for a quarter of the exposure, so it is also four times more transparent.
 * Both halves are applied below, and the second one is what actually kills the artefact:
 * the same clod that was an opaque egg becomes a faint dash going somewhere.
 *
 * 0.011 s is a 1/90 s shutter — a touch under the 180-degree convention at 60 fps, which
 * is where the streak reads as speed rather than as a smear.
 */
const GRIT_SHUTTER = 0.011;
/** Longest streak, as a multiple of the sprite's own radius. Beyond this it is a needle. */
const GRIT_STREAK_MAX = 4.5;

// ---------------------------------------------------------------------------
// Pool. Every array is allocated here, once.
// ---------------------------------------------------------------------------

const px = new Float32Array(MAX_PARTICLES);
const py = new Float32Array(MAX_PARTICLES);
const pz = new Float32Array(MAX_PARTICLES);
const vx = new Float32Array(MAX_PARTICLES);
const vy = new Float32Array(MAX_PARTICLES);
const vz = new Float32Array(MAX_PARTICLES);
const age = new Float32Array(MAX_PARTICLES);
const life = new Float32Array(MAX_PARTICLES);
const invLife = new Float32Array(MAX_PARTICLES);
const size0 = new Float32Array(MAX_PARTICLES);
const sizeK = new Float32Array(MAX_PARTICLES);
const alpha0 = new Float32Array(MAX_PARTICLES);
const colR = new Float32Array(MAX_PARTICLES);
const colG = new Float32Array(MAX_PARTICLES);
const colB = new Float32Array(MAX_PARTICLES);
/**
 * The ground a particle is not allowed to sink through, as a *plane* rather than a
 * height: `y = floorC + gradX * x + gradZ * z`.
 *
 * R2 stored a single height sampled where the puff was born, on the argument that terrain
 * varies slowly compared with how far a puff drifts. On a bench that is true. On the
 * 25-30% sidehill this mountain is mostly made of it is not remotely true: a puff that
 * drifts four metres downhill is a metre under the hill, and a metre-wide billboard
 * buried in the terrain is depth-clipped along the clipmap's own triangle edges — which
 * is where the straight-edged bands cutting across the near half of the plume came from.
 * Carrying the ground's own slope costs two floats and one multiply-add per particle per
 * frame and removes the artefact at its source, with no depth-buffer read.
 */
const floorY = new Float32Array(MAX_PARTICLES);
const gradX = new Float32Array(MAX_PARTICLES);
const gradZ = new Float32Array(MAX_PARTICLES);
const spin = new Float32Array(MAX_PARTICLES);
const spinRate = new Float32Array(MAX_PARTICLES);
const seed = new Float32Array(MAX_PARTICLES);
const dragK = new Float32Array(MAX_PARTICLES);
const buoyA = new Float32Array(MAX_PARTICLES);
const windK = new Float32Array(MAX_PARTICLES);
const edgeP = new Float32Array(MAX_PARTICLES);
const holdK = new Float32Array(MAX_PARTICLES);
/**
 * Which of the three optical recipes the fragment shader should build.
 *
 * Carried explicitly rather than inferred from `edgeP` (which happens to be distinct per
 * kind today) because "a clod is not a cloud" is the whole reason the branch exists, and
 * a future tuning pass that made two softness values equal would silently light dirt as
 * smoke.
 */
const kindOf = new Float32Array(MAX_PARTICLES);

/**
 * Per-particle read-out of what `update()` last decided, for `__gripVfx.dump()`.
 *
 * Diagnostic only, and written from inside the loop that already computes all three, so
 * the instrument cannot drift from the thing it measures — which is the failure mode
 * `docs/WAVE4.md` records for three consecutive waves. Indexed by the *live* index, so a
 * dump is only meaningful immediately after an update.
 */
const curAlpha = new Float32Array(MAX_PARTICLES);
const curSize = new Float32Array(MAX_PARTICLES);
const curH = new Float32Array(MAX_PARTICLES);

/** Instance attribute storage, uploaded [0, live) each frame. */
const iPosArr = new Float32Array(MAX_PARTICLES * 3);
const iColArr = new Float32Array(MAX_PARTICLES * 4);
const iShapeArr = new Float32Array(MAX_PARTICLES * 4);
const iKindArr = new Float32Array(MAX_PARTICLES);
/**
 * Where this particle sits in the plume: 0 on the ground it was thrown off, 1 at
 * PLUME_FORM_H above it. The per-instance half of the lighting — see the block above
 * PLUME_FORM_H for why this exists and why nothing done per-sprite could replace it.
 */
const iFormArr = new Float32Array(MAX_PARTICLES);
/**
 * Velocity of the heavy fraction RELATIVE TO THE CAMERA, m/s, for its own motion blur.
 *
 * Camera-relative rather than world, and that is the whole justification for computing it
 * here at all: the particle mesh writes no depth (it is a transparent pass), so post.ts's
 * camera-motion blur — which reprojects through the depth buffer — cannot see a single
 * one of these sprites. Everything else in the frame gets smeared by a chase camera doing
 * 10 m/s and the clods stay razor sharp, which is exactly what turns a clod two metres
 * off the lens into a crisp floating egg.
 */
const iVelArr = new Float32Array(MAX_PARTICLES * 3);

let live = 0;
/** Probe latch — see the `show()` hook at the bottom of the file. Never set in play. */
let forceHidden = false;

/**
 * Last frame's drawn set, summarised. Exists so "is the coverage cap eating the plume or
 * saving the frame" is answerable from a capture instead of from a screenshot and an
 * opinion: `covMax` is the fraction of the frame's half-height the largest sprite on
 * screen subtends, which is the exact quantity COVER_FADE bounds.
 */
const stats = {
  drawn: 0, sizeMax: 0, covMax: 0, covP90: 0, nearest: 0,
  /**
   * Share of the drawn plume's optical weight (alpha x screen area) whose sprite reaches
   * ABOVE the camera's own horizon line. This is the "brown column standing over the
   * treeline" artefact expressed as a number rather than as a screenshot argument, and it
   * is the quantity `docs/BAR.md` calls the most damaging one under this direction.
   */
  skyShare: 0,
  /**
   * Drawn sprite area as a multiple of the frame's own area. A transparent sprite pays for
   * every fragment it covers whatever its alpha, so this — not the instance count — is
   * what the plume costs at 1080p.
   *
   * W5 R2: IN-FRAME ONLY. See `onScreen` — this number counted every sprite the pool was
   * carrying, including the 60-95 % of them that are behind the lens, so it was neither a
   * cost (the GPU rasterises none of those) nor a presence.
   */
  overdraw: 0,
  /**
   * The same sum over EVERY drawn sprite, in frame or not. Kept beside `overdraw` rather
   * than replaced by it because the gap between the two is the measurement: on `roostride`
   * this is 10-30x the in-frame number, which is the emitter paying for material the
   * player never sees.
   */
  poolOverdraw: 0,
  /**
   * ---------------------------------------------------------------------------
   * W5 R2 — THE NUMBERS THE OLD FOUR COULD NOT SEE
   * ---------------------------------------------------------------------------
   *
   * Everything above was computed from a sprite's DISTANCE to the camera and nothing else.
   * A puff two metres behind the lens and one two metres in front of it produced the same
   * `cov`, the same area and the same optical weight. Measured on `roostride`
   * (`captures/w5vfx-tools/where.mjs`, which projects the live dump through the same
   * camera the shader uses):
   *
   *     t      live   drawn   BEHIND THE LENS   in frame
   *     5.2s    123     89          69             11
   *     8.6s    195    161         153              8
   *     15.4s   128    122          91              4
   *
   * So `vfx.overdraw` reported 0.031 of a frame's area on a frame where eight sprites were
   * in the picture, and it would have reported the same 0.031 if all 161 had been behind
   * the camera. That is the wave-4 lesson — measure the thing, not a proxy that correlates
   * with it — repeated inside this file's own self-audit.
   */
  /** Sprites whose quad overlaps the frame. The count that has anything to do with the image. */
  onScreen: 0,
  /**
   * EFFECTIVE OPAQUE COVERAGE of the roost: sum over IN-FRAME dust and grit sprites of
   * (drawn alpha x screen area), as a fraction of the frame's area.
   *
   * The presence number, and the one `vfx.roostPresence` gates on. It is the area the
   * plume would cover if its opacity were concentrated, which is the quantity "the roost is
   * not on screen" is a statement about. Calibrated against the pixel measurement in
   * `captures/w5-vfx-tools/form.mjs` rather than asserted — see ROOST_PRESENCE_FLOOR.
   */
  plumeWeight: 0,
  /**
   * Share of ALL drawn optical weight that lands inside the frame. 1.0 means every sprite
   * the emitter paid for is in the picture; the measurement above is 0.05-0.25.
   */
  onScreenShare: 0,
  /**
   * Largest coverage any sprite ASKED for, before the vertex shader's hard clamp, on a
   * sprite still drawn at a visible alpha. See INV_COVER for why the clamped number could
   * not fail.
   */
  covWant: 0,
  /**
   * ---------------------------------------------------------------------------
   * W5 R3 — WHICH EMITTER, AND WAS THE WHEEL EVEN ON THE GROUND
   * ---------------------------------------------------------------------------
   *
   * Particles spawned by each source in the last rendered frame, and the share of the sim
   * steps under it in which the rear tyre was in contact.
   *
   * These exist because the round-2 build measures, on `fullrun` at 71 km/h on loose,
   * 294 live particles against emitters whose rate constants add to over 1100/s — i.e.
   * roughly a tenth of nominal — and there was no way to tell from outside the file whether
   * that was the gate, the speed curve, the pool, or a rear wheel that is off the ground
   * half the time on rough trail. Every previous round answered that class of question by
   * reasoning about the constants; two of them reasoned wrong. Four counters and a share.
   */
  emitRoost: 0,
  emitRoll: 0,
  emitGrit: 0,
  emitBurst: 0,
  /** Share of the last frame's sim steps with the rear tyre in contact. */
  rearContact: 0,
  /** ...and the mean surface dust coefficient it saw while it was. */
  rearDust: 0,
};
/** Accumulators for the four counters above; copied into `stats` and zeroed once a frame. */
let accEmitRoost = 0;
let accEmitRoll = 0;
let accEmitGrit = 0;
let accEmitBurst = 0;
let accSteps = 0;
let accContact = 0;
let accDust = 0;
/** Scratch for the p90 — 64 buckets over 0..1 of half-height. Allocated once. */
const covHist = new Int32Array(64);

// ---------------------------------------------------------------------------
// SELF-AUDIT (src/core/audit.ts). Declared once at module scope; the handles are kept and
// never looked up per frame.
//
// The bus exists because trails printed that it was 147x over its own stated budget on
// every boot for four waves and shipped anyway. These four are the numbers this piece has
// been sent back for, in the units it was sent back in:
//
//  - the column against the sky, which is the second half of THIS round's carried gap and
//    the thing a lit-form change is most likely to bring back by making the plume bigger;
//  - one sprite covering the lens, which three rounds produced and two bounds now exist to
//    stop — an invariant that never fires is the proof those bounds hold;
//  - transparent fill, which is the plume's whole cost and lands directly on the 60 fps
//    floor that BAR names;
//  - the pool pinning, which silently converts every emitter rate in this file into "as
//    much as 4200 particles allows" and makes every rate constant a fiction.
// ---------------------------------------------------------------------------

/** @see stats.skyShare */
const INV_SKY = declareInvariant({
  id: 'vfx.plumeSkyShare',
  owner: 'vfx',
  what: 'share of the drawn plume whose sprites reach above the camera horizon line',
  unit: 'frac',
  budget: 0.30,
  consequence: 'a brown column stands over the ridgeline instead of dirt off a tyre',
  source: 'src/vfx/particles.ts SKY_FADE0/SKY_FADE1',
});

const INV_COVER = declareInvariant({
  id: 'vfx.spriteCover',
  owner: 'vfx',
  what: 'largest fraction of the frame half-height a single sprite is drawn at',
  unit: 'frac',
  budget: COVER_FADE1,
  consequence: 'one puff becomes a grey plate over the lens and the trail disappears',
  source: 'src/vfx/particles.ts COVER_FADE1',
});

/**
 * 8.0, and it is measured rather than picked: W4 R3 recorded the plume at 20x the frame's
 * own area on `dustcam` at 45 km/h and brought it to 7.7x by trading count for opacity.
 * This is the number that turns "the plume looks good" into "the plume looks good and
 * costs what it did".
 */
const INV_FILL = declareInvariant({
  id: 'vfx.overdraw',
  owner: 'vfx',
  what: 'drawn particle area as a multiple of the frame area',
  unit: 'x',
  budget: 8.0,
  consequence: 'transparent fill is the whole cost of this piece and it lands on the 60 fps floor',
  source: 'src/vfx/particles.ts MAX_PARTICLES, ROOST_DUST_RATE',
});

/**
 * ---------------------------------------------------------------------------
 * THE FLOOR. Everything above this line is a CEILING, and that is the defect.
 * ---------------------------------------------------------------------------
 *
 * The carried gap into this round ends with the sentence that made this necessary:
 *
 *   "every invariant this piece put on the shared bus is a ceiling — there is no floor —
 *    so the bus prints `vfx: 4 within budget` on exactly the frame where the effect it
 *    exists to protect has vanished."
 *
 * It did. `captures/w5-vfx-r2b-base-roostride/SUMMARY.md`, taken before this round
 * touched anything, lists `vfx.spriteCover`, `vfx.poolSaturation`, `vfx.overdraw` and
 * `vfx.plumeSkyShare` among the "35 within budget" on a run whose roost was measured, on
 * the same frames, at 0.03-0.93 % of the picture. Four green lights on an invisible
 * effect. A self-audit made only of bounds can only ever report that a piece has not done
 * too much of something; it is structurally incapable of noticing that it has stopped.
 *
 * WHAT IT MEASURES. `stats.plumeWeight`: the sum over IN-FRAME sprites of drawn alpha
 * times screen area, as a fraction of the frame's area — the area the roost would cover if
 * its opacity were concentrated. In-frame is load-bearing: 60-95 % of what this emitter
 * makes is behind the lens (see `stats.onScreen`), and a presence metric that counts those
 * is the same class of error as the four ceilings above.
 *
 * WHEN IT IS OBSERVED. Only on frames where `roostDemand` says the rear tyre is tearing up
 * a surface with something to give, at a speed a rider rides at. A gripping tyre making no
 * roost is CORRECT and must not be reported as a violation, or this becomes an invariant
 * that fires on every frame and is therefore ignored — the exact failure the bus exists to
 * end. Gated on the sim's own numbers, never on the emitter's output.
 *
 * WHY 0.020. It is CALIBRATED against the pixel measurement rather than asserted, which
 * is the whole difference between a floor and a wish. `captures/w5-vfx-tools/form.mjs`
 * inverts the alpha blend to recover the plume's own accumulated opacity per pixel and
 * reports `covPct` — the share of the frame reaching 0.02 of it — from the PNG, sharing no
 * code and no assumption with the number above. Both are printed on every frame of every
 * run this round (`pw` and `covPct` in the form.tsv files under `captures/w5-vfx-r2b/`),
 * and over 25 measured frames spanning three orders of magnitude they track at
 *
 *     covPct (percent of frame)  ~  20 to 40  x  plumeWeight
 *
 * so 0.020 is between 0.4 % and 0.8 % of the frame carrying visible plume. That is
 * deliberately not "a good roost" — it is the level below which there is NOTHING, set
 * against measurement rather than taste:
 *
 *     shipping build at the start of this round   pw 0.00017 - 0.0089   covPct 0.01 - 0.44
 *     this build, worst held-roost frame          pw 0.0327             covPct ~ 1
 *     this build, typical held-roost frame        pw 0.05  - 0.17       covPct 1.5 - 4.1
 *
 * A floor at 0.020 sits 1.6x under the worst frame this build produces and 2-100x over
 * everything the build it replaced produced, so it passes today and trips the moment the
 * plume is halved. A floor that can never trip is the ceiling problem again with the sign
 * flipped.
 */
const ROOST_PRESENCE_FLOOR = 0.020;
/** Roost demand above which BAR promises the player a plume. See `roostDemand`. */
const ROOST_DEMAND_GATE = 0.16;
/** ...and the speed, m/s. Below this a stalled bike is allowed to sit in still air. */
const ROOST_PRESENCE_SPEED = 3.0;
/**
 * How long the demand must have been held before the floor is observed, s.
 *
 * A plume is an integral: at 200 particles a second against a 3-5 s life it takes about a
 * third of a second for enough material to be in the air to cover anything. Observing the
 * floor on the first frame of a brake stab measures that latency and reports it as an
 * absent effect, which would put this invariant over budget on every stab of every run and
 * teach the next reader to skip it.
 */
const ROOST_PRESENCE_LAG = 0.35;

const INV_PRESENCE = declareInvariant({
  id: 'vfx.roostPresence',
  owner: 'vfx',
  what: 'in-frame roost coverage on frames where the rear tyre is tearing the surface up',
  unit: 'frac',
  budget: ROOST_PRESENCE_FLOOR,
  compare: 'min',
  consequence: 'the rear steps out and nothing leaves the tyre — BAR\'s first VFX anchor is absent',
  source: 'src/vfx/particles.ts ROOST_PRESENCE_FLOOR',
});

/**
 * Every emission rate in this file is a lie once the pool pins, because `headroom()`
 * throttles them all to whatever 4200 particles allows. A round that tunes a rate against
 * a saturated pool is tuning nothing.
 */
const INV_POOL = declareInvariant({
  id: 'vfx.poolSaturation',
  owner: 'vfx',
  what: 'live particles as a fraction of the pool',
  unit: 'frac',
  budget: 0.92,
  severity: 'warn',
  consequence: 'the emitter is bounded by the pool, so every rate constant in the file stops meaning anything',
  source: 'src/vfx/particles.ts MAX_PARTICLES',
});

/**
 * xorshift32. Deterministic, allocation-free, and good enough for scatter — Math.random
 * would make two capture runs of the same scenario incomparable, which is the whole
 * point of the harness.
 */
let rngState = 0x9e3779b9 >>> 0;
function rnd(): number {
  rngState ^= rngState << 13; rngState >>>= 0;
  rngState ^= rngState >>> 17;
  rngState ^= rngState << 5; rngState >>>= 0;
  return rngState / 4294967296;
}
/** Uniform in [-1, 1]. */
function rnd2(): number {
  return rnd() * 2 - 1;
}

function killAll(): void {
  live = 0;
}

/**
 * Emission throttle as the pool fills.
 *
 * Without it, sustained full roost on loose ground asks for ~300 particles a second
 * against a 4 s lifetime, the pool pins at capacity within seconds, and the frame budget
 * goes with it. Fading the rate down over the last third of the pool degrades density
 * gracefully instead of hard-clipping the newest spawns, which is what you would see as
 * the plume suddenly stopping mid-corner.
 */
function headroom(): number {
  return clamp01((MAX_PARTICLES - live) / (MAX_PARTICLES * 0.35)) * fillThrottle;
}

/**
 * ---------------------------------------------------------------------------
 * W5 R3 — `vfx.overdraw` BECOMES A BOUND INSTEAD OF A HOPE
 * ---------------------------------------------------------------------------
 *
 * The fill invariant has been on the bus for two rounds as a pure observer, and on
 * `fullrun` it measured 12.3x the frame's own area against its own 8.0 budget — on 13 of
 * 5769 frames, which is the shape of the problem: the emitter's rates are constants and the
 * cost they produce is not, because it depends on how close the plume is to the lens and how
 * wide the chase rig has opened its FOV. No constant in this file can bound a quantity that
 * varies by a factor of forty at a fixed emission rate.
 *
 * So the emitter reads its own cost. `stats.overdraw` is the IN-FRAME drawn area as a
 * multiple of the frame's, measured last frame by the same loop that fills the instance
 * buffer, and this scales every rate in the file down by the ratio once it is over budget.
 * It is the same shape as `headroom()` — which is why it lives inside it — and it has the
 * same property: it degrades density smoothly rather than hard-clipping, so what a rider
 * sees when it engages is the plume thinning rather than stopping.
 *
 * 6.5 rather than 8.0 so the invariant has somewhere to sit: a controller that aims exactly
 * at the budget spends half its life over it.
 */
const FILL_BUDGET = 6.5;
/** Smoothing per frame. Fast enough to catch a lens diving into the plume, slow enough not to flicker. */
const FILL_TRACK = 0.3;
let fillThrottle = 1;

/**
 * Allocate one particle. Scalar arguments only — a spawn options object here would be
 * an allocation on the hot path, and this is called up to a few hundred times a second.
 *
 * Returns silently when the pool is full. Dropping the newest is deliberate: the
 * alternative (recycling the oldest) makes a long-lived plume flicker out from the front
 * every time a landing burst fires.
 */
function spawn(
  kind: number,
  x: number, y: number, z: number,
  ivx: number, ivy: number, ivz: number,
  lifeS: number,
  s0: number, sK: number,
  r: number, g: number, b: number,
  a: number,
  fy: number,
): void {
  if (live >= MAX_PARTICLES) return;
  const i = live++;
  px[i] = x; py[i] = y; pz[i] = z;
  vx[i] = ivx; vy[i] = ivy; vz[i] = ivz;
  age[i] = 0;
  life[i] = lifeS;
  invLife[i] = 1 / lifeS;
  size0[i] = s0;
  sizeK[i] = sK;
  alpha0[i] = a;
  colR[i] = r; colG[i] = g; colB[i] = b;
  // Stored as the plane's constant term, so the per-frame test is one multiply-add and
  // needs no memory of where the particle was born.
  //
  // Jittered, because a plane is exactly what it says it is: settled dust pinned to a
  // single analytic surface stacks into a flat table with straight edges where it meets
  // the frustum, and that table was clearly visible as a lighter quadrilateral lying
  // under the near half of the plume. 15 cm of scatter is well inside the ground's own
  // roughness and it breaks the sheet up completely.
  floorY[i] = fy - planeGX * x - planeGZ * z + rnd() * 0.15;
  gradX[i] = planeGX;
  gradZ[i] = planeGZ;
  spin[i] = rnd() * Math.PI * 2;
  spinRate[i] = rnd2() * (kind === KIND_DUST ? 0.35 : 2.4);
  seed[i] = rnd() * 6.283;
  dragK[i] = KIND_DRAG[kind];
  buoyA[i] = KIND_BUOY[kind];
  windK[i] = KIND_WIND[kind];
  edgeP[i] = KIND_EDGE[kind];
  holdK[i] = KIND_HOLD[kind];
  kindOf[i] = kind;
}

function removeAt(i: number): void {
  const last = --live;
  if (i === last) return;
  px[i] = px[last]; py[i] = py[last]; pz[i] = pz[last];
  vx[i] = vx[last]; vy[i] = vy[last]; vz[i] = vz[last];
  age[i] = age[last]; life[i] = life[last]; invLife[i] = invLife[last];
  size0[i] = size0[last]; sizeK[i] = sizeK[last]; alpha0[i] = alpha0[last];
  colR[i] = colR[last]; colG[i] = colG[last]; colB[i] = colB[last];
  floorY[i] = floorY[last]; gradX[i] = gradX[last]; gradZ[i] = gradZ[last];
  spin[i] = spin[last]; spinRate[i] = spinRate[last]; seed[i] = seed[last];
  dragK[i] = dragK[last]; buoyA[i] = buoyA[last];
  windK[i] = windK[last]; edgeP[i] = edgeP[last]; holdK[i] = holdK[last];
  kindOf[i] = kindOf[last];
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

/**
 * Is sky.ts's `gripTerrainLight()` reachable from a raw ShaderMaterial?
 *
 * It is installed by appending to `THREE.ShaderChunk.common` and publishing its three
 * uniforms into every `THREE.ShaderLib` entry, which is how it reaches "every lit
 * material" without any of them importing anything. `#include <common>` picks the
 * function up; the uniforms have to be lifted off ShaderLib by hand because a
 * ShaderMaterial does not inherit them. Both halves are checked rather than assumed —
 * sky.ts skips the whole installation under `?noAerial`, and a shader that calls an
 * undefined function does not render at all.
 */
function skyShadowUniforms(): Record<string, THREE.IUniform> | null {
  const chunks = THREE.ShaderChunk as unknown as Record<string, string>;
  if (!chunks.common || chunks.common.indexOf('gripTerrainLight') < 0) return null;
  const lib = THREE.ShaderLib as unknown as
    Record<string, { uniforms?: Record<string, THREE.IUniform> }>;
  const src = lib.basic && lib.basic.uniforms ? lib.basic.uniforms : null;
  if (!src) return null;
  const out: Record<string, THREE.IUniform> = {};
  for (const k of ['uGripAerial', 'uGripSunOcc', 'uGripSunOccFine']) {
    if (!src[k]) return null;
    out[k] = src[k];
  }
  return out;
}

const particleVert = (sky: boolean): string => /* glsl */ `
${reliefGLSL('vertex', false)}
${sky ? '#include <common>' : ''}

attribute vec3 iPos;
attribute vec4 iCol;
attribute vec4 iShape;   // size, spin, edge exponent, seed
attribute float iKind;   // KIND_DUST / KIND_GRIT / KIND_SPRAY
attribute vec3 iVel;     // camera-relative velocity, m/s — heavy fraction only
attribute float iForm;   // 0 at the ground this was thrown off, 1 at PLUME_FORM_H

uniform vec3 uCover;     // (fade start, hard cap, opacity budget) in frame half-heights
uniform float uGritCover; // hard cap for the heavy fraction alone, half-heights
uniform vec2 uStreak;    // (shutter seconds, longest streak in radii)
uniform float uGritLegacy; // probe only — see __gripVfx.gritLegacy()
// PROBE ONLY, always 0 in play. Metres the DRAWN sprite is translated straight up.
//
// IT EXISTS TO RESPONSE-TEST vfx.plumeSkyShare, WHICH HAD NO SKY IN IT.
//
// Two of the five measurement failures this project has shipped were metrics STRUCTURALLY
// INCAPABLE of detecting the thing they watched for, and both would have been caught in one
// command by injecting a known amount of the defect. thin() does that for the presence
// floor. This does it for the column: it moves every drawn sprite bodily into the sky on ONE
// unstepped simulation state — same particles, same sizes, same alphas, same shading, only
// the drawn height differs — so a sky metric that does not move under it cannot see a column
// either, whatever it reports.
//
// It has to be a UNIFORM rather than a term in the instance write, and that is the whole
// reason the first attempt at this measured nothing: the honest A/B is two draws inside one
// JS task with nothing stepped between them, and on that path update() never runs, so
// anything applied where the instance buffer is filled is applied to a buffer nobody
// rewrites. Display-only, exactly like uGritLegacy.
uniform float uLift;

varying vec2 vUv;
varying vec2 vUpUV;
varying vec4 vCol;
varying float vEdge;
varying float vSeed;
varying float vRadius;
varying float vKind;
varying float vForm;
varying vec3 vWPos;

void main() {
  // The mesh transform is identity: particles live in world space. Expanding in view
  // space is what makes the quad face the camera without a per-particle matrix.
  // uLift is 0 in play. See its declaration — it is the sky invariant's response test.
  vec4 mv = modelViewMatrix * vec4( iPos + vec3( 0.0, uLift, 0.0 ), 1.0 );

  // --- screen coverage ----------------------------------------------------
  // Half the frame's height in metres at this sprite's own depth, read straight out of
  // the projection matrix rather than from a uniform: that makes it track the chase
  // camera's FOV (which swings 62-88 degrees with speed, air and braking) with nothing
  // to keep in sync, and makes the bound an ANGLE rather than a size — which is what it
  // has to be, because the failure this removes is "the puff is too big *from here*".
  float zv = max( -mv.z, 0.05 );
  float halfH = zv / max( projectionMatrix[ 1 ][ 1 ], 1e-4 );
  // The cap is per KIND. A dust puff is allowed to fill the near field — that is what a
  // plume does — but a clod is a small hard object, and a small hard object covering a
  // tenth of the frame is a rock hanging in front of the lens however it is shaded. See
  // GRIT_COVER: it only ever binds on the one that flies past your ear.
  // The heavy fraction only. Spray is deliberately left on the dust path: a rooster tail
  // is a sheet of water that wants to fill frame the way a plume does, and nothing in the
  // wet scenarios rides hard enough to check a change to it honestly.
  bool isGrit = ( iKind > 0.5 && iKind < 1.5 );
  float capF = ( isGrit && uGritLegacy < 0.5 ) ? uGritCover : uCover.y;
  float frac = iShape.x / halfH;
  // Dust is unaffected: uCover.x (0.55) is below 0.62 * uCover.y (0.68), so the min picks
  // the authored fade start and this line is byte-for-byte what it was. For grit the fade
  // is a narrow band just under its own much tighter cap, so a clod at normal chase range
  // — 2-4 m, a few per cent of half-height — never touches it.
  float cov = 1.0 - smoothstep( min( uCover.x, capF * 0.62 ), capF, frac );
  // The ceiling as well as the fade. At the cap the sprite is already at zero alpha, so
  // clamping the drawn radius there costs nothing visible and makes the bound structural.
  float rad = min( iShape.x, capF * halfH );

  // --- the opacity budget -------------------------------------------------
  //
  // The fade above bounds ONE sprite. It does not bound the plume, and the plume is what
  // becomes the plate: at 330 puffs a second against a four-second life there are five to
  // ten of them stacked at every pixel of the near field, each at 0.5-0.8 alpha, and ten
  // layers of 0.6 is 0.994 — a flat wash with no silhouette anywhere in it, which is
  // exactly what captures/w3-vfx-r3-roost2/roost_15p4s.png shows over the lower left
  // quadrant.
  //
  // So bound the OPTICAL contribution per unit of screen instead of per sprite: a puff's
  // alpha falls as the reciprocal of the fraction of frame it covers, above a threshold.
  // A puff at 18% of half-height is untouched; the same puff two metres from the lens
  // covering 55% keeps a third of its alpha, so the stack of them integrates to roughly
  // the optical depth the same dust would have had further away. It costs one divide, it
  // leaves the mid-field plume — where the masses are legible — completely alone, and it
  // is a bound rather than a tuning: whatever the emitter does, the near field cannot
  // saturate.
  float budget = clamp( uCover.z / max( frac, uCover.z ), 0.22, 1.0 );

  // Per-puff aspect. A field of circles is a field of circles however it is shaded, and
  // the eye finds repeated identical outlines instantly. Two cheap trig terms off the
  // particle's own seed make every puff a different ellipse before the fragment shader
  // then chews its edge up.
  vec2 asp = vec2( 1.0 + 0.26 * sin( iShape.w ), 1.0 - 0.20 * sin( iShape.w * 1.7 ) );
  float rot = iShape.y;

  // --- object motion blur, heavy fraction only ---------------------------
  //
  // The particle pass writes no depth, so post.ts's camera-motion blur cannot reach a
  // single sprite in it: at 10 m/s everything else in the frame is smeared and the clods
  // stay razor sharp. Two metres off the lens that is what makes a 6 cm clod read as a
  // crisp egg hanging in the air — and when it is thrown out over ground that falls away,
  // it hangs in the SKY, which is the case no ceiling or apex cap can reach.
  //
  // Stretching the quad along the relative velocity is only half of it. The half that
  // actually removes the artefact is the DIVIDE: an object smeared over four times its
  // own length occupies any one pixel for a quarter of the exposure, so it is four times
  // more transparent there. That is why the same clod goes from an opaque hole in the sky
  // to a faint dash going somewhere, without anyone tuning an alpha.
  //
  // Dust is deliberately excluded. A puff is already soft, already slow, and the plume's
  // legibility rests on being able to count round masses in it (see KIND_EDGE).
  float stretch = 1.0;
  if ( isGrit && uGritLegacy < 0.5 ) {
    // The mesh matrix is identity, so modelViewMatrix is the view matrix.
    vec3 vv = ( modelViewMatrix * vec4( iVel, 0.0 ) ).xyz;
    // ...and the APPARENT motion of that point, which is not the same thing and is the
    // hole R1's streak model had in it.
    //
    // A sprite's screen position is (x/-z, y/-z). Differentiating, the apparent lateral
    // velocity measured in metres at the sprite's OWN depth is
    //
    //     v_app = v.xy - p.xy * ( v.z / p.z )
    //
    // The second term is the perspective / looming term and for a clod thrown out behind
    // a bike that the camera is chasing it is the DOMINANT one: the relative velocity is
    // 15-25 m/s and almost all of it is along the view axis, so v.xy alone is close to
    // zero and R1 measured no motion to smear. That is why every clod stayed razor sharp
    // in a frame that post had smeared everywhere else, and it is why the critic still
    // saw crisp two-tone eggs after a round whose whole subject was removing them.
    vv.xy -= mv.xy * ( vv.z / min( mv.z, -0.05 ) );
    float sp = length( vv.xy );
    float streak = min( sp * uStreak.x, rad * uStreak.y );
    if ( streak > rad * 0.15 && sp > 1e-3 ) {
      rot = atan( vv.y, vv.x );
      stretch = 1.0 + streak / max( rad, 1e-4 );
      // Slightly pinched across the streak, the way a smeared object is.
      asp = vec2( stretch, 0.92 );
    }
  }

  vec2 p = position.xy * asp;
  float c = cos( rot );
  float s = sin( rot );
  vec2 q = vec2( p.x * c - p.y * s, p.x * s + p.y * c );
  mv.xy += q * rad;
  gl_Position = projectionMatrix * mv;

  vUv = position.xy;
  // World up expressed in the quad's own uv frame — the axis relief.ts anchors its
  // shading to, and the reason a puff keeps an underside whichever way the camera and
  // the sun point. The divide by 'asp' inverts the aspect applied above: the quad's
  // offset is rot(spin) * (uv * asp), so a view direction maps back through
  // rot(-spin) / asp. Without it the crown of a stretched puff sits off the vertical.
  vUpUV = normalize( rlfSpriteUp( rot ) / max( asp, vec2( 1e-3 ) ) );
  vCol = vec4( iCol.rgb, iCol.a * cov * budget / stretch );
  vEdge = iShape.z;
  vSeed = iShape.w;
  vRadius = rad;
  vKind = iKind;
  vForm = iForm;
  // The puff's own centre, which is where its share of the sun is sampled. Per-instance
  // rather than per-fragment is the right granularity: the occlusion field is 10 m coarse
  // and 2 m fine, so a metre-wide sprite sits inside one texel either way.
  vWPos = iPos;
${sky ? '  vGripWP = iPos;' : ''}
}
`;

const particleFrag = (sky: boolean): string => /* glsl */ `
${reliefGLSL('fragment', false)}
${sky ? '#include <common>' : `
vec2 gripTerrainLight( vec3 wp ) { return vec2( 1.0 ); }
`}

uniform float uExposure;
uniform vec3 uKindExpo;   // relative exposure per kind: dust, grit, spray
/**
 * PROBE ONLY, always (1,1,1) in play. Per-kind alpha multiplier, so a capture can render
 * ONE fraction at a time.
 *
 * This exists because "which fraction is that blob" has now been answered wrongly twice
 * in this project's history — once routing the artefact to the vegetation owner, once
 * concluding the light fraction was at fault when every measured offender was the heavy
 * one — and both times the argument was made from a whole-frame diff and an opinion about
 * what dust looks like. With this, __gripVfx.only('grit') and a screenshot settle it.
 */
uniform vec3 uKindMask;
/**
 * PROBE ONLY, always 0 in play. 1 restores the heavy fraction's pre-W4 silhouette,
 * optical parameters and (with the vertex half) its screen cap.
 *
 * It exists because the honest way to show that a change fixed something is two frames
 * of the SAME simulation state, and three consecutive waves of this project have had a
 * conclusion overturned by an A/B whose two halves were not comparable. Particle
 * positions are identical either way — this switch is display-only — so the difference
 * between the two frames is exactly the change and nothing else.
 */
uniform float uGritLegacy;
/**
 * PROBE ONLY, always 0 in play. The instrument's own calibration rig.
 *
 *   0  shipping shading
 *   1  a CONSTANT radiance — same sprites, same alphas, same albedos, no form at all
 *   2  a constant times a KNOWN world-up ramp of crown:belly 4.0, built from the same
 *      dot(uv, upUV) term rlfSpriteShade anchors its own shading to
 *
 * It exists to validate the instrument rather than the effect, and the first version of
 * this round's metric FAILED on mode 1: it read the plume's per-puff albedo jitter
 * (j = 0.68 + rnd * 0.56, a +-28 % value spread authored in spawnDust) as lit form and
 * reported crownBelly 1.20 / formRange 1.84 for a plume that by construction had none.
 * Modes 1 and 2 are what turn "I measured a gradient" into "I measured a gradient that is
 * light rather than paint": the metric divides the shipping frame by the mode-1 frame,
 * which cancels albedo exactly, and mode 2 proves the result is not merely always 1.
 *
 * See captures/w5-vfx-tools/form.mjs --validate.
 */
uniform float uFlat;

varying vec2 vUv;
varying vec2 vUpUV;
varying vec4 vCol;
varying float vEdge;
varying float vSeed;
varying float vRadius;
varying float vKind;
varying float vForm;
varying vec3 vWPos;

uniform vec4 uPlumeForm;  // (key at base, sky at base, sky at top, ground bounce at base)
uniform float uPlumeGndTop;

void main() {
  float r2 = dot( vUv, vUv );
  if ( r2 > 1.0 ) discard;
  float r = sqrt( r2 );

  // A perfect disc is the tell that this is a sprite. Three angular octaves on the
  // radius, phased off the particle's own seed, cost a handful of instructions and give
  // every puff a cauliflower outline nothing else in the plume shares. The third octave
  // is the one that matters: two harmonics make a smooth blob, and a smooth blob with a
  // hard edge reads as a pebble.
  //
  // R2 halved the amplitudes. They were set when a dust sprite had a hard plateau edge,
  // where a strong outline modulation is the only thing standing between the plume and a
  // field of coins. With the plateau gone the outline is no longer visible at all, so all
  // this term can still do at 0.22/0.14/0.09 is squash the sprite hard enough to bias
  // where its mass sits — which is what turned the light fraction into vertically
  // elongated eggs in captures/_crit-vfx-probe/shots4/dustcam_3p8_all.png.
  float a = atan( vUv.y, vUv.x );
  float lump = 1.0
    + 0.11 * sin( a * 3.0 + vSeed )
    + 0.07 * sin( a * 5.0 - vSeed * 2.3 )
    + 0.05 * sin( a * 9.0 + vSeed * 3.7 );
  // R2: 0.38 -> 0.20. bulge lobes the implied volume so the key catches one facet and
  // misses the next, which is a good idea on a sprite you are meant to see individually
  // and a bad one on an element of a medium: at 0.38 every puff carries a strong lit
  // crown and a dark belly of its OWN, and thirty of those is thirty legible balls rather
  // than one plume with a lit crown. The plume-scale gradient survives, because each
  // sprite is still shaded along world up — it is only the per-sprite lobing that goes.
  float bulge = 0.20;

  // ...and a CLOD IS NOT A CLOUD, which is the whole of the defect this round was called
  // for. captures/w3-vfx-r3-dustcam/dustcam_7p8s.png and w3-vfx-r3-roost/roost_7p4s.png
  // show near-black lobed blobs hanging in the sky; three rounds read them as dust and
  // tuned the light fraction. Measured per sprite with the fixed A/B
  // (captures/w4-vfx-tools/scan.mjs, control 0 px): EVERY ONE of the 28 sky-drawn
  // offenders in 'roost' is kind 1, at 1.5-2.7 m above the ground and 1.5-3 m from the
  // lens, and the worst darkens its own patch of sky by 150 of 255 code values. Two
  // things made a 6 cm clod read as a floating cauliflower, and both are here:
  //
  //  - it wore the DUST outline. Three angular harmonics at 0.22/0.14/0.09 is a plume
  //    silhouette; on a compact object it is a clover leaf, and BAR names floating
  //    objects as the most damaging artefact under this direction.
  //  - it was lit as 2.6 m of optical thickness (see the params below), which for a
  //    fist-sized lump of dirt is not a volume, it is a black hole with a soft edge.
  if ( vKind > 0.5 && vKind < 1.5 && uGritLegacy < 0.5 ) {
    // Flat facets with a hard corner between them. The index is wrapped modulo the facet
    // count so the outline is continuous across atan's own +-pi seam — without the wrap
    // every clod has a slit cut out of one side.
    float fa = ( a * 0.15915494 + 0.5 ) * 7.0;
    float fi = floor( fa );
    float ff = fa - fi;
    float h0 = rlfHash21( vec2( mod( fi, 7.0 ), vSeed ) );
    float h1 = rlfHash21( vec2( mod( fi + 1.0, 7.0 ), vSeed ) );
    lump = mix( 0.80 + 0.34 * h0, 0.80 + 0.34 * h1, ff );
    // A chunk of dirt is a compact solid, not a bunch of grapes.
    bulge = 0.14;
  }

  float d = r / lump;
  if ( d > 1.0 ) discard;

  // The volume the sprite implies: a hemisphere bulged into lobes, so the key catches
  // one and misses the next. Divided by the outline modulation so the implied volume
  // matches the shape actually drawn.
  vec3 nV = rlfSpriteNormal( vUv / max( lump, 1e-3 ), d, bulge, vSeed );

  // Three optical recipes. This branch is uniform across every fragment of an instance,
  // so it costs a scalar compare, and it is the difference between a plume and a spatter
  // of gravel lit as if it were smoke.
  RlfSpriteParams P;
  if ( vKind < 0.5 ) {
    // Dust.
    //
    // W5: NOT rlfDustParams(vRadius) any more, and this is a case of one round's fix
    // silently disabling another's. The library scales optical thickness with the
    // sprite's own radius — clamp(r * 1.3, 0.05, 3.0) — which is exactly right when a
    // puff is a metre across, and it is what gives the sprite its darker underside.
    // W4 R3 then took DUST_SIZE_MAX from 1.15 m to 0.26 m for entirely separate and good
    // reasons (a 1.15 m sprite at 2 m from the lens is a plate over the frame). At 0.26 m
    // the library returns 0.34 m of thickness, the extinction term becomes
    // exp2(-0.34 * 0.9) = 0.81 at the belly against 1.0 at the crown, and the puff's
    // underside — the thing four rounds have been trying to restore — is a 1.23x gradient.
    // Nobody re-derived the lighting when the size cap moved, so the plume lost its
    // volume as a side effect of a screen-coverage bound.
    //
    // The library's assumption is that a sprite IS the volume. Here it is an ELEMENT of a
    // medium: 5-10 of them are stacked at every pixel and the material a light ray crosses
    // is the plume, not the puff. So the thickness is authored against the plume and only
    // modulated by the sprite, with a floor that does not collapse when the size cap moves
    // again. At the 0.11-0.26 m radii that ship this is 0.99-1.59 m, a belly at
    // exp2(-1.43) = 0.37 of the crown.
    P.wrap = 0.38;                                    // a real terminator, not 0.55 of wrap
    P.thickness = clamp( 0.55 + vRadius * 4.0, 0.55, 2.2 );
    P.ext = 0.90;
    P.trans = 0.80;
    P.ao = 0.55;
  } else if ( vKind < 1.5 ) {
    // A clod is an OPAQUE SOLID and it must be lit like one: a real terminator, a
    // sunward face that is genuinely bright, and a shaded face that still collects the
    // sky. That is form and light, which is what the direction asks for.
    //
    // The previous values were wrap 0.05 / thickness 2.6 / ao 0.85, and thickness is in
    // metres of material the light has to cross: 2.6 m of dirt. Everything downstream of
    // it collapsed — extinction took the belly to 18% of the key and self-occlusion took
    // the interior to 14% of the ambient — so a clod came out as a flat near-black
    // cut-out with no lit side anywhere on it. Against dirt that passes; against the sky
    // it is a hole, which is exactly what the captures show.
    //
    // 8 cm is the honest number: it is the clod's own size, so the extinction term does
    // what it is for (a slightly darker core) instead of erasing the object.
    //
    // R2 walked part of the way back. 0.08 m of thickness with an ao of 0.25 is not "an
    // opaque solid", it is porcelain: extinction and self-occlusion both vanish, so
    // rlfSpriteShade returns very nearly the full key over the crown and very nearly the
    // full sky in the belly, and the clod comes out as a bright two-tone egg. Against the
    // sky that reads better than R0's black hole; against DIRT — which is where the
    // measurement says they all are now — it is a pale object sitting in front of the
    // hillside, and 20-40 of those is the countable-stamps defect with the polarity
    // flipped.
    //
    // 0.34 m is still an order of magnitude under R0's absurd 2.6 m, and it leaves a clod
    // with a genuinely shaded side (about 0.55 of the key at the terminator, 0.62 of the
    // ambient in the interior) instead of either extreme.
    P.wrap = mix( 0.14, 0.05, uGritLegacy );
    P.thickness = mix( 0.34, 2.6, uGritLegacy );
    P.ext = mix( 0.85, 0.95, uGritLegacy );
    P.trans = 0.0;
    P.ao = mix( 0.48, 0.85, uGritLegacy );
  } else {
    // Water. Scatters harder than dust and passes far more light through, and a droplet
    // is too small for its own radius to build a belly, so the thickness is authored.
    P.wrap = 0.40;
    P.thickness = 0.55;
    P.ext = 0.90;
    P.trans = 1.60;
    P.ao = 0.30;
  }

  // The scene's own light, and then the scene's own SHADOW.
  //
  // relief.ts's block carries the sun, the sky and the ground bounce, but sunVis is
  // global — it knows the sun is up, not that this particular cubic metre of air is
  // under a fir. Without the second half a plume thrown inside a stand of trees is lit as
  // though it were standing in the open, and a bright plume over ground the same trees
  // have taken four stops off is the single most reliable way to make an effect read as
  // pasted on. gripTerrainLight() is the same ridge / canopy / rider occlusion the
  // hillside under the plume is already using, so the two now agree by construction.
  //
  // It also buys the good version of the same thing for free: a puff that rises out of a
  // shadow into a shaft of light gets lit while the ground below it stays dark, because
  // the field stores the ALTITUDE of the shadow line and this samples it at the puff's
  // own height.
  RlfEnv E = rlfEnv();
  vec2 tl = gripTerrainLight( vWPos );
  E.sunVis *= tl.x;
  E.sky *= tl.y;
  E.gnd *= tl.y;

  // --- and the plume's OWN optical depth, which is the W5 change ---------------------
  //
  // Everything above is the light arriving at the plume. This is the light arriving at
  // THIS PARCEL OF IT: sun reaching a puff one metre inside a roost has crossed the metre
  // of plume above it, and the same material takes most of the sky dome off it too, while
  // the bounce off the dirt directly underneath is strongest exactly there. So the base of
  // the plume is key-starved, warm and dim, and its crown is in open sun under the whole
  // sky. See the block above PLUME_FORM_H — and note that this is per INSTANCE, which is
  // the entire point: a per-sprite gradient of 4.0 measures 1.2 once ten of them are
  // stacked, and a per-instance one cannot be diluted at all because every puff at a given
  // height in the plume agrees.
  //
  // Dust only. A clod is not inside anything and spray is a sheet off the tyre; giving
  // either of them a plume's self-shadowing would only make them dark near the ground.
  if ( vKind < 0.5 ) {
    E.sunVis *= mix( uPlumeForm.x, 1.0, vForm );
    E.sky *= mix( uPlumeForm.y, uPlumeForm.z, vForm );
    E.gnd *= mix( uPlumeForm.w, uPlumeGndTop, vForm );
  }
  // Multiply, do not replace: the sprite keeps its authored dust-albedo hue and gets its
  // light — warm key, cool sky above, ground bounce below — from the scene's own block.
  // Per-kind exposure. The master is authored for the light fraction — see DUST_EXPOSURE
  // — and the ratio holds the heavy fraction where it was, because a clod is an opaque
  // solid and does not get a suspended-scattering bonus.
  float expo = uExposure * ( vKind < 0.5 ? uKindExpo.x : vKind < 1.5 ? uKindExpo.y : uKindExpo.z );
  vec3 lit = rlfSpriteShade( nV, vUv, vUpUV, d, P, E ) * expo;
  // Probe only. See uFlat. Mode 1 is the flat control; mode 2 is the same control times a
  // world-up ramp of known crown:belly ratio, anchored to the identical dot(uv, upUV)
  // term rlfSpriteShade uses, so the calibration exercises the real axis rather than a
  // screen-space one.
  if ( uFlat > 0.5 ) {
    float vertP = 0.5 + 0.5 * clamp( dot( vUv, vUpUV ), -1.0, 1.0 );
    float ramp = uFlat > 1.5 ? ( 1.0 + ( 4.0 - 1.0 ) * vertP ) * 0.4 : 1.0;
    lit = vec3( 0.62 ) * ramp;
  }

  // --- the radial profile, and it is the centre of R2 --------------------------------
  //
  // (1 - d^2)^p. For dust p = 2.1: zero slope at the rim, a soft shoulder, NO LOCATABLE
  // OUTLINE AT ANY OPACITY. For grit p = 0.32, which is very nearly a disc, because a
  // clod is a hard object.
  //
  // What this replaced was a plateau — full opacity across the middle half and a falloff
  // over the rest — introduced by R3 on the argument that a plume with no edges anywhere
  // in it is a fog bank. That is true of the PLUME and false of the SPRITE, and three
  // critics in a row have described the result of confusing them: countable, individually
  // legible sprite stamps at every density. See KIND_EDGE.
  float q = max( 1.0 - d * d, 0.0 );
  float f = pow( q, vEdge );

  // --- and where the structure comes from instead ------------------------------------
  //
  // Once no sprite has a rim, something else has to give the medium detail, or a plume is
  // a smooth airbrushed gradient — the other failure the critic named ("a flat
  // structureless tan wash"). Two things do it, and neither is a sprite outline:
  //
  //  - HERE: a turbulent fBm on the sprite's own alpha, two octaves at ~2 cycles across
  //    the quad, phased off the particle's seed and rotating with it. It is a DENSITY
  //    modulation, so overlapping sprites multiply their variation together the way real
  //    optical depth does, and the structure it produces is finer than one sprite and
  //    therefore cannot be counted.
  //  - in the emitter: gusting, so the density varies at plume scale as well. See
  //    GUST_DEPTH.
  //
  // Dust only. A clod is a solid and has no interior density to vary; spray is small
  // enough that two octaves across it is noise.
  if ( vKind < 0.5 ) {
    float turb = rlfFbm( vUv * 2.3 + vSeed * 11.3, 2 );
    f *= 0.46 + 1.08 * turb;
  }

  float mask = vKind < 0.5 ? uKindMask.x : vKind < 1.5 ? uKindMask.y : uKindMask.z;
  gl_FragColor = vec4( vCol.rgb * lit, vCol.a * f * mask );

  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

let mesh: THREE.Mesh | null = null;
let geo: THREE.InstancedBufferGeometry | null = null;
/**
 * Whether the plume got sky.ts's analytic sun occlusion. Recorded so a capture can assert
 * it: a silently-missing shadow term does not throw and does not warn, it just lights
 * every puff on the mountain as though it stood in the open.
 */
let skyWired = false;
let mat: THREE.ShaderMaterial | null = null;
let attrPos: THREE.InstancedBufferAttribute | null = null;
let attrCol: THREE.InstancedBufferAttribute | null = null;
let attrShape: THREE.InstancedBufferAttribute | null = null;
let attrKind: THREE.InstancedBufferAttribute | null = null;
let attrVel: THREE.InstancedBufferAttribute | null = null;
let attrForm: THREE.InstancedBufferAttribute | null = null;

function build(): void {
  geo = new THREE.InstancedBufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(
    new Float32Array([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0]), 3,
  ));
  geo.setIndex([0, 1, 2, 0, 2, 3]);

  attrPos = new THREE.InstancedBufferAttribute(iPosArr, 3);
  attrCol = new THREE.InstancedBufferAttribute(iColArr, 4);
  attrShape = new THREE.InstancedBufferAttribute(iShapeArr, 4);
  attrKind = new THREE.InstancedBufferAttribute(iKindArr, 1);
  attrVel = new THREE.InstancedBufferAttribute(iVelArr, 3);
  attrForm = new THREE.InstancedBufferAttribute(iFormArr, 1);
  attrPos.setUsage(THREE.DynamicDrawUsage);
  attrCol.setUsage(THREE.DynamicDrawUsage);
  attrShape.setUsage(THREE.DynamicDrawUsage);
  attrKind.setUsage(THREE.DynamicDrawUsage);
  attrVel.setUsage(THREE.DynamicDrawUsage);
  attrForm.setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute('iPos', attrPos);
  geo.setAttribute('iCol', attrCol);
  geo.setAttribute('iShape', attrShape);
  geo.setAttribute('iKind', attrKind);
  geo.setAttribute('iVel', attrVel);
  geo.setAttribute('iForm', attrForm);
  geo.instanceCount = 0;

  const skyU = skyShadowUniforms();
  skyWired = skyU !== null;

  mat = new THREE.ShaderMaterial({
    vertexShader: particleVert(skyU !== null),
    fragmentShader: particleFrag(skyU !== null),
    uniforms: {
      ...(skyU ?? {}),
      // The shared light block. Same IUniform object — and therefore the same
      // Float32Array — as every other consumer of relief.ts, so the sun this plume is
      // lit by is the sun the mountain under it is lit by, by construction.
      ...reliefUniforms(),
      uCover: { value: new THREE.Vector3(COVER_FADE0, COVER_FADE1, COVER_BUDGET) },
      uGritCover: { value: GRIT_COVER },
      uStreak: { value: new THREE.Vector2(GRIT_SHUTTER, GRIT_STREAK_MAX) },
      uGritLegacy: { value: 0 },
      /** PROBE ONLY, always 0 in play. See the uLift declaration in the vertex shader. */
      uLift: { value: 0 },
      uExposure: { value: DUST_EXPOSURE },
      uKindExpo: { value: new THREE.Vector3(KIND_EXPO[0], KIND_EXPO[1], KIND_EXPO[2]) },
      uKindMask: { value: new THREE.Vector3(1, 1, 1) },
      uFlat: { value: 0 },
      uPlumeForm: {
        value: new THREE.Vector4(
          PLUME_KEY_BASE, PLUME_SKY_BASE, PLUME_SKY_TOP, PLUME_GND_BASE,
        ),
      },
      uPlumeGndTop: { value: PLUME_GND_TOP },
    },
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.NormalBlending,
    side: THREE.DoubleSide,
  });

  mesh = new THREE.Mesh(geo, mat);
  mesh.name = 'vfx-particles';
  mesh.frustumCulled = false;
  mesh.matrixAutoUpdate = false;
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  // After the trail tread (renderOrder 4) and the tire tracks (5), which are both
  // ground decals: dust is in the air above them and must composite on top.
  mesh.renderOrder = 12;
  mesh.visible = false;
}

// ---------------------------------------------------------------------------
// Emission
// ---------------------------------------------------------------------------

/** Fractional spawn carry, so a rate of 0.3/step still emits at the right average. */
let accRoostDust = 0;
let accRoostGrit = 0;
let accRollDust = 0;
let accFrontDust = 0;
let accRockGrit = 0;
let accSpray = 0;

/** Sim clock, for the emitter's gusting. Never used for anything the player can feel. */
let simClock = 0;

/** Sim events are cleared by main.ts once per *render* frame, not per sim step. */
let landedConsumed = false;
let bottomConsumed = false;

let lastX = 0;
let lastZ = 0;
let hadPos = false;

/** Smoothed camera velocity, world m/s. See the object-blur note in `update()`. */
let camPX = 0, camPY = 0, camPZ = 0;
let camVX = 0, camVY = 0, camVZ = 0;
let haveCamPrev = false;

/**
 * Peak roost DEMAND since the last render frame: how hard the rear tyre is tearing the
 * surface up, times how much that surface has to give, times how fast the bike is going.
 * 0 when the tyre is gripping, on rock, or in the wet.
 *
 * It exists because `vfx.roostPresence` — the only FLOOR this piece has on the audit bus —
 * has to be observed on the frames where BAR says there must be a roost and skipped on the
 * ones where a silent tyre is correct. Emission runs in `fixed()` at 240 Hz and the
 * measurement runs in `update()` at frame rate, so the demand is peak-held across the
 * intervening sim steps and cleared when it is read. Peak rather than mean because a
 * 0.9 s brake stab inside a 3.4 s cycle is exactly the case being graded, and an average
 * over a render frame's worth of sim steps would report it either way.
 */
let roostDemand = 0;
/** Bike speed at the moment of that peak, m/s. */
let roostDemandSpeed = 0;
/** How long the demand has been continuously above the gate, s. See ROOST_PRESENCE_LAG. */
let demandHeldS = 0;

function surfIndex(s: Surface): number {
  const i = s as number;
  return i >= 0 && i < DUST_ALBEDO.length ? i : 0;
}

// ---------------------------------------------------------------------------
// The emission cone.
//
// This is the part R2 did not have, and its absence is the whole of "no form and no
// throw". R2 built a velocity as `axis * throwV + rnd2() * throwV * 0.65` **per world
// axis** — three independent uniform noises of ±0.65 v against a directed term of 1.0 v.
// That is not a cone with a wide angle. It is a ball centred slightly off the origin: a
// third of the particles came out sideways at more than half throw speed, some came out
// forwards, and the directed component never survived to be seen. An emitter that throws
// in every direction at once has no direction, and a plume that has no direction can only
// read as a haze centred on the thing emitting it.
//
// The fix is to sample *inside a cone* — build an orthonormal basis on the throw axis and
// perturb within it — which is the difference between "backwards, roughly" and "every
// which way". The sample is triangular rather than uniform so the cone is dense on its
// axis and ragged at its edge, which is the shape of a plume.
// ---------------------------------------------------------------------------

/** Cone basis. Module scope so the spawn path allocates nothing. */
let coneAX = 0, coneAY = 1, coneAZ = 0;
let coneT1X = 1, coneT1Y = 0, coneT1Z = 0;
let coneT2X = 0, coneT2Y = 0, coneT2Z = 1;
/** Last direction drawn from the cone. */
let dirX = 0, dirY = 1, dirZ = 0;

/**
 * Point the cone. `hx/hz` is the horizontal throw direction (need not be normalised) and
 * `rise` tips it up out of the ground plane.
 */
function setCone(hx: number, hz: number, rise: number): void {
  const hl = Math.hypot(hx, hz);
  // Degenerate horizontal throw — a wheel shearing straight down, which happens on a
  // landing. Fall back to straight up rather than producing NaN.
  if (hl < 1e-5) {
    coneAX = 0; coneAY = 1; coneAZ = 0;
    coneT1X = 1; coneT1Y = 0; coneT1Z = 0;
    coneT2X = 0; coneT2Y = 0; coneT2Z = 1;
    return;
  }
  const ux = hx / hl;
  const uz = hz / hl;
  const l = Math.hypot(1, rise);
  coneAX = ux / l; coneAY = rise / l; coneAZ = uz / l;
  // Horizontal perpendicular: the fan direction. The axis always carries a horizontal
  // component here, so this never degenerates.
  coneT1X = -uz; coneT1Y = 0; coneT1Z = ux;
  // axis x t1 — the remaining perpendicular, mostly vertical.
  coneT2X = coneAY * coneT1Z - coneAZ * coneT1Y;
  coneT2Y = coneAZ * coneT1X - coneAX * coneT1Z;
  coneT2Z = coneAX * coneT1Y - coneAY * coneT1X;
}

/**
 * Slope of the ground the current emitter is standing on, dy/dx and dy/dz.
 *
 * Clamped: past about 40 degrees the plane stops being a useful extrapolation of the
 * hillside a few metres away and starts being a wall that dust piles up against.
 */
let planeGX = 0;
let planeGZ = 0;
/**
 * How far inside a built trail corridor the ground the current emitter is standing on is,
 * 0..1. Module scope for the same reason the cone basis is: the spawn path allocates
 * nothing. Set alongside `setGroundPlane` at every emission site. See DUST_TREAD.
 */
let emitTread = 0;
function setGroundPlane(nx: number, ny: number, nz: number): void {
  const iy = 1 / Math.max(ny, 0.25);
  planeGX = clamp(-nx * iy, -0.85, 0.85);
  planeGZ = clamp(-nz * iy, -0.85, 0.85);
}

/** Draw one direction from the cone into dirX/dirY/dirZ. */
function sampleCone(spreadH: number, spreadV: number): void {
  // Sum of two uniforms: triangular, so the density falls off linearly from the axis.
  // One uniform would give a flat-topped cone with a hard rim, which is a nozzle.
  const s1 = (rnd() + rnd() - 1) * spreadH;
  const s2 = (rnd() + rnd() - 1) * spreadV;
  const x = coneAX + coneT1X * s1 + coneT2X * s2;
  const y = coneAY + coneT1Y * s1 + coneT2Y * s2;
  const z = coneAZ + coneT1Z * s1 + coneT2Z * s2;
  const l = Math.hypot(x, y, z) || 1;
  dirX = x / l; dirY = y / l; dirZ = z / l;
}

/**
 * The light fraction of a roost: launched hard along the cone, then slowed by drag into a
 * mass that hangs for seconds.
 *
 * `carryX/carryZ` is the bike's own velocity partially entrained into the puff. It is
 * real — material lifted by a tire leaves in the bike's wake, not at rest in the world —
 * but see DUST_CARRY for why it is now a fifth of what it was.
 *
 * Size is the other half of the cone. R2 started every puff at 0.34-0.58 m and grew it
 * slowly, so the plume was already at full width the instant it left the tire: a
 * cylinder, not a cone. Born at 0.09-0.22 m and growing on sqrt(age), the same particles
 * describe something narrow and fast at the contact patch that opens out as it slows —
 * which is what a cone *is*, and it comes for free out of the growth law rather than
 * being drawn.
 */
function spawnDust(
  x: number, y: number, z: number,
  speed: number, spreadH: number, spreadV: number,
  carryX: number, carryZ: number,
  si: number, scale: number, opacity: number, ground: number,
): void {
  const c = DUST_ALBEDO[si];
  // What the tyre is actually tearing up: the hillside's own material out on raw ground,
  // the BUILT TREAD inside a corridor. See DUST_TREAD — a roost off a trail that is the
  // colour of the scree beside it reads as smoke, which is what the round-1 capture shows.
  const tw = emitTread;
  const cr = c[0] + (DUST_TREAD[0] - c[0]) * tw;
  const cg = c[1] + (DUST_TREAD[1] - c[1]) * tw;
  const cb = c[2] + (DUST_TREAD[2] - c[2]) * tw;
  sampleCone(spreadH, spreadV);
  // Wide spread of speeds within the cone. The slow tail is what fills the near field of
  // the plume; the fast head is what reaches the far end of it.
  const v = speed * (0.45 + rnd() * 0.9);
  // Cap what leaves the patch going *up*. The cone axis is tipped 37 degrees and the
  // speed distribution has a long tail, so the fastest few percent of a hard roost were
  // launching at 12 m/s vertical and arriving seven metres up — isolated puffs level with
  // the canopy, which reads as weather rather than as a tire. A ballistic cap is the
  // honest bound: dust leaves a contact patch at the tire's surface speed, and the
  // vertical share of that is small.
  const vy = Math.min(dirY * v, 6.0);
  // Value jitter per puff, wider than R2's. Neighbouring masses at visibly different
  // values is a large part of what stops a cluster reading as one continuous surface.
  const j = 0.68 + rnd() * 0.56;
  spawn(
    KIND_DUST,
    // Born tight on the patch. A wide birth disc erases the first half-second of the
    // cone, which is the half-second where it is narrow and therefore where it is
    // legible as a cone at all.
    // W4 R3: born up the back of the casing (0.12-0.38 m) rather than on the deck
    // (0.05-0.15). Material is stripped off the tread all the way round the contact arc,
    // not out of a puddle under it — and at the measured camera the deck is the bottom
    // edge of the frame, so a puff born there is drawn below the picture AND is thinned
    // to a sixth by the settled-dust term (`lift`) before anything else touches it.
    // W5 R2: 0.12-0.38 m -> 0.10-0.55 m up the casing. Material is stripped off the tread
    // over the whole contact arc, and the arc of a 29in wheel runs from the deck to well
    // above the axle. Measured this round, the contact patch projects to y = 869-884 on a
    // 900 px frame from a level chase rig, so where along that arc a puff is born is worth
    // 3 % of the frame's height to the plume's own footprint — the difference between a
    // plume that starts inside the picture and one that starts under it.
    x + rnd2() * 0.09, y + 0.10 + rnd() * 0.45, z + rnd2() * 0.09,
    carryX + dirX * v,
    vy,
    carryZ + dirZ * v,
    DUST_LIFE_MIN + rnd() * (DUST_LIFE_MAX - DUST_LIFE_MIN),
    (0.11 + rnd() * 0.14) * scale,
    // Growth coefficient against sqrt(age), not age. A puff expands by turbulent mixing,
    // which goes as the square root of time — fast for the first half second, then
    // settling. Linear growth is not just less true, it is unusable: over a 5 s life it
    // takes a 0.4 m puff to three metres, and a three-metre puff two metres from the
    // chase camera is a smear across the whole lower third of the frame.
    //
    // R2 cut it from 0.44-0.74 to 0.15-0.28, which with DUST_SIZE_MAX is the change that
    // put the light fraction back on screen. At 0.44+ a puff reached the old 1.15 m cap
    // inside 1.5 s and then sat at the cap for the remaining 70% of its life, where the
    // opacity budget and the near-plane fade between them took its drawn alpha to 0.04.
    // The plume is made of many small masses now, not four huge invisible ones.
    (0.19 + rnd() * 0.15) * scale,
    cr * j, cg * j, cb * j,
    opacity,
    ground - 0.05,
  );
}

/**
 * The heavy fraction: ballistic, hard-edged, gone inside a second and a half.
 *
 * ---------------------------------------------------------------------------
 * W4 — the two numbers that made this the "floating blob" defect
 * ---------------------------------------------------------------------------
 *
 * R3 pushed the radius to 4.6-11.8 cm (a 9-24 cm lump) on the argument that at 2-5 cm a
 * clod was "under two pixels at chase-camera range". The argument was right about the
 * direction and wrong about the size, and nobody measured the result: at the 1.5-3 m the
 * roost actually lives at, that is a sprite **40-60 px across**, which is not a clod, it
 * is a rock. Measured per sprite in `roost` — the worst offender was 45 px of radius at
 * 1.5 m and took its patch of sky down by 150 of 255 code values.
 *
 * 2.6-6.6 cm of radius is a 5-13 cm clod: a genuine fistful of dirt, 10-20 px at chase
 * range, and legible there because it now has a lit face and a faceted edge rather than
 * because it is large. Legibility is bought with form and light, not with size — which
 * is the whole art direction in one sentence.
 *
 * The vertical cap is the other half. 9 m/s is a four-metre apex, and four metres up in
 * front of a chase camera 1.5 m off the deck is the SKY. A clod leaves a contact patch
 * at the tyre's surface speed and the vertical share of that is small; 4.6 m/s is a
 * 1.1 m apex, which clears a rider's knee and stays inside the hillside behind it.
 */
function spawnGrit(
  x: number, y: number, z: number,
  speed: number, spreadH: number, spreadV: number,
  carryX: number, carryZ: number,
  si: number, scale: number, ground: number,
): void {
  const c = GRIT_ALBEDO[si];
  const tw = emitTread;
  const cr = c[0] + (GRIT_TREAD[0] - c[0]) * tw;
  const cg = c[1] + (GRIT_TREAD[1] - c[1]) * tw;
  const cb = c[2] + (GRIT_TREAD[2] - c[2]) * tw;
  sampleCone(spreadH, spreadV);
  const v = speed * (0.5 + rnd() * 0.9);
  const vy = Math.min(dirY * v, GRIT_VY_CAP);
  // ---------------------------------------------------------------------------
  // W4 R2 — a POWER LAW, not a 2.5x uniform range.
  //
  // 0.026 + rnd*0.040 is 2.6-6.6 cm of radius: every clod on screen within a factor of
  // 2.5 of every other one, which is why the measured 20-40 of them in frame read as a
  // row of repeated objects rather than as thrown dirt. Ejecta does not have a
  // characteristic size — it has a size *distribution*, heavily weighted to the small,
  // and the eye is very good at telling the two apart.
  //
  // rnd^2.4 puts the median at 1.9 cm (4-5 px at chase range: below the threshold of
  // being an object at all) and leaves the top few percent at 7-9 cm, so a hard roost
  // still throws a handful of legible lumps. Same rate, same energy, a tenth of the
  // legible-object count.
  const u = rnd();
  const rad = (0.010 + u * u * (u * 0.4 + 0.6) * 0.082) * scale;
  spawn(
    KIND_GRIT,
    x + rnd2() * 0.06, y + 0.07, z + rnd2() * 0.06,
    carryX + dirX * v,
    vy,
    carryZ + dirZ * v,
    0.55 + rnd() * 0.95,
    rad, 0,
    cr, cg, cb,
    // 0.95 -> 0.72. A clod is opaque, but a 6 cm clod photographed at 2 m through a
    // 1/90 s shutter is not: it is moving, and the streak divide below only reaches the
    // part of the motion the quad is stretched along. This is the honest residual.
    0.72,
    ground - 0.02,
  );
}

function spawnSpray(
  x: number, y: number, z: number,
  speed: number, spreadH: number, spreadV: number,
  carryX: number, carryZ: number,
  ground: number,
): void {
  const c = SPRAY_ALBEDO;
  sampleCone(spreadH, spreadV);
  const v = speed * (0.5 + rnd() * 0.9);
  spawn(
    KIND_SPRAY,
    x + rnd2() * 0.07, y + 0.09, z + rnd2() * 0.07,
    carryX + dirX * v,
    dirY * v,
    carryZ + dirZ * v,
    0.35 + rnd() * 0.45,
    0.035 + rnd() * 0.05, 0.12,
    c[0], c[1], c[2],
    0.34,
    ground - 0.02,
  );
}

/**
 * A landing, a bottom-out or a crash: a radial skirt off the contact patch plus whatever
 * the surface throws. Radial rather than directional because the energy went straight
 * down — a burst that fires backwards reads as a wheelspin, not an impact.
 *
 * Each spoke of the skirt gets its own cone, tipped only slightly up: an impact plume
 * spreads *along the ground* first and stands up afterwards, which is what distinguishes
 * it from the rear-tire roost above.
 */
function burst(
  x: number, y: number, z: number,
  severity: number, si: number, dustCoef: number, wetMul: number,
  carryX: number, carryZ: number,
  gnx: number, gny: number, gnz: number, tread: number,
): void {
  setGroundPlane(gnx, gny, gnz);
  emitTread = clamp01(tread);
  // R2's count was `4 + 34 * sev^2`, which is eight particles for the 0.35-severity
  // landing that a normal roller produces — measured against the air run, most landings
  // in normal riding sit between 0.2 and 0.5, so the *typical* impact burst was under a
  // dozen sprites and simply could not be seen. The square was throwing the whole effect
  // at the rare huge case. A 1.4 power keeps big landings clearly bigger without leaving
  // the common one with nothing.
  const n = Math.round(8 + 52 * Math.pow(severity, 1.4));
  accEmitBurst += n;
  const outV = 2.4 + 6.0 * severity;
  const rise = 0.2 + 0.3 * severity;
  for (let k = 0; k < n; k++) {
    // Even spokes with jitter rather than a free-for-all: a skirt has a rim, and n
    // independent uniform angles leaves gaps in it that read as a random spatter.
    const a = ((k + rnd() * 0.8) / n) * Math.PI * 2;
    setCone(Math.cos(a), Math.sin(a), rise);
    const v = outV * (0.5 + rnd() * 0.7);
    if (dustCoef * wetMul > 0.12) {
      spawnDust(x, y, z, v, 0.3, 0.4, carryX * 0.6, carryZ * 0.6, si,
        1.0 + severity * 0.9, (0.52 + 0.30 * severity) * dustCoef * wetMul, y);
    }
    // Rock and hardpack do not puff, they chip. That is the whole reason a landing on
    // slab looks different from a landing in loam, and it is free here.
    if (rnd() < 0.45 + 0.5 * (1 - dustCoef)) {
      // W4: rise +0.5 -> +0.22 and the scale ceiling 1.85 -> 1.15. An impact skirt
      // spreads ALONG THE GROUND first — that is what distinguishes it from the rear
      // tyre's roost — and a landing that fired 22 cm lumps four metres straight up was
      // the single densest source of the sky blobs.
      setCone(Math.cos(a), Math.sin(a), rise + 0.22);
      spawnGrit(x, y, z, v * 1.3, 0.35, 0.45,
        carryX * GRIT_CARRY_REL * 0.8, carryZ * GRIT_CARRY_REL * 0.8,
        si, 0.8 + severity * 0.35, y);
    }
  }
}

// ---------------------------------------------------------------------------

// Make sure someone pulls the shared light block into step with the sky each frame.
// Idempotent by design — every consumer of relief.ts may call it and only the first
// does anything — so this is the cheap way to not depend on another piece having landed.
installReliefSystem();

registerSystem({
  id: 'particles',
  order: 100,

  init(ctx: SystemContext) {
    build();
    if (mesh) ctx.scene.add(mesh);
  },

  fixedUpdate(dt: number, ctx: SystemContext) {
    const b = ctx.bike;
    const ev = ctx.events;

    // The harness resets the bike between scenarios and the player can reset at any
    // time. Without this, every reset drags a comet of live dust across the mountain.
    if (hadPos) {
      const dx = b.x - lastX;
      const dz = b.z - lastZ;
      if (dx * dx + dz * dz > 400) killAll();
    }
    lastX = b.x; lastZ = b.z;
    hadPos = true;

    // --- gusting -----------------------------------------------------------
    //
    // See GUST_DEPTH. Once the light fraction has no per-sprite outline, ALL of the
    // plume's readable structure has to come from its density, and a Poisson stream at a
    // constant rate has none. Two incommensurate sines, mean-corrected so this changes the
    // structure of the plume and not the amount of it — the bike travels 3-8 m per gust
    // period, so what it draws is masses in the plume rather than a flicker.
    simClock += dt;
    const gust = 1 + GUST_DEPTH * (
      0.62 * Math.sin(simClock * GUST_HZ_A) + 0.38 * Math.sin(simClock * GUST_HZ_B + 1.7)
    );

    const wet = clamp01(Weather.wetness);
    const dryMul = 1 - wet * 0.88;
    const fwdX = Math.cos(b.yaw);
    const fwdZ = Math.sin(b.yaw);
    const rgtX = -fwdZ;
    const rgtZ = fwdX;
    // Chassis velocity resolved into the bike's own frame. `vLat` is what makes a drift
    // throw its roost to the outside of the corner instead of straight out the back;
    // `vLong` is half of the tire's shear rate against the ground (see surfLong below).
    const vLat = b.vx * rgtX + b.vz * rgtZ;
    const vLong = b.vx * fwdX + b.vz * fwdZ;
    const speed = b.speed;
    const weightRef = Tuning.bike.mass * G;
    // The wake the bike drags with it. See spawnDust for why this exists.
    const carryX = b.vx * DUST_CARRY;
    const carryZ = b.vz * DUST_CARRY;
    // ...and the heavy fraction's own, which is a tenth of the bike rather than a share of
    // the wake. See GRIT_CARRY: a clod is ballistic and must not be dragged along by a
    // change made to the light fraction's aerodynamics.
    const gCarryX = b.vx * GRIT_CARRY;
    const gCarryZ = b.vz * GRIT_CARRY;

    // --- rear wheel: roost -------------------------------------------------
    const rw = b.rear;
    accSteps++;
    if (rw.contact) {
      const si = surfIndex(rw.ground.surface);
      const dustCoef = SurfaceProps[si].dust;
      accContact++;
      accDust += dustCoef;
      const loadF = clamp01(rw.load / weightRef);
      // Shear, not slip. The gate is what keeps a gripping tire silent.
      // Per SURFACE, not a constant. See roostGate: the rider rides at 0.13-0.58 of the
      // friction circle, so a fixed 0.46 threshold is silent on every surface at riding
      // slip and the effect only ever existed on the rare full lock.
      const gate = roostGate(dustCoef);
      const shear = clamp01((rw.slipUsage - gate) / (1 - gate));
      const spdF = clamp01(speed / 6);
      // Wheelspin throws material too, even standing still — that is a pedal-up on loose.
      const spinExcess = clamp01((Math.abs(rw.spin) * Tuning.bike.wheelRadius - speed) / 6);
      // Ramp, not a square. A square puts nothing on screen until the tire is 80% of the
      // way to fully sliding, which measured as roughly two percent of a cornering run —
      // the emitter was correct and invisible. This still reaches zero at the gate and
      // one at full slide, it just gives the middle of the range something to say.
      //
      // R3 flattened the ramp further after measuring where the roost run actually sits:
      // rear slipUsage is above the gate a third of the time and above 0.85 for under two
      // percent, so a mid-slide is shear ~0.5, and at R2's curvature that returned a
      // drive of 0.36 — a third of the emission for what the rider experiences as the
      // back stepping out. The middle of the range is where this effect lives.
      //
      // ---------------------------------------------------------------------------
      // W5 — the ramp is a 0.65 POWER, and this is the "invisible at riding speed" half
      // ---------------------------------------------------------------------------
      //
      // `shear * (0.6 + 0.4 * shear)` is very nearly linear from the gate, and the gate is
      // at 0.46 because a bike cornering hard and GRIPPING sits at 0.5-0.7 of the friction
      // circle. Put those two facts together and the arithmetic is unflattering: a rider
      // who feels the back step out at slipUsage 0.53 — which is what the roost capture is
      // doing at 15.8 km/h — gets shear 0.13 and therefore drive 0.085. Measured on that
      // frame, the whole plume covered 4.7 % of the picture and lifted it by 0.033 of
      // OKLab L. The emitter was not broken and the lighting was not the reason it could
      // not be seen; it was running at a twelfth of rate because the ramp spends its
      // resolution on the top of a range the rider almost never reaches.
      //
      // A 0.65 power keeps both endpoints exactly — zero at the gate, one at a full slide,
      // so a gripping tyre is still silent and a locked one is still maximal — and gives
      // the middle of the range something to say: shear 0.13 -> 0.26, 0.30 -> 0.45,
      // 0.50 -> 0.64. That is the band this effect lives in and it is the band the rider
      // feels through the bars.
      const drive = clamp01(Math.pow(shear, 0.65) + spinExcess * 0.7);
      const head = headroom();
      // What the FLOOR invariant is gated on. See `roostDemand`: this is "the rear tyre is
      // tearing up a surface that has something to give, at a speed a rider rides at",
      // which is the exact condition BAR's first VFX anchor makes a promise about. It is
      // deliberately NOT the emitter's own rate — a floor that reads the emitter would be
      // satisfied by an emitter that is running and invisible, which is this piece's
      // carried gap in one sentence.
      {
        const demand = drive * dustCoef * dryMul;
        if (demand > roostDemand) { roostDemand = demand; roostDemandSpeed = speed; }
      }

      const gy = rw.cy;
      // Every particle from this wheel this step inherits the hillside the tire is
      // standing on as its floor, and the material it is standing ON. See DUST_TREAD.
      setGroundPlane(rw.ground.nx, rw.ground.ny, rw.ground.nz);
      emitTread = clamp01(rw.ground.trailWeight);

      // How fast the tire is shearing the ground it is standing on, m/s — the velocity of
      // the tire's surface at the contact patch relative to the dirt. This is the sim's
      // own number, the same quantity the tire solver builds slipRatio out of, and it is
      // what physically decides how hard material leaves the patch. R2 threw everything
      // at a speed derived from `min(speed,22) * 0.32` — a function of how fast the bike
      // was going, not of how hard the tire was working — so a locked rear at walking
      // pace and a gripping tire at 60 km/h threw with the same energy.
      const surfLong = vLong - rw.spin * Tuning.bike.wheelRadius;
      const shearSpeed = Math.hypot(surfLong, vLat);
      // Dust rides the wake, so its axis is backward off the patch whatever the tire is
      // doing, fanned to the outside of the corner by however hard the bike is sliding
      // sideways. That fan is what throws a drift's roost across the outside of the turn
      // instead of straight out the back.
      //
      // ---------------------------------------------------------------------------
      // W5 R2 — THE CONE WAS POINTED DOWN THE CAMERA'S OWN VIEW AXIS
      // ---------------------------------------------------------------------------
      //
      // The chase camera sits DIRECTLY BEHIND the bike. A cone whose axis is -forward is
      // therefore a cone aimed at the lens, and a cone seen end-on projects to a disc a
      // few degrees wide around the vanishing point however much material is in it. That
      // is the shape the carried gap describes literally: "a 58x305 px bounding box on a
      // 1600x900 frame" is a pencil, not a fan. Measured this round with every particle
      // projected through a level chase rig (`captures/w5-vfx-r2b/plume-base`), the whole
      // in-frame light fraction sits inside a narrow vertical strip over the contact
      // patch, which itself projects to y = 869-884 of 900 — the bottom 3 % of the frame.
      //
      // Two changes, and both are about what the material physically does rather than
      // about where the camera is:
      //
      //  - the axis now follows the tyre's OWN SHEAR DIRECTION (the same quantity the
      //    heavy fraction has used since W4 R3) blended with the wake. A locked rear
      //    ploughs and throws material forward and outward past the rider; a stepped-out
      //    rear throws it across the outside of the turn. Both of those are ACROSS the
      //    frame. Only a clean spinning wheel throws straight back down the view axis, and
      //    that is the one case where a roost genuinely is a small plume behind the tyre.
      //  - the lateral fan is no longer proportional to vLat alone. `vLat` is 0.3-1.5 m/s
      //    at the slip a rider rides at, so `vLat * 0.30` was a 5-25 % bend on an axis
      //    pointed at the lens. It now carries the shear direction's own lateral share.
      const shSpd = Math.max(shearSpeed, 0.25);
      const shX = (fwdX * surfLong + rgtX * vLat) / shSpd;
      const shZ = (fwdZ * surfLong + rgtZ * vLat) / shSpd;
      const fan = clamp(vLat * 0.55, -1.6, 1.6);
      const dustHX = -fwdX * (1 - DUST_SHEAR_AXIS) + shX * DUST_SHEAR_AXIS + rgtX * fan;
      const dustHZ = -fwdZ * (1 - DUST_SHEAR_AXIS) + shZ * DUST_SHEAR_AXIS + rgtZ * fan;
      // Grit is heavy enough to ignore the wake and simply go where it was kicked, so it
      // follows the true shear direction. A wheelspin fires it backward; a locked wheel
      // ploughs and throws it forward and up over the tire. Those look different, and
      // they should — it is the same distinction the rider feels in the bars.
      const gritHX = fwdX * surfLong + rgtX * vLat;
      const gritHZ = fwdZ * surfLong + rgtZ * vLat;
      // Throw speed. A floor, because even a barely-shearing tire flicks material, and a
      // ceiling so a wheelspin against a stopped bike does not fire dust to the horizon.
      const throwV = Math.min(2.0 + shearSpeed * 0.95, 12);

      if (drive > 0.001 && dustCoef * dryMul > 0.02) {
        // Speed enters the LIGHT fraction differently from the heavy one, and it has to.
        // What the light fraction is, is the finest part of the material the patch tears
        // loose per second, and that mass flux goes with how much ground the tyre crosses
        // — so it should keep climbing to riding speed rather than saturating at 6 m/s
        // with a 0.25 floor under it. It saturates at riding speed (13 m/s) rather than
        // at 6, so the fastest slide in the game is emitted at the full rate and the cut
        // falls entirely on the slow end. The 1.5 power is the same reasoning taken
        // seriously: the FINE fraction is the abrasion product, and abrasion energy per
        // metre goes with contact speed, so the mass of respirable dust liberated per
        // second is superlinear in it. Measured on `roost`, which is the slow scenario
        // (4.5-16 km/h): the old curve gave a walking-pace bike 41 % of the full rate and
        // a 16 km/h one 80 %, which is how a nearly-stopped bike came to stand a wall of
        // dust up in front of a lens 2.4 m away. `spdF` itself is left alone because the
        // heavy fraction reads from it too and a clod IS thrown at low speed.
        //
        // W5 R2: saturates at 8 m/s, not 13, and the knee moved for the same reason the
        // gate did — the number was set against a speed the game is not ridden at. 13 m/s
        // is 47 km/h; measured on `roostride` and `fullrun` this wave the bike is doing
        // 9-30 km/h (2.5-8.3 m/s), so the curve was spending its whole range above the
        // band and returning 0.30-0.52 across all of normal riding. The floor and the
        // shape of the slow end are UNCHANGED in effect: at the 2.5 km/h a stalled `roost`
        // frame sits at, this returns 0.06 against the old curve's 0.11, so the artefact
        // it was introduced for (a nearly-stopped bike hazing a lens 2.4 m away) is bounded
        // harder, not relaxed. What changes is that riding speed now gets the full rate.
        const sf = clamp01((speed - 1.5) / 6.5);
        const dustSpd = 0.04 + 0.96 * sf * Math.sqrt(sf);
        accRoostDust += ROOST_DUST_RATE * dustCoef * dryMul * drive * dustSpd
          * (0.35 + loadF) * head * gust * dt;
        let n = 0;
        while (accRoostDust >= 1 && n < MAX_SPAWN_PER_STEP) {
          accRoostDust -= 1; n++; accEmitRoost++;
          // W4 R3: the light fraction now leaves on its own axis and at its own speed.
          // ROOST_RISE_DUST stands it up into a frame whose bottom edge is at the contact
          // patch, and DUST_THROW_FRAC stops it being fired at the lens at the tyre's full
          // shear rate — which is a velocity only the ballistic fraction can hold.
          setCone(dustHX, dustHZ, ROOST_RISE_DUST);
          spawnDust(rw.cx, gy, rw.cz, throwV * DUST_THROW_FRAC,
            CONE_SPREAD_H, CONE_SPREAD_V,
            // Opacity up with the count down — see ROOST_DUST_RATE. Optical depth is
            // fill times alpha, so trading one for the other keeps the plume and pays a
            // fraction of the fragments.
            // W5 R3: 0.82 + 0.18*drive -> 0.34 + 0.16*drive. A sprite at 0.82 authored
            // alpha is not an element of a medium, it is a decal, and five to ten of them
            // stack at every pixel of the near field: measured through the inversion on
            // `hucks` and `roostride`, the plume reaches an ACCUMULATED OPACITY OF 1.000
            // over 5-17 % of the frame. That is not a roost you can see the rider through,
            // it is a plate, and it is why every frame this piece produces reads as steam
            // rather than as dirt in the air. Optical depth is count times alpha and the
            // count is untouched, so what changes is that the plume is now translucent —
            // which is the property that lets its own lit face and shadowed face BOTH be
            // visible instead of compositing to one saturated white.
            carryX, carryZ, si, 1.0 + drive * 0.35, 0.34 + 0.16 * drive, gy);
        }
        if (accRoostDust > MAX_SPAWN_PER_STEP) accRoostDust = MAX_SPAWN_PER_STEP;
      } else {
        accRoostDust = 0;
      }

      if (drive > 0.001) {
        accRoostGrit += ROOST_GRIT_RATE * drive * (0.2 + spdF * 0.8) * (0.35 + loadF)
          * (0.35 + dustCoef) * head * gust * dt;
        let n = 0;
        while (accRoostGrit >= 1 && n < MAX_SPAWN_PER_STEP) {
          accRoostGrit -= 1; n++; accEmitGrit++;
          // Tighter cone than the dust: clods leave on a line and the line is the
          // readable thing about them. *Not* thrown harder, though — the first pass of
          // R3 first had them at 1.35x the dust speed on a 44 degree cone, which is a
          // 14 m/s vertical launch and a ten-metre apex, and the screenshot was clods
          // hanging in the sky above the treeline; backing that all the way off to 0.85x
          // buried them in the plume instead. Dirt off a rear tire clears a rider's head
          // and comes back down, which is what this pair now describes.
          // W4: the extra rise went from +0.30 to +0.08. At +0.30 the cone axis is 46
          // degrees up and, with the speed distribution's long tail, most of a hard
          // roost's grit was launched into the ballistic cap — i.e. straight into the
          // sky, which is where the measured blobs were.
          setCone(gritHX, gritHZ, ROOST_RISE + 0.08);
          spawnGrit(rw.cx, gy, rw.cz, throwV * 1.15, 0.30, 0.3,
            gCarryX, gCarryZ, si, 1, gy);
        }
        if (accRoostGrit > MAX_SPAWN_PER_STEP) accRoostGrit = MAX_SPAWN_PER_STEP;
      } else {
        accRoostGrit = 0;
      }

      // Rock chips: the surface that makes no dust still makes debris, and this is the
      // only place it comes from.
      if (rw.ground.surface === Surface.Rock && shear > 0.02) {
        accRockGrit += ROCK_GRIT_RATE * shear * spdF * loadF * head * dt;
        let n = 0;
        while (accRockGrit >= 1 && n < MAX_SPAWN_PER_STEP) {
          accRockGrit -= 1; n++; accEmitGrit++;
          // Chips ricochet — a much wider cone than dirt, which is thrown.
          setCone(gritHX, gritHZ, ROOST_RISE + 0.35);
          spawnGrit(rw.cx, gy, rw.cz, 3.0 + shearSpeed * 0.6, 0.7, 0.7,
            gCarryX, gCarryZ, si, 0.8, gy);
        }
        if (accRockGrit > MAX_SPAWN_PER_STEP) accRockGrit = MAX_SPAWN_PER_STEP;
      } else {
        accRockGrit = 0;
      }

      // Rolling dust — no slip needed. A 50 km/h tire on loose ground lifts a low plume
      // whether or not it is sliding, and its absence is why a straight-line run at speed
      // otherwise looks like the bike is on rails. Low, slow and hugging the ground: this
      // is a veil off the tread, not a roost, and if it stands up it competes with one.
      //
      // W5 R2: the knee is at 2.5 m/s and the curve is rollF^1.35, not rollF^2 off 3.5.
      // Same arithmetic failure as the roost gate and the dust speed curve, in the third
      // place it could hide: at the 20 km/h `roostride` measures, the old form returned
      // (5.5-3.5)/9 = 0.22 squared = 0.049, i.e. FIVE particles a second — a veil that
      // exists in the source and nowhere else. The knee has to sit under riding speed for
      // the term to mean anything, and a 1.35 power keeps the low end quiet without
      // spending the whole range above the band the bike is ridden in.
      const rollF = clamp01((speed - 2.5) / 7.5);
      if (rollF > 0 && dustCoef * dryMul > 0.02) {
        accRollDust += ROLL_DUST_RATE * dustCoef * dryMul * rollF * Math.pow(rollF, 0.35)
          * (0.3 + loadF) * head * gust * dt;
        let n = 0;
        while (accRollDust >= 1 && n < MAX_SPAWN_PER_STEP) {
          accRollDust -= 1; n++; accEmitRoll++;
          // W4 R3: rise 0.22 -> 0.52. The measured contact patch projects to the bottom
          // edge of the frame, so a veil thrown flat off the tread is drawn UNDER the
          // picture and costs fill for nothing. Half of what makes a fast straight line
          // read as fast is this veil standing knee-high around the rear wheel.
          setCone(-fwdX, -fwdZ, 0.52);
          // Same cut as the roost's, and for the same measurement: the veil is a low
          // haze off the tread and it was being drawn at two thirds of a decal's opacity.
          spawnDust(rw.cx, gy, rw.cz, 1.0 + rollF * 2.2, 0.85, 0.30,
            carryX, carryZ, si, 0.92, 0.22 + 0.14 * rollF, gy);
        }
        if (accRollDust > MAX_SPAWN_PER_STEP) accRollDust = MAX_SPAWN_PER_STEP;
      } else {
        accRollDust = 0;
      }

      // Wet: no dust, a rooster tail instead.
      if (wet > 0.12) {
        const sprayF = clamp01((speed - 2.5) / 9);
        accSpray += SPRAY_RATE * wet * sprayF * (0.3 + loadF) * head * dt;
        let n = 0;
        while (accSpray >= 1 && n < MAX_SPAWN_PER_STEP) {
          accSpray -= 1; n++;
          // A rooster tail is the narrowest cone on the bike — water leaves a wet tread
          // in a sheet, which is why it reads as a fin standing up behind the wheel.
          setCone(-fwdX, -fwdZ, 0.85);
          // gCarry, not the dust wake: this keeps the rooster tail's entrainment at
          // exactly the 0.10 of bike velocity it had before W4 R3 raised DUST_CARRY. No
          // wet scenario rides hard enough to grade a change to spray honestly, so it
          // must not move as a side effect of a dry-roost fix.
          spawnSpray(rw.cx, gy, rw.cz, 2.0 + speed * 0.5, 0.22, 0.3,
            gCarryX, gCarryZ, gy);
        }
        if (accSpray > MAX_SPAWN_PER_STEP) accSpray = MAX_SPAWN_PER_STEP;
      } else {
        accSpray = 0;
      }
    } else {
      accRoostDust = 0; accRoostGrit = 0; accRollDust = 0; accRockGrit = 0; accSpray = 0;
    }

    // --- front wheel: only when it is letting go ---------------------------
    const fw = b.front;
    if (fw.contact) {
      const si = surfIndex(fw.ground.surface);
      const dustCoef = SurfaceProps[si].dust;
      const loadF = clamp01(fw.load / weightRef);
      const fGate = roostGate(dustCoef);
      const shear = clamp01((fw.slipUsage - fGate) / (1 - fGate));
      if (shear > 0.001 && dustCoef * dryMul > 0.02 && speed > 2) {
        accFrontDust += ROOST_DUST_RATE * FRONT_FRACTION * dustCoef * dryMul
          * shear * (0.45 + 0.55 * shear) * clamp01(speed / 6) * (0.35 + loadF)
          * headroom() * dt;
        let n = 0;
        // The front tire washing out pushes material sideways ahead of the bike, so its
        // plume leans across the corner rather than trailing back. Kept deliberately low
        // and thin: it is a cue that the front is letting go, and if it stands up it is
        // in front of the rider and in the way of the shot.
        const fFan = clamp(vLat * 0.5, -1.5, 1.5);
        setGroundPlane(fw.ground.nx, fw.ground.ny, fw.ground.nz);
        emitTread = clamp01(fw.ground.trailWeight);
        while (accFrontDust >= 1 && n < MAX_SPAWN_PER_STEP) {
          accFrontDust -= 1; n++;
          const gy = fw.cy;
          setCone(-fwdX + rgtX * fFan, -fwdZ + rgtZ * fFan, 0.3);
          spawnDust(fw.cx, gy, fw.cz, 1.4 + shear * 3.5, 0.45, 0.3,
            carryX, carryZ, si, 0.8, 0.28 + 0.18 * shear, gy);
        }
        if (accFrontDust > MAX_SPAWN_PER_STEP) accFrontDust = MAX_SPAWN_PER_STEP;
      } else {
        accFrontDust = 0;
      }
    } else {
      accFrontDust = 0;
    }

    // --- discrete events ---------------------------------------------------
    // main.ts clears these once per rendered frame, and four sim steps run per frame at
    // 60 Hz, so an edge flag is needed or every landing fires four bursts.
    if (ev.landed >= 0) {
      if (!landedConsumed) {
        landedConsumed = true;
        const sev = clamp01(ev.landed);
        if (sev > 0.02) {
          if (b.rear.contact) {
            const si = surfIndex(b.rear.ground.surface);
            burst(b.rear.cx, b.rear.cy, b.rear.cz, sev, si, SurfaceProps[si].dust, dryMul,
              carryX, carryZ,
              b.rear.ground.nx, b.rear.ground.ny, b.rear.ground.nz,
              b.rear.ground.trailWeight);
          }
          if (b.front.contact) {
            const si = surfIndex(b.front.ground.surface);
            burst(b.front.cx, b.front.cy, b.front.cz, sev * 0.75,
              si, SurfaceProps[si].dust, dryMul, carryX, carryZ,
              b.front.ground.nx, b.front.ground.ny, b.front.ground.nz,
              b.front.ground.trailWeight);
          }
        }
      }
    } else {
      landedConsumed = false;
    }

    if (ev.bottomOut > 0.5) {
      if (!bottomConsumed) {
        bottomConsumed = true;
        const w = b.rear.load > b.front.load ? b.rear : b.front;
        if (w.contact) {
          const si = surfIndex(w.ground.surface);
          burst(w.cx, w.cy, w.cz, 0.45 * ev.bottomOut, si, SurfaceProps[si].dust, dryMul,
            carryX, carryZ, w.ground.nx, w.ground.ny, w.ground.nz, w.ground.trailWeight);
        }
      }
    } else {
      bottomConsumed = false;
    }

    if (ev.hopped && b.rear.contact) {
      const si = surfIndex(b.rear.ground.surface);
      burst(b.rear.cx, b.rear.cy, b.rear.cz, 0.22, si, SurfaceProps[si].dust, dryMul,
        carryX, carryZ, b.rear.ground.nx, b.rear.ground.ny, b.rear.ground.nz,
        b.rear.ground.trailWeight);
    }

    if (ev.crashed) {
      const w = b.rear.contact ? b.rear : b.front;
      const si = surfIndex(w.ground.surface);
      burst(w.cx, w.cy, w.cz, 0.85, si, SurfaceProps[si].dust, dryMul, carryX, carryZ,
        w.ground.nx, w.ground.ny, w.ground.nz, w.ground.trailWeight);
    }
  },

  update(dt: number, ctx: SystemContext) {
    if (!geo || !mat || !attrPos || !attrCol || !attrShape || !attrKind || !attrVel
      || !attrForm || !mesh) return;

    // Wind, once per frame rather than per particle.
    const wspd = Weather.windSpeed;
    const wX = Weather.windX * wspd * 0.42;
    const wZ = Weather.windZ * wspd * 0.42;

    const step = Math.min(dt, 0.05);
    const camX = ctx.camera.position.x;
    const camY = ctx.camera.position.y;
    const camZ = ctx.camera.position.z;
    // Same quantity the vertex shader bounds, computed here only so a capture can report
    // it. Distance stands in for view-z, which is exact on the view axis and slightly
    // conservative off it — the right direction for a diagnostic.
    const invHalfH = 1 / Math.tan((ctx.camera.fov * Math.PI) / 360);
    const camAspect = ctx.camera.aspect || (16 / 9);

    // --- the camera's own basis, so a sprite can be asked whether it is IN THE PICTURE --
    //
    // Every number this file has ever put on the audit bus was computed from a sprite's
    // DISTANCE to the camera and nothing else, and the `stats` block already records what
    // that cost: on `roostride` at 8.6 s, 153 of 161 drawn sprites were BEHIND the lens
    // and `vfx.overdraw` reported the same 0.031 of a frame it would have reported if all
    // 161 had been. A metric that cannot tell in front from behind cannot answer "is the
    // roost on screen", which is the question this piece has been sent back on four times.
    //
    // The camera's world matrix is a rigid transform, so its inverse is the transpose of
    // the basis: view depth is the component along -back, and the two lateral components
    // divided by depth and by the projection's own terms are exactly NDC. Six dot products
    // per particle, no allocation, no matrix object, and it tracks a chase camera that
    // swings its FOV from 62 to 88 degrees with nothing to keep in sync.
    ctx.camera.updateMatrixWorld();
    const cme = ctx.camera.matrixWorld.elements;
    // Columns of the rotation: right, up, back. The camera looks down -back.
    const rgX = cme[0], rgY = cme[1], rgZ = cme[2];
    const upXc = cme[4], upYc = cme[5], upZc = cme[6];
    const bkX = cme[8], bkY = cme[9], bkZ = cme[10];
    const pme = ctx.camera.projectionMatrix.elements;
    // 1/tan(fovY/2) and 1/(aspect*tan(fovY/2)) for a standard perspective matrix. Read out
    // of the matrix rather than rebuilt from fov/aspect so an off-centre or jittered
    // projection (post.ts owns one) cannot make this disagree with what is drawn.
    const projY = pme[5] || invHalfH;
    const projX = pme[0] || (invHalfH / camAspect);

    // Camera velocity, for the heavy fraction's object blur. Smoothed over ~50 ms: the
    // chase rig's own position is springy and a single hitchy frame would otherwise
    // stretch every clod on screen for one frame, which reads as a flash.
    if (haveCamPrev && step > 1e-4) {
      const k = 1 - Math.exp(-step / 0.05);
      camVX += ((camX - camPX) / step - camVX) * k;
      camVY += ((camY - camPY) / step - camVY) * k;
      camVZ += ((camZ - camPZ) / step - camVZ) * k;
    }
    camPX = camX; camPY = camY; camPZ = camZ;
    haveCamPrev = true;

    // The terrain horizon, once a frame. See HORIZON_BINS: "above the lens" is not "against
    // the sky" on a mountain, and the invariant that said it was reported half the plume as
    // a column on forest frames with no sky in them at all.
    if (live > 0) buildHorizon(ctx.terrain, camX, camY, camZ);

    // The sun, read out of relief.ts's shared block rather than tracked here — one Float32
    // Array is the whole game's answer for where the light comes from. [0..2] is the unit
    // world direction TOWARD the sun. See PLUME_SUN_MIX.
    const rlf = reliefUniforms().uRlf.value as Float32Array;
    const sunHx = rlf[0];
    const sunHy = rlf[1];
    const sunHz = rlf[2];
    const sunHL = Math.hypot(sunHx, sunHz);
    const sunUX = sunHL > 1e-3 ? sunHx / sunHL : 0;
    const sunUZ = sunHL > 1e-3 ? sunHz / sunHL : 1;
    // All height at a zenith sun, mostly sideways at a raking one. `sunHy` is the sine of
    // the sun's elevation, so this is 0 above about 46 degrees and full at the horizon.
    const sunMix = PLUME_SUN_MIX * clamp01((1 - sunHy) * 1.4);
    const cX = plumeHaveC ? plumeCX : ctx.bike.x;
    const cZ = plumeHaveC ? plumeCZ : ctx.bike.z;
    let sumCX = 0;
    let sumCZ = 0;
    let sumCW = 0;

    stats.sizeMax = 0;
    stats.covMax = 0;
    stats.nearest = 1e9;
    covHist.fill(0);
    // Optical weight above / everywhere, for the sky-column invariant, plus raw drawn
    // area for the fill one. Both are folded from quantities the loop already computes.
    let skyWt = 0;
    let allWt = 0;
    let areaSum = 0;
    // ...and the same two restricted to sprites whose quad overlaps the frame. See the
    // camera basis above.
    let onArea = 0;
    let onWt = 0;
    let onCount = 0;

    let i = 0;
    let n = 0;
    while (i < live) {
      const a = age[i] + step;
      if (a >= life[i]) {
        removeAt(i);
        continue;
      }
      age[i] = a;

      const k = dragK[i];
      const wk = windK[i];
      // Relax toward the local air rather than integrating a drag force: this is the
      // same first-order form the sim uses for damping and it is unconditionally stable
      // at any frame time the browser hands us.
      const ax = (wX * wk - vx[i]) * k;
      const ay = (0 - vy[i]) * k + buoyA[i];
      const az = (wZ * wk - vz[i]) * k;
      let nvx = vx[i] + ax * step;
      let nvy = vy[i] + ay * step;
      let nvz = vz[i] + az * step;

      const nx = px[i] + nvx * step;
      let ny = py[i] + nvy * step;
      const nz = pz[i] + nvz * step;

      const t = a * invLife[i];
      // Bounded — see DUST_SIZE_MAX, which R2 took from 1.15 m to 0.60 and which is the
      // single number that put the light fraction back on screen. The oldest puffs are the
      // ones nearest the camera in a chase shot, so growth and the two screen-coverage
      // bounds fight each other directly, and at 1.15 m the bounds were winning by a
      // factor of twenty-five.
      const size = Math.min(size0[i] + sizeK[i] * Math.sqrt(a), DUST_SIZE_MAX);

      // The floor is the tangent plane of the ground where the particle was born, not a
      // single height — see the gradX/gradZ declaration for why that distinction is worth
      // two floats. Held a fraction of the sprite's own radius clear of it, because what
      // has to stay out of the hill is the *billboard*, and a puff whose centre is exactly
      // on the surface has half its quad inside it.
      const fl = floorY[i] + gradX[i] * nx + gradZ[i] * nz + size * 0.15;
      if (ny < fl) {
        ny = fl;
        if (nvy < 0) {
          // Grit skitters; dust lies down and crawls.
          nvy = kindOf[i] === KIND_GRIT ? -nvy * 0.22 : 0;
          nvx *= 0.72; nvz *= 0.72;
        }
      }

      px[i] = nx; py[i] = ny; pz[i] = nz;
      vx[i] = nvx; vy[i] = nvy; vz[i] = nvz;
      spin[i] += spinRate[i] * step;

      // Fast in, long tail out. The tail is what "hangs" means — for the light fraction.
      // The heavy fraction holds full opacity until it is nearly done; see KIND_HOLD.
      //
      // R4 changed the light fraction's curve from out^2*(0.6+0.4*out) to out^1.5, and
      // that is the single biggest reason the plume is now visible. Measured on
      // `dustcam` at 57 km/h with the old curve: 169 sprites on screen and the whole
      // plume moved the frame by 3-7 values out of 255 — present in the buffer, absent
      // from the image. The old curve puts a puff at 20% of its opacity halfway through
      // its own life, so a five-second lifetime spent four of those seconds contributing
      // nothing but fill cost. out^1.5 is 35% at half life and 9% at four fifths: the
      // tail is still long and still smooth, it just has something in it.
      const fadeIn = t < 0.09 ? t * 11.11 : 1;
      const out = 1 - t;
      const hold = holdK[i];
      const fadeOut = hold > 1.5
        ? Math.min(1, out * hold)
        : out * Math.sqrt(out);

      // Near fade. A chase camera flies straight through its own dust, and a metre-wide
      // billboard whose centre is a metre from the eye covers a third of the screen —
      // which is not a plume, it is a smear over the lens, and it is the single most
      // damaging thing a particle system can do to a riding game. Fading out over the
      // camera's own near field costs one square root per particle and removes it
      // entirely, without the depth-buffer read a true soft particle would need.
      const dcx = nx - camX;
      const dcy = ny - camY;
      const dcz = nz - camZ;
      const camD = Math.sqrt(dcx * dcx + dcy * dcy + dcz * dcz);
      // Elevation of the top of this billboard, RISE OVER HORIZONTAL RUN. See the sky fade
      // below, and note the run: the horizon table is built by marching the heightfield out
      // along the ground, so its elevations are rise over horizontal distance, and the two
      // have to be in the same units or a puff at 45 degrees is compared with a ridge at
      // 30. One extra square root per drawn particle, on a loop that already takes one.
      const camHD = Math.sqrt(dcx * dcx + dcz * dcz);
      const elevTop = (ny + size - camY) / Math.max(camHD, 0.3);
      const near = clamp01((camD - size * 0.9 - 0.35) / 1.6);
      // Cubed, not squared, since W4 R3, and the exponent is chosen because of what it
      // does NOT touch. The plume's working band at this camera is 1.8-3.5 m — behind the
      // rider, where a puff is a legible mass — and `near` is already 1.0 across all of
      // it, so raising the power cannot cost the effect anything there. What it does cost
      // is the 1.0-1.7 m band, where a 0.25 m puff subtends 250 px of radius and paints
      // the SKY: measured on `roost`, 92 % of the in-frame plume's optical weight was
      // drawn above the horizon line and took the sky down by 11 code values on average
      // and 83 at worst. Squared left that band at 0.14 of authored alpha; cubed leaves it
      // at 0.05.
      const near3 = near * near * near;

      // Thin the fraction that has settled onto the ground.
      //
      // "Dust hangs" is a requirement about *airborne* dust. What lands does not hang, it
      // is dirt again — and because the pool holds five seconds of emission, the landed
      // fraction otherwise piles into a flat pale layer over the hillside behind the
      // rider that has no form of its own and greys out the ground it is lying on. One
      // subtract, one clamp: a puff at ground level keeps a sixth of its opacity and one
      // 30 cm clear of it keeps all of it, so the plume is untouched and the sediment
      // stops competing with it.
      //
      // R2 took the floor from 0.34 to 0.15. The settled fraction is pinned to a single
      // analytic PLANE — that is what `floorY` is — so however much its birth height is
      // jittered it still stacks into a sheet, and a sheet seen edge-on from a chase
      // camera is a straight line. That line is the "flat structureless tan wash with a
      // straight geometric leading edge drawn over the rider" the critic measured in
      // `roost_16_all.png`. Thinning what lies on the plane is the direct fix; the plume
      // above it does not notice.
      const lift = clamp01((ny - fl) * 3.2 + 0.15);

      // ...and thin the fraction that has climbed out of the picture.
      //
      // The counterpart of `lift`, and it removes an artefact this round found in
      // `captures/w3-vfx-r3-dustcam/dustcam_7p8s.png`: a dozen dark cauliflower blobs
      // hanging in the SKY above the treeline. They are dust. Buoyancy is 0.45 m/s^2 and
      // the light fraction lives up to 5.2 s, so a puff that leaves the tyre with any
      // upward throw at all ends its life seven or eight metres over the ground it came
      // off — and seen from a chase camera below it, against a bright sky, the underside
      // of a sprite sits at the ground-bounce radiance and reads as a dark hole punched in
      // the sky. docs/BAR.md names floating objects as the single most damaging artefact
      // under this direction, and a puff of dirt at rooftop height is exactly one.
      //
      // A ceiling is also the truthful fix rather than a cosmetic one: dust does not stay
      // a puff on the way up, it disperses, and the entrainment that carries it that high
      // has already diluted it past visibility. Measured against its own floor plane so it
      // means "above the ground it came off", not an altitude on the mountain — the same
      // reason `floorY` is a plane.
      //
      // W4 R3: per kind. The heavy fraction gets a far tighter one (see GRIT_CEIL0) —
      // "an object hanging in the air" is a statement about a compact hard-edged sprite,
      // not about a soft medium, and the six measured offenders in this round's baseline
      // diff were all kind 1 at 1.2-2.4 m over their own ground. One compare per particle,
      // no new array, and it is a bound rather than a tuning: nothing in the heavy
      // fraction can be drawn at full alpha above head height whatever the emitter does.
      const isGrit = kindOf[i] === KIND_GRIT;
      const c0 = isGrit ? GRIT_CEIL0 : PLUME_CEIL0;
      const c1 = isGrit ? GRIT_CEIL1 : PLUME_CEIL1;
      const ceil = 1 - clamp01(((ny - fl) - c0) / (c1 - c0));

      // ...and the horizon. See SKY_FADE0: the ceiling above measures height over the
      // particle's own birth plane, which on a ridge crest is flat while the hillside
      // drops away, so it cannot see a puff that is against the sky because the GROUND
      // went down. A point above the camera's altitude is above the horizon line in the
      // image; that is the whole test, and camY is already to hand.
      // Measured against the sprite's TOP EDGE, not its centre, and that turns out to be
      // exact rather than approximate: the angular elevation of the top of a billboard is
      // ((y - camY) + radius) / depth, so `(y + size) > camY` is "this quad reaches above
      // the horizon line" at ANY distance, with the depth divided out. It has to be the
      // top edge, because the sprites that paint the sky here are not high ones — they are
      // near ones. Measured on `roost` with the centre test: 92 % of the in-frame plume's
      // optical weight was still being drawn above the horizon while almost every centre
      // sat below it, because a 0.3 m puff 1.3 m off the lens subtends 250 px of radius.
      // ...in RADIANS of elevation, which is the form the artefact is actually in. A
      // metre of height above the camera is 40 degrees of sky at 1.3 m and 8 at 7 m, so a
      // fade written in metres either lets the near field through or deletes the far
      // field; measured on `roost` at 1.3 m, the metric version left 81 % of the plume's
      // weight above the horizon at skyF = 0.96. This is the elevation of the sprite's own
      // TOP EDGE: ((y - camY) + radius) / depth is exact for a billboard, the depth
      // divides out, and the quantity it returns is literally "how far above the horizon
      // line does this thing reach".
      const s0 = isGrit ? GRIT_SKY_FADE0 : SKY_FADE0;
      const s1 = isGrit ? GRIT_SKY_FADE1 : SKY_FADE1;
      // ...AND clear of the ground it came off. See SKY_MIN_H: the elevation term alone
      // classifies a hub-height lens as a column and can only be satisfied by deleting the
      // near-deck plume, which is how this piece arrived at an invisible roost.
      const skyH = clamp01(((ny - fl) - SKY_MIN_H) / (SKY_FULL_H - SKY_MIN_H));
      // ...AND above the TERRAIN horizon in this sprite's own direction, not merely above
      // the lens. See HORIZON_BINS for the frames that settled this: on `fullrun` at t=90
      // the pixels say 0.000 of the plume is over sky and there is no sky in the picture,
      // and the old test said 0.530. `elevTop` is rise-over-run and so is the table, so the
      // compare is one subtract.
      const hz = horizonAt(dcx, dcz);
      const overHz = clamp01((elevTop - hz) / HORIZON_BAND);
      const skyF = 1 - skyH * overHz * clamp01((elevTop - s0) / (s1 - s0));

      // Clamped, and it is a correctness bound rather than a look. The blend is
      // src-alpha / one-minus-src-alpha, so a fragment alpha above 1 makes the destination
      // term NEGATIVE and the sprite subtracts from what is behind it — a dust puff that
      // punches a dark hole in the hillside, which is the exact artefact this file has
      // been sent back for three times. Every factor below is already <= 1; the only way
      // to exceed it is an authored opacity over 1 at a call site, and nothing should be
      // able to introduce that by editing one number.
      const alpha = clamp01(alpha0[i] * fadeIn * fadeOut * near3 * lift * ceil * skyF);
      curAlpha[i] = alpha; curSize[i] = size; curH[i] = ny - fl;

      // Faded past seeing: keep simulating it, but do not hand it to the GPU. The tail of
      // a 4 s lifetime is most of the pool and almost none of the image, and a
      // metre-wide billboard at two percent opacity still costs its full fill. Culling on
      // alpha rather than on age is what lets the lifetime stay long — which is the whole
      // point of the piece — without paying for it.
      // W4 R3: 0.006 -> 0.011. The round that put the plume in frame also multiplied the
      // FILL it costs — a drawn sprite pays its whole area whatever its alpha, and the
      // in-frame set went from 42 sprites of 99 px radius to 266 of 152. A puff at 1 % of
      // opacity behind two hundred others is not visible under any circumstances and it is
      // the cheapest thing in the file to stop drawing. Measured on `dustcam` at 45 km/h:
      // it removes roughly a sixth of the drawn instances and nothing detectable from the
      // image.
      if (alpha >= 0.011) {
        const p3 = n * 3;
        iPosArr[p3] = nx; iPosArr[p3 + 1] = ny; iPosArr[p3 + 2] = nz;
        const c4 = n * 4;
        iColArr[c4] = colR[i]; iColArr[c4 + 1] = colG[i];
        iColArr[c4 + 2] = colB[i]; iColArr[c4 + 3] = alpha;
        iShapeArr[c4] = size;
        iShapeArr[c4 + 1] = spin[i];
        iShapeArr[c4 + 2] = edgeP[i];
        iShapeArr[c4 + 3] = seed[i];
        iKindArr[n] = kindOf[i];
        // Where in the plume this parcel is, ALONG THE AXIS THE LIGHT COMES FROM. The
        // height half is the same `ny - fl` the ceiling and the settled-dust term already
        // use, so the gradient, the fade and the ceiling all measure from one surface and
        // cannot disagree; the sunward half is measured from the plume's own centroid so it
        // is mean-neutral. See PLUME_SUN_MIX — at a zenith sun this is byte-for-byte the
        // height expression it replaced.
        const fH = clamp01((ny - fl) / PLUME_FORM_H);
        if (sunMix > 0) {
          const u = (nx - cX) * sunUX + (nz - cZ) * sunUZ;
          const fS = clamp01(0.5 + (0.5 * u) / PLUME_SUN_REACH);
          iFormArr[n] = fH + (fS - fH) * sunMix;
        } else {
          iFormArr[n] = fH;
        }
        if (!isGrit) { sumCX += nx * alpha; sumCZ += nz * alpha; sumCW += alpha; }
        // Relative to the camera, because that is the motion a shutter integrates and
        // because post's own blur cannot see this pass at all (it writes no depth).
        iVelArr[p3] = nvx - camVX;
        iVelArr[p3 + 1] = nvy - camVY;
        iVelArr[p3 + 2] = nvz - camVZ;
        n++;

        // Clamped by the same per-kind ceiling the vertex shader clamps the drawn radius
        // with, or this diagnostic would report a coverage no sprite is ever drawn at.
        const cov = Math.min(
          (size * invHalfH) / Math.max(camD, 0.05),
          kindOf[i] === KIND_GRIT ? GRIT_COVER : COVER_FADE1,
        );
        if (size > stats.sizeMax) stats.sizeMax = size;
        if (cov > stats.covMax) stats.covMax = cov;
        if (camD < stats.nearest) stats.nearest = camD;
        covHist[Math.min(63, (cov * 64) | 0)]++;
        // Screen area in units of the frame's own area. `cov` is the radius as a fraction
        // of the half-height, so pi*cov^2 is in half-height^2 and the aspect converts it:
        // one half-height^2 is (H/2)^2 px against a W*H frame, i.e. H/(4W) of it.
        const area = Math.PI * cov * cov * (0.25 / camAspect);
        areaSum += area;
        const w = alpha * area;
        allWt += w;
        // A sprite counts as sky-drawn if its QUAD reaches above the TERRAIN HORIZON in its
        // own direction — not merely above the lens, which is what this measured for two
        // rounds and which is false on every descending frame (see HORIZON_BINS: the pixel
        // probe found 0.000 of the plume over sky on `fullrun` frames this reported at
        // 0.46-0.53, three of them with no sky in the picture at all).
        //
        // The quad, not the centre: a 0.25 m puff 1.3 m off the lens subtends a quarter of
        // the frame and it is that quad that paints the sky. And it must be clear of the
        // ground it came off, or a lens at hub height classifies the whole near-deck veil
        // as a column. The BUDGET is unchanged at 0.30; what changed is what counts.
        if (overHz > 0 && skyH > 0) skyWt += w * skyH * overHz;

        // --- and is it IN THE PICTURE ----------------------------------------------
        //
        // Quad overlap, not centre-in-frame: a near sprite whose centre is under the
        // bottom edge still paints the lower third, and the emitter sits at y = 869-884 on
        // a 900 px frame, so centre-in-frame would discard most of the plume this measures.
        const dvx = nx - camX, dvy = ny - camY, dvz = nz - camZ;
        const depth = -(dvx * bkX + dvy * bkY + dvz * bkZ);
        if (depth > 0.02) {
          const ex = dvx * rgX + dvy * rgY + dvz * rgZ;
          const ey = dvx * upXc + dvy * upYc + dvz * upZc;
          // Radius in NDC, per axis. halfH in metres at this depth is depth/projY.
          const rY = (size * projY) / depth;
          const rX = (size * projX) / depth;
          if (Math.abs((ex * projX) / depth) - rX < 1 && Math.abs((ey * projY) / depth) - rY < 1) {
            onCount++;
            onArea += area;
            onWt += w;
          }
        }
      }

      i++;
    }

    // The light fraction's own alpha-weighted centroid, for next frame's sunward
    // coordinate. Held when there is nothing drawn so a plume that blinks out and back does
    // not swing its gradient through the bike.
    if (sumCW > 1e-4) {
      plumeCX = sumCX / sumCW; plumeCZ = sumCZ / sumCW; plumeHaveC = true;
    }

    // Per-source emission and rear-tyre contact for the last frame. See stats.emitRoost.
    stats.emitRoost = accEmitRoost; stats.emitRoll = accEmitRoll;
    stats.emitGrit = accEmitGrit; stats.emitBurst = accEmitBurst;
    stats.rearContact = accSteps > 0 ? accContact / accSteps : 0;
    stats.rearDust = accContact > 0 ? accDust / accContact : 0;
    accEmitRoost = 0; accEmitRoll = 0; accEmitGrit = 0; accEmitBurst = 0;
    accSteps = 0; accContact = 0; accDust = 0;

    stats.drawn = n;
    // IN-FRAME ONLY, which is what the field's own doc comment has claimed since it was
    // written and what nothing computed until now. `areaSum` — every sprite the pool is
    // carrying, most of them behind the lens — is kept as `poolOverdraw` so the two can be
    // compared rather than confused.
    stats.overdraw = onArea;
    // ...and feed it back to the emitter. See FILL_BUDGET: this is what makes vfx.overdraw
    // a bound rather than an observation.
    {
      const want = onArea > FILL_BUDGET ? FILL_BUDGET / onArea : 1;
      fillThrottle += (want - fillThrottle) * FILL_TRACK;
    }
    stats.poolOverdraw = areaSum;
    stats.onScreen = onCount;
    stats.plumeWeight = onWt;
    stats.onScreenShare = allWt > 1e-9 ? onWt / allWt : 0;
    stats.skyShare = allWt > 1e-9 ? skyWt / allWt : 0;
    if (n > 0) {
      let acc = 0;
      const want = n * 0.9;
      let b = 0;
      for (; b < 64 && acc < want; b++) acc += covHist[b];
      stats.covP90 = b / 64;
    } else {
      stats.covP90 = 0;
      stats.nearest = 0;
    }

    // --- self-audit --------------------------------------------------------
    //
    // Reported once a frame from the same numbers the loop above just produced, so the
    // instrument cannot drift from the thing it measures — which is the failure this
    // project has shipped in five consecutive waves. The sky share is only meaningful when
    // there is a plume to have a share OF: a frame with three sprites in it will report
    // 1.0 or 0.0 at random and would fill the report with noise, so it is gated on the
    // plume carrying real optical weight rather than on a bare instance count.
    // 0.06 of a frame-area's worth of optical weight, not 0.02. A SHARE is meaningless on
    // a frame with four sprites in it — two near puffs that happen to sit above the lens
    // make it 1.0 — and the measured worst was coming from exactly those: raising the gate
    // took the reported worst on `roost` from 0.47 (5 frames of 613) to what a real plume
    // does, with no change to the plume itself. An invariant that fires on noise gets
    // ignored, which is the failure mode this bus exists to end.
    // ...and a frame with no plume in it genuinely has no column, so it is observed as a
    // zero rather than skipped. Skipping made SUMMARY.md print "declared but never
    // observed (owner has not wired it up)" for this invariant on every scenario whose
    // plume stayed under the gate, which is an accusation rather than a measurement.
    if (n > 0) {
      const v = allWt > 0.06 ? stats.skyShare : 0;
      // Labelled with the LENS HEIGHT OVER THE CHASSIS, because this metric cannot tell
      // "the plume climbed above the rider's eye line" (a VFX defect) from "the lens
      // dropped below the plume" (someone else's). Measured this wave, every shipped frame
      // came out of a chase camera rolled about 90 degrees while that piece was being
      // rewritten, and the frames this invariant fires on are ones where the lens sits at
      // or under the chassis. Whoever reads the report next should not have to guess.
      if (v > INV_SKY.worst) {
        INV_SKY.observeWhere(v, `lens ${(camY - ctx.bike.y).toFixed(2)} m over chassis`);
      } else {
        INV_SKY.observe(v);
      }
    }
    if (n > 0) INV_COVER.observeAt(stats.covMax, camX, camY, camZ);
    INV_FILL.observe(onArea);
    INV_POOL.observe(live / MAX_PARTICLES);

    // --- the floor -----------------------------------------------------------
    //
    // Read and cleared here, once a render frame, whatever happened in the 1-8 sim steps
    // under it. The demand is the SIM's statement that there should be a roost; the weight
    // is the PICTURE's statement about whether there is one. Reporting the second only when
    // the first is true is what makes this an invariant rather than a complaint.
    // A plume takes time to build. The first frames of a brake stab legitimately have
    // nothing in the air yet, and a floor observed on them measures LATENCY, not presence —
    // it would report a violation on every stab in the run and be ignored inside a wave,
    // which is the failure mode this bus exists to end. So the demand has to have been
    // held: the timer runs while the tyre is working and resets the moment it is not.
    if (roostDemand > ROOST_DEMAND_GATE && roostDemandSpeed > ROOST_PRESENCE_SPEED) {
      demandHeldS += dt;
    } else {
      demandHeldS = 0;
    }
    if (demandHeldS > ROOST_PRESENCE_LAG) {
      if (stats.plumeWeight < INV_PRESENCE.worst || INV_PRESENCE.samples === 0) {
        INV_PRESENCE.observeWhere(
          stats.plumeWeight,
          `demand ${roostDemand.toFixed(2)} at ${(roostDemandSpeed * 3.6).toFixed(0)} km/h, `
          + `${stats.onScreen} of ${n} sprites in frame`,
        );
      } else {
        INV_PRESENCE.observe(stats.plumeWeight);
      }
    }
    roostDemand = 0;
    roostDemandSpeed = 0;

    geo.instanceCount = n;
    mesh.visible = n > 0 && !forceHidden;
    if (n === 0) return;

    attrPos.clearUpdateRanges();
    attrPos.addUpdateRange(0, n * 3);
    attrPos.needsUpdate = true;
    attrCol.clearUpdateRanges();
    attrCol.addUpdateRange(0, n * 4);
    attrCol.needsUpdate = true;
    attrShape.clearUpdateRanges();
    attrShape.addUpdateRange(0, n * 4);
    attrShape.needsUpdate = true;
    attrKind.clearUpdateRanges();
    attrKind.addUpdateRange(0, n);
    attrKind.needsUpdate = true;
    attrVel.clearUpdateRanges();
    attrVel.addUpdateRange(0, n * 3);
    attrVel.needsUpdate = true;
    attrForm.clearUpdateRanges();
    attrForm.addUpdateRange(0, n);
    attrForm.needsUpdate = true;

    // No light wiring here any more. `relief.ts` owns the sun, the sky and the ground
    // bounce for the whole game and pushes them into one shared uniform block once a
    // frame; this material merged that block at construction, so a dawn plume is lit by
    // dawn without this system knowing the sky exists.
  },

  dispose() {
    geo?.dispose();
    mat?.dispose();
  },
});

/** Diagnostics for the capture harness and the debug overlay. */
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__gripVfx = {
    particleCount: () => live,
    max: MAX_PARTICLES,
    killAll,
    /** Update-phase cost of both VFX systems, ms. The 2 ms budget is measured here. */
    costMs: () => (systemTimings.particles ?? 0) + (systemTimings.tracks ?? 0),
    /**
     * A/B switch for probes: "is that grey plate in braking_7p0s actually dust?" answered
     * with a picture instead of an argument.
     *
     * It has to do BOTH things. `mesh.visible` is rewritten from the live count on every
     * `update()`, so the latch is what makes the answer survive a stepped frame — but the
     * latch alone is only consumed *by* `update()`, and the honest way to A/B a frame is
     * two `renderer.render()` calls inside one JS task with nothing stepped in between
     * (see `captures/w3-vfx-r3-tools/probe.mjs` for why: stepping at all perturbs the
     * scene, so a stepped A/B has a noise floor the size of its signal). On that path no
     * update runs, the latch is never read, and this returned "0 pixels changed" for a
     * plume that was on screen — which is how the round-3 report concluded the mid-air
     * blobs in `dustcam_7p8s` were somebody else's foliage. They are dust: hiding the mesh
     * directly changes 24% of `descend_14p0s` and the blob bbox exactly.
     */
    show(v: boolean) { forceHidden = !v; if (mesh) mesh.visible = v && stats.drawn > 0; },
    /** Live uniform poke so the coverage cap can be swept without a rebuild. */
    cover(fade0: number, fade1: number, budget = COVER_BUDGET) {
      if (mat) (mat.uniforms.uCover.value as THREE.Vector3).set(fade0, fade1, budget);
    },
    exposure(v: number) { if (mat) mat.uniforms.uExposure.value = v; },
    /**
     * Probe hook: draw only one fraction. `only(null)` restores all three.
     *
     * Never set in play. See the uKindMask declaration for why a project that has twice
     * misattributed this effect's worst artefact now ships the ability to photograph one
     * fraction at a time.
     */
    only(kind: 'dust' | 'grit' | 'spray' | null) {
      if (!mat) return;
      const v = mat.uniforms.uKindMask.value as THREE.Vector3;
      if (kind === null) v.set(1, 1, 1);
      else v.set(kind === 'dust' ? 1 : 0, kind === 'grit' ? 1 : 0, kind === 'spray' ? 1 : 0);
    },
    /**
     * PROBE ONLY. Scale every sprite's alpha by k, on the frame that is already on screen.
     *
     * ---------------------------------------------------------------------------
     * THIS IS THE INSTRUMENT'S OWN RESPONSE TEST, AND IT IS WHY IT EXISTS
     * ---------------------------------------------------------------------------
     *
     * Two of the five measurement failures this project has shipped were metrics that were
     * STRUCTURALLY INCAPABLE of detecting the thing they were watching for: `overTravel`
     * could not see the bike sinking into the terrain, and a camera "green pixel" counter
     * with no depth scored a clean forest corridor identically to a lens buried in a
     * branch. Neither was caught by inspection; both would have been caught in one command
     * by injecting a known amount of the defect and checking that the number moved.
     *
     * The defect this piece is graded on is "the plume is not on screen". So this injects
     * exactly that, by a known factor, on one unstepped simulation state: the sprites, the
     * positions, the sizes and the shading are byte-identical and only the opacity is
     * scaled. A presence metric that cannot see `thin(0.25)` cannot see the defect either,
     * and any claim it makes about a fix is worthless.
     *
     * Never set in play — the whole tuple is (1,1,1) unless a probe writes it, and the
     * uniform it uses is the same one `only()` writes, which already multiplies the
     * fragment's alpha.
     */
    thin(k: number) {
      if (!mat) return;
      const v = Math.max(0, Number(k) || 0);
      (mat.uniforms.uKindMask.value as THREE.Vector3).set(v, v, v);
    },
    /**
     * Probe hook: restore the heavy fraction's pre-W4 look for one frame.
     *
     * The floating-blob defect survived three rounds partly because every "before" was a
     * different run from every "after" — a different seed, a different line, and by wave
     * 4 a different mountain. This makes the comparison two draws of ONE simulation
     * state: the sprites are in the same places at the same sizes with the same alphas,
     * and the only thing that differs is the silhouette, the optical parameters and the
     * screen cap. Never set in play.
     */
    gritLegacy(v: boolean) { if (mat) mat.uniforms.uGritLegacy.value = v ? 1 : 0; },
    /**
     * PROBE ONLY. Translate every DRAWN sprite `d` metres straight up on the frame that is
     * already on screen. See the `uLift` declaration — the sky invariant's response test.
     */
    lift(d: number) { if (mat) mat.uniforms.uLift.value = Number(d) || 0; },
    /**
     * Probe hook and instrument calibration. 0 = shipping, 1 = flat control, 2 = a known
     * world-up ramp of crown:belly 4.0. Never set in play; see the uFlat declaration.
     */
    flat(v: boolean | number) { if (mat) mat.uniforms.uFlat.value = Number(v) || 0; },
    /** Last frame's drawn set. See `stats` above for what covMax means. */
    stats: () => stats,
    /**
     * Every live particle as `update()` last left it: world position, drawn radius,
     * final alpha, height above its own floor plane and kind.
     *
     * This exists because "the roost hangs as near-black blobs against the sky" is a
     * statement about *individual sprites*, and three rounds argued about it from
     * whole-frame diffs. With this a probe can project each sprite to its own pixel,
     * read the background it is drawn over out of the A/B's D frame, and report the
     * contrast that one sprite creates — which is the quantity the defect is about.
     */
    dump() {
      const n = live;
      const out = {
        n: 0,
        x: [] as number[], y: [] as number[], z: [] as number[],
        a: [] as number[], s: [] as number[], h: [] as number[], k: [] as number[],
      };
      for (let i = 0; i < n; i++) {
        if (curAlpha[i] < 0.011) continue;
        out.x.push(px[i]); out.y.push(py[i]); out.z.push(pz[i]);
        out.a.push(curAlpha[i]); out.s.push(curSize[i]); out.h.push(curH[i]);
        out.k.push(kindOf[i]);
        out.n++;
      }
      return out;
    },
    /**
     * Whether the plume is being occluded by the same analytic sun the hillside under it
     * is. `false` means every puff is lit as though it stood in the open, which is not
     * something a screenshot reliably shows and is worth being able to assert.
     */
    debug: () => ({ skyWired, live, drawn: stats.drawn }),
  };
}

export {};
